import { Box, Button, MenuItem, TextField } from '@mui/material';
import { Menu, useMenuIsDrawer } from 'Components/RoadmapComponents/Menu';
import { useAudioRecognition } from 'AudioRecognition';
import type { RecognitionFrame } from 'AudioRecognition/types';
import { midiToName } from 'AudioRecognition/util/note';
import axios from 'axios';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  type ReactNode,
} from 'react';
import {
  useMidiContext,
  type Listener,
  type PreferredInputMode,
  type PedalMessage,
} from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { createOSMD } from 'Utils/OSMDUtility';
import * as Tone from 'tone';
import { OpenSheetMusicDisplay as OSMD } from '@museflow-ai/opensheetmusicdisplay';
import { DOWNBEAT_B64, UPBEAT_B64 } from 'assets/audio/clicks/base64';
import PlayIcon from 'assets/images/Play.png';
import RestartIcon from 'assets/images/Repertoire/Restart.png';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { decodeBase64ToAudioBuffer } from 'Utils/audioBuffers';
import { CustomSwitch } from 'Components/StyledComponents';
import AudioDiagnosticsPanel from './components/AudioDiagnosticsPanel';
import { NoteLengthIcon, ScoreDragNoteGlyph } from './components/NoteGlyphs';
import {
  ClearDraftDialog,
  SaveRepertoireDialog,
  SaveSuccessDialog,
} from './components/SaveDialogs';
import StatPill from './components/StatPill';
import {
  getAudioCaptureScore,
  hasAudioCaptureOnsetEvidence,
  normalizeMidiNotes,
  normalizeMidiNotesPreservingOrder,
  rankAudioCaptureNotes,
  selectAudioCaptureCandidates,
  selectAudioCaptureConsensusCandidates,
  type AudioCaptureCandidate,
  type AudioCaptureCandidateObservation,
} from './utils/audioCapture';
import {
  AUDIO_DIAGNOSTICS_MAX_FRAMES,
  AUDIO_DIAGNOSTICS_UI_INTERVAL_MS,
  buildAudioDiagnosticsFrame,
  buildAudioDiagnosticsSnapshot,
  type AudioDiagnosticsFrame,
  type AudioDiagnosticsSnapshot,
} from './utils/audioDiagnostics';
import PageNav from '../PageNav';
import PlaybackTempoControl from '../components/PlaybackTempoControl';
import useDraftPlaybackTimeKeeper from '../components/useDraftPlaybackTimeKeeper';
import {
  dimPaperRestartIconChrome,
  dimPaperRestartButtonChrome,
  transportPlayButtonChrome,
  transportRestartButtonChrome,
} from '../shared/playbackChrome';
import { usePracticeScoreTheme } from 'Hooks/usePracticeScoreTheme';
import {
  applyScoreThemeToSvgElement,
  getScoreThemePalette,
  scoreThemeToPracticeDarkMode,
} from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import {
  buildLiveComposerMusicXml,
  COMMON_TIME_SIGNATURES,
  DEFAULT_TIME_SIGNATURE,
  getMeasureDurationDivisions,
  type AccidentalSpelling,
  type LiveComposerEvent,
  type LiveComposerPedalEvent,
  type StaffNumber,
} from '../CreateMusicXml';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as repertoireActions from 'Actions/repertoire';
import * as appActions from 'Actions/app';
import type { RepertoireData, RepertoireReducer, UserRepertoireData } from 'Types/RepertoireTypes';

const AUDIO_LISTENER_NAME = 'repertoire-create-audio-listener';
const MIDI_LISTENER_NAME = 'repertoire-create-midi-listener';
const CHORD_GROUP_WINDOW_MS = 55;
const AUDIO_ONSET_DEDUP_MS = 260;
const AUDIO_RELEASE_GRACE_MS = 180;
const AUDIO_SPARSE_ONSET_WAIT_MS = 220;
const DEFAULT_PLAYBACK_TEMPO_BPM = 104;
const MIN_PLAYBACK_TEMPO_BPM = 40;
const MAX_PLAYBACK_TEMPO_BPM = 220;
const DEFAULT_CAPTURE_TEMPO_MIN_BPM = 60;
const DEFAULT_CAPTURE_TEMPO_MAX_BPM = 180;
const AUTO_TEMPO_MAX_ONSETS = 8;
const AUTO_TEMPO_MAX_REQUANTIZED_EVENTS = 24;
const AUTO_TEMPO_MAX_INTERVAL_MS = 2400;
const AUTO_TEMPO_MIN_CONFIDENCE = 0.26;
const ADVANCED_TOOLS_OPEN_STORAGE_KEY = 'repertoire-create-advanced-tools-open-v1';
const AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY = 'repertoire-create-audio-diagnostics-visible-v1';
const AUTO_TEMPO_CANDIDATE_DIVISIONS = [1, 2, 3, 4, 6, 8, 12, 16];
const MIN_EVENT_DURATION_DIVISIONS = 1;
const DIVISIONS_PER_BEAT = 4;
const DIVISIONS_PER_WHOLE_NOTE = DIVISIONS_PER_BEAT * 4;
const PEDAL_LABEL_DOWN = 'Pedal Down';
const PEDAL_LABEL_UP = 'Pedal Up';
const DEFAULT_MANUAL_NOTE_MIDI = 60;
const STAFF_SPLIT_MIDI = 60;
const EDITOR_TOP_MIDI = 84;
const EDITOR_BOTTOM_MIDI = 36;
const MANUAL_NOTE_DRAG_TYPE = 'application/x-live-draft-note';
const EXISTING_NOTE_DRAG_TYPE = 'application/x-live-draft-event-id';
const OSMD_UNIT_IN_PIXELS = 10;
const NOTE_ANCHOR_VERTICAL_MISMATCH_PX = 16;
const DEFAULT_DRAFT_TITLE = 'Untitled Repertoire Draft';
const CREATED_REPERTOIRE_SOURCE = 'created';
const CREATED_REPERTOIRE_AUTHOR = 'you';
const DRAFT_AUTOSAVE_KEY = 'museflow_repertoire_create_draft_v1';
const DRAFT_HISTORY_LIMIT = 80;
const TABLET_WORKBENCH_MEDIA = '@media (min-width: 1180px)';
const WIDE_WORKBENCH_MEDIA = '@media (min-width: 1500px)';

const DRAFT_DURATION_OPTIONS = [
  { label: '16th', divisions: 1 },
  { label: '8th', divisions: 2 },
  { label: 'Quarter', divisions: 4 },
  { label: 'Dotted quarter', divisions: 6 },
  { label: 'Half', divisions: 8 },
  { label: 'Whole', divisions: 16 },
];

const ACCIDENTAL_SPELLING_OPTIONS: Array<{
  id: AccidentalSpelling;
  label: string;
  ariaLabel: string;
}> = [
  { id: 'auto', label: 'Auto', ariaLabel: 'Automatic spelling' },
  { id: 'double-flat', label: '♭♭', ariaLabel: 'Double flat spelling' },
  { id: 'flat', label: '♭', ariaLabel: 'Flat spelling' },
  { id: 'natural', label: '♮', ariaLabel: 'Natural spelling' },
  { id: 'sharp', label: '♯', ariaLabel: 'Sharp spelling' },
  { id: 'double-sharp', label: '𝄪', ariaLabel: 'Double sharp spelling' },
];
const PRERENDERED_MEASURE_COUNT = 4;

type RecordingHandMode = 'both' | 'right' | 'left';
type BuilderMode = 'capture' | 'edit' | 'practice';

const RECORDING_HAND_OPTIONS: Array<{ id: RecordingHandMode; label: string }> = [
  { id: 'both', label: 'Both' },
  { id: 'right', label: 'Right' },
  { id: 'left', label: 'Left' },
];

const BUILDER_MODE_OPTIONS: Array<{ id: BuilderMode; label: string }> = [
  { id: 'capture', label: 'Capture' },
  { id: 'edit', label: 'Edit' },
  { id: 'practice', label: 'Practice' },
];

const readStoredBoolean = (key: string, fallback = false) => {
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(key);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    return fallback;
  }

  return fallback;
};

const writeStoredBoolean = (key: string, value: boolean) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures.
  }
};

const getStaffForRecordingHand = (mode: RecordingHandMode): StaffNumber | undefined => {
  if (mode === 'right') return 1;
  if (mode === 'left') return 2;
  return undefined;
};

type ComposerInputMode = PreferredInputMode | null;
type ComposerSource = LiveComposerEvent['source'];
type RecentCaptureItem = {
  id: string;
  capturedAtMs: number;
  source: ComposerSource;
  label: string;
};
type PlaybackSamplerWindow = Window & {
  __ensureSamplerReady?: (forceRebuild?: boolean) => Promise<void>;
  __museflowSampler?: Tone.Sampler;
};

type MetronomeSamples = {
  downBeatSample: Tone.Player;
  upBeatSample: Tone.Player;
};

type PendingChord = {
  startedAtMs: number;
  startDivisions: number;
  staff?: StaffNumber;
  midiNotes: Set<number>;
  noteScores?: Map<number, number>;
  source: 'midi' | 'audio';
  timeoutId: number;
  rawCaptureSessionStartedAtMs: number;
  rawCaptureSessionStartDivisions: number;
};

type CaptureTimingSession = {
  source: 'midi' | 'audio';
  startedAtMs: number;
  startDivisions: number;
};

type TimelineDraftEvent = LiveComposerEvent & {
  startDivisions: number;
  durationDivisions: number;
};

type NoteHandlePosition = {
  leftPx: number;
  topPx: number;
};

type RenderedNoteAnchor = NoteHandlePosition & {
  midi: number;
  startDivisions: number;
  elements: Element[];
};

type FractionLike = {
  RealValue?: number;
  Numerator?: number;
  Denominator?: number;
  WholeValue?: number;
  GetExpandedNumerator?: () => number;
};

type PitchLike = {
  AccidentalHalfTones?: number;
  FundamentalNote?: number;
  Octave?: number;
  getHalfTone?: () => number;
};

type SourceNoteLike = {
  Pitch?: PitchLike;
  halfTone?: number;
  getAbsoluteTimestamp?: () => FractionLike;
  isRest?: () => boolean;
};

type GraphicalNoteLike = {
  sourceNote?: SourceNoteLike;
  vfnoteIndex?: number;
  PositionAndShape?: BoundingBoxLike;
  getNoteheadSVGs?: () => Element[];
  getSVGGElement?: () => Element;
};

type GraphicalVoiceEntryLike = {
  notes?: GraphicalNoteLike[];
};

type GraphicalStaffEntryLike = {
  graphicalVoiceEntries?: GraphicalVoiceEntryLike[];
};

type PointLike = {
  x?: number;
  y?: number;
};

type SizeLike = {
  width?: number;
  height?: number;
};

type BoundingBoxLike = {
  AbsolutePosition?: PointLike;
  Size?: SizeLike;
};

type GraphicalLineLike = {
  SVGElement?: Node;
};

type SourceMeasureLike = {
  AbsoluteTimestamp?: FractionLike;
  measureListIndex?: number;
  MeasureNumber?: number;
};

type GraphicalMeasureLike = {
  PositionAndShape?: BoundingBoxLike;
  MeasureNumber?: number;
  beginInstructionsWidth?: number;
  endInstructionsWidth?: number;
  parentSourceMeasure?: SourceMeasureLike;
};

type StaffLike = {
  idInMusicSheet?: number;
};

type StaffLineLike = {
  PositionAndShape?: BoundingBoxLike;
  StaffLines?: GraphicalLineLike[];
  Measures?: GraphicalMeasureLike[];
  ParentStaff?: StaffLike;
  StaffHeight?: number;
};

type MusicSystemLike = {
  StaffLines?: StaffLineLike[];
};

type MusicPageLike = {
  MusicSystems?: MusicSystemLike[];
};

type VerticalGraphicalStaffEntryContainerLike = {
  StaffEntries?: Array<GraphicalStaffEntryLike | null>;
};

type GraphicSheetLike = {
  VerticalGraphicalStaffEntryContainers?: VerticalGraphicalStaffEntryContainerLike[];
  MusicPages?: MusicPageLike[];
};

type ManualNoteDrop = {
  midi: number;
  startDivisions: number;
  durationDivisions: number;
  staff?: StaffNumber;
};

type ExistingNoteDrop = ManualNoteDrop & {
  sourceMidi?: number;
};

type SelectedNoteTarget = {
  eventId: string;
  midi: number;
};

type EventSelectOptions = {
  toggleSelection?: boolean;
};

type ManualNotePointerDragHandler = (
  durationDivisions: number,
  event: ReactPointerEvent<HTMLElement>,
) => void;

type PointerScorePlacement = {
  midi: number;
  rawStartDivisions: number;
  staff?: StaffNumber;
};

type SvgCoordinateMapper = (point: { x: number; y: number }) => NoteHandlePosition;

type ScoreMeasureGeometry = {
  startDivisions: number;
  durationDivisions: number;
  leftPx: number;
  rightPx: number;
  contentLeftPx: number;
  contentRightPx: number;
};

type ScoreStaffGeometry = {
  systemIndex: number;
  staff: StaffNumber;
  topLinePx: number;
  lineGapPx: number;
  centerYPx: number;
  topBoundPx: number;
  bottomBoundPx: number;
  measures: ScoreMeasureGeometry[];
};

type ScoreGeometry = {
  staves: ScoreStaffGeometry[];
};

type ScoreCursorGeometry = {
  leftPx: number;
  topPx: number;
  heightPx: number;
};

type ActiveCapturedEvent = {
  id: string;
  startedAtMs: number;
  startDivisions: number;
  source: 'midi' | 'audio';
  allNotes: Set<number>;
  activeNotes: Set<number>;
  rawCaptureSessionStartedAtMs: number;
  rawCaptureSessionStartDivisions: number;
};

type PendingAudioOnsetBurst = {
  startedAtMs: number;
  deadlineMs: number;
  observations: AudioCaptureCandidateObservation[];
};

type PlaybackInstruction =
  | {
      id: string;
      atDivisions: number;
      type: 'pedal';
      pedalDown: boolean;
    }
  | {
      id: string;
      atDivisions: number;
      type: 'note-on' | 'note-off';
      midiNotes: number[];
      source: ComposerSource;
    };

type ComposerDraftSnapshot = {
  capturedEvents: LiveComposerEvent[];
  capturedPedalEvents: LiveComposerPedalEvent[];
  draftTitle: string;
  timeSignatureId: string;
  selectedDurationDivisions: number;
  recordingCursorDivisions: number;
  recordingHandMode: RecordingHandMode;
  playbackTempo: number;
  metronomeEnabled: boolean;
  captureTempoAutoEnabled: boolean;
  captureTempoBpm: number;
  captureTempoMinBpm: number;
  captureTempoMaxBpm: number;
  captureTempoConfidence: number;
};

const getPendingChordNotes = (pendingChord: PendingChord) => {
  const pendingNotes = Array.from(pendingChord.midiNotes);
  if (pendingChord.source === 'audio') {
    return normalizeMidiNotes(rankAudioCaptureNotes(pendingNotes, pendingChord.noteScores));
  }

  return normalizeMidiNotes(pendingNotes);
};

const clampTempoBpm = (
  tempo: number,
  minTempo = MIN_PLAYBACK_TEMPO_BPM,
  maxTempo = MAX_PLAYBACK_TEMPO_BPM,
) => Math.max(minTempo, Math.min(maxTempo, Math.round(tempo)));

const getSixteenthDurationMs = (tempoBpm: number) =>
  60_000 / Math.max(tempoBpm, 1) / DIVISIONS_PER_BEAT;

const quantizeElapsedDivisionsForTempo = (durationMs: number, tempoBpm: number) =>
  Math.max(0, Math.round(Math.max(0, durationMs) / Math.max(getSixteenthDurationMs(tempoBpm), 1)));

const quantizeDurationDivisionsForTempo = (durationMs: number, tempoBpm: number) =>
  Math.max(MIN_EVENT_DURATION_DIVISIONS, quantizeElapsedDivisionsForTempo(durationMs, tempoBpm));

const canAudioFrameSustainActiveNotes = (frame: RecognitionFrame) => {
  const minRms = frame.minRms ?? 0;
  if (minRms <= 0) return true;

  return (frame.rms ?? 0) >= minRms;
};

const getAudioFrameStableMidiNotes = (frame: RecognitionFrame) => {
  if (frame.recognitionMode === 'free-capture' && (frame.selectedNotes?.length ?? 0) > 0) {
    return normalizeMidiNotes((frame.selectedNotes ?? []).map((note) => note.midi));
  }

  return normalizeMidiNotes(frame.midiNotes ?? []);
};

const getCaptureSessionDivisionsAtMs = (
  capturedAtMs: number,
  sessionStartedAtMs: number,
  sessionStartDivisions: number,
  tempoBpm: number,
) =>
  Math.max(
    0,
    Math.round(
      sessionStartDivisions +
        quantizeElapsedDivisionsForTempo(capturedAtMs - sessionStartedAtMs, tempoBpm),
    ),
  );

const hasAdaptiveEventTiming = (event: LiveComposerEvent) =>
  Boolean(event.adaptiveTiming) &&
  typeof event.rawStartedAtMs === 'number' &&
  Number.isFinite(event.rawStartedAtMs) &&
  typeof event.rawCaptureSessionStartedAtMs === 'number' &&
  Number.isFinite(event.rawCaptureSessionStartedAtMs) &&
  typeof event.rawCaptureSessionStartDivisions === 'number' &&
  Number.isFinite(event.rawCaptureSessionStartDivisions);

const hasAdaptivePedalTiming = (event: LiveComposerPedalEvent) =>
  Boolean(event.adaptiveTiming) &&
  typeof event.rawCaptureSessionStartedAtMs === 'number' &&
  Number.isFinite(event.rawCaptureSessionStartedAtMs) &&
  typeof event.rawCaptureSessionStartDivisions === 'number' &&
  Number.isFinite(event.rawCaptureSessionStartDivisions);

const freezeAdaptiveEventTiming = (event: LiveComposerEvent): LiveComposerEvent => ({
  ...event,
  adaptiveTiming: false,
  rawStartedAtMs: undefined,
  rawEndedAtMs: undefined,
  rawCaptureSessionStartedAtMs: undefined,
  rawCaptureSessionStartDivisions: undefined,
});

const requantizeAdaptiveEventForTempo = (
  event: LiveComposerEvent,
  tempoBpm: number,
): LiveComposerEvent => {
  if (!hasAdaptiveEventTiming(event)) return event;

  const startDivisions = getCaptureSessionDivisionsAtMs(
    event.rawStartedAtMs!,
    event.rawCaptureSessionStartedAtMs!,
    event.rawCaptureSessionStartDivisions!,
    tempoBpm,
  );
  const durationDivisions =
    typeof event.rawEndedAtMs === 'number' && Number.isFinite(event.rawEndedAtMs)
      ? quantizeDurationDivisionsForTempo(event.rawEndedAtMs - event.rawStartedAtMs!, tempoBpm)
      : event.durationDivisions;

  return {
    ...event,
    restBeforeDivisions: 0,
    startDivisions,
    durationDivisions,
  };
};

const requantizeAdaptivePedalForTempo = (
  event: LiveComposerPedalEvent,
  tempoBpm: number,
): LiveComposerPedalEvent => {
  if (!hasAdaptivePedalTiming(event)) return event;

  return {
    ...event,
    positionDivisions: getCaptureSessionDivisionsAtMs(
      event.capturedAtMs,
      event.rawCaptureSessionStartedAtMs!,
      event.rawCaptureSessionStartDivisions!,
      tempoBpm,
    ),
  };
};

const getAdaptiveTempoOnsetsFromEvents = (events: LiveComposerEvent[]) =>
  events
    .filter(hasAdaptiveEventTiming)
    .map((event) => event.rawStartedAtMs!)
    .sort((a, b) => a - b)
    .filter(
      (startedAtMs, index, onsets) =>
        index === 0 || startedAtMs - onsets[index - 1] > CHORD_GROUP_WINDOW_MS,
    )
    .slice(-AUTO_TEMPO_MAX_ONSETS);

const getMedian = (values: number[]) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const midpoint = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0 ? (sorted[midpoint - 1] + sorted[midpoint]) / 2 : sorted[midpoint];
};

const getTempoCandidateDivisions = (selectedDurationDivisions: number) =>
  Array.from(
    new Set([
      Math.max(MIN_EVENT_DURATION_DIVISIONS, Math.round(selectedDurationDivisions)),
      ...AUTO_TEMPO_CANDIDATE_DIVISIONS,
    ]),
  );

const estimateAdaptiveCaptureTempo = (
  onsets: number[],
  currentTempoBpm: number,
  selectedDurationDivisions: number,
  minTempoBpm: number,
  maxTempoBpm: number,
) => {
  const candidateDivisions = getTempoCandidateDivisions(selectedDurationDivisions);
  const intervalTempos: number[] = [];

  onsets.slice(-AUTO_TEMPO_MAX_ONSETS).forEach((startedAtMs, index, recentOnsets) => {
    if (index === 0) return;
    const gapMs = startedAtMs - recentOnsets[index - 1];
    if (gapMs <= CHORD_GROUP_WINDOW_MS || gapMs > AUTO_TEMPO_MAX_INTERVAL_MS) return;

    const bestCandidate = candidateDivisions
      .map((divisions) => ({
        divisions,
        tempoBpm: (60_000 * divisions) / (DIVISIONS_PER_BEAT * gapMs),
      }))
      .filter((candidate) => candidate.tempoBpm >= minTempoBpm && candidate.tempoBpm <= maxTempoBpm)
      .sort((a, b) => {
        const aScore =
          Math.abs(Math.log(a.tempoBpm / Math.max(currentTempoBpm, 1))) +
          (a.divisions === selectedDurationDivisions ? 0 : 0.08);
        const bScore =
          Math.abs(Math.log(b.tempoBpm / Math.max(currentTempoBpm, 1))) +
          (b.divisions === selectedDurationDivisions ? 0 : 0.08);
        return aScore - bScore;
      })[0];

    if (bestCandidate) {
      intervalTempos.push(bestCandidate.tempoBpm);
    }
  });

  const medianTempo = getMedian(intervalTempos);
  if (medianTempo == null) return null;

  const relativeDeviation =
    intervalTempos.reduce(
      (sum, tempo) => sum + Math.abs(tempo - medianTempo) / Math.max(medianTempo, 1),
      0,
    ) / Math.max(1, intervalTempos.length);
  const intervalConfidence = Math.min(1, intervalTempos.length / 4);
  const consistencyConfidence = Math.max(0, 1 - relativeDeviation / 0.18);
  const confidence = Math.max(0, Math.min(1, intervalConfidence * consistencyConfidence));

  return {
    tempoBpm: clampTempoBpm(medianTempo, minTempoBpm, maxTempoBpm),
    confidence,
    intervalCount: intervalTempos.length,
  };
};

const formatChordLabel = (midiNotes: number[]) => {
  const normalizedNotes = normalizeMidiNotes(midiNotes);
  if (normalizedNotes.length === 0) return 'Rest';
  return normalizedNotes.map(midiToName).join(' + ');
};

const normalizeAccidentalSpellings = (accidentalSpellings?: Record<number, AccidentalSpelling>) => {
  if (!accidentalSpellings) return undefined;

  const nextSpellings: Record<number, AccidentalSpelling> = {};
  Object.entries(accidentalSpellings).forEach(([rawMidi, spelling]) => {
    const midi = Number(rawMidi);
    if (!Number.isFinite(midi) || spelling === 'auto') return;
    nextSpellings[Math.max(0, Math.min(127, Math.round(midi)))] = spelling;
  });

  return Object.keys(nextSpellings).length > 0 ? nextSpellings : undefined;
};

const pickAccidentalSpellings = (
  accidentalSpellings: Record<number, AccidentalSpelling> | undefined,
  midiNotes: number[],
) => {
  if (!accidentalSpellings) return undefined;

  const nextSpellings: Record<number, AccidentalSpelling> = {};
  normalizeMidiNotes(midiNotes).forEach((midi) => {
    const spelling = accidentalSpellings[midi];
    if (spelling && spelling !== 'auto') {
      nextSpellings[midi] = spelling;
    }
  });

  return normalizeAccidentalSpellings(nextSpellings);
};

const shiftAccidentalSpellings = (
  accidentalSpellings: Record<number, AccidentalSpelling> | undefined,
  pitchDelta: number,
) => {
  if (!accidentalSpellings) return undefined;

  const nextSpellings: Record<number, AccidentalSpelling> = {};
  Object.entries(accidentalSpellings).forEach(([rawMidi, spelling]) => {
    const midi = Number(rawMidi);
    if (!Number.isFinite(midi) || spelling === 'auto') return;
    const shiftedMidi = Math.max(0, Math.min(127, Math.round(midi + pitchDelta)));
    nextSpellings[shiftedMidi] = spelling;
  });

  return normalizeAccidentalSpellings(nextSpellings);
};

const getDraftEventStartDivisions = (event: LiveComposerEvent, fallbackCursorDivisions: number) =>
  event.startDivisions == null
    ? fallbackCursorDivisions + Math.max(0, Math.round(event.restBeforeDivisions))
    : Math.max(0, Math.round(event.startDivisions));

const isDraftRestEvent = (event: Pick<LiveComposerEvent, 'midiNotes'>) =>
  normalizeMidiNotes(event.midiNotes).length === 0;

const getDraftEventSortMidi = (event: Pick<LiveComposerEvent, 'midiNotes' | 'staff'>) => {
  const firstMidi = normalizeMidiNotes(event.midiNotes)[0];
  if (firstMidi != null) return firstMidi;
  if (event.staff === 2) return STAFF_SPLIT_MIDI - 1;
  return STAFF_SPLIT_MIDI;
};

const getDraftTimelineEvents = (events: LiveComposerEvent[]): TimelineDraftEvent[] => {
  let cursorDivisions = 0;

  return events
    .map((event) => {
      const durationDivisions = Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions);
      const startDivisions = getDraftEventStartDivisions(event, cursorDivisions);
      cursorDivisions =
        event.startDivisions == null
          ? cursorDivisions + Math.max(0, Math.round(event.restBeforeDivisions)) + durationDivisions
          : Math.max(cursorDivisions, startDivisions + durationDivisions);

      return {
        ...event,
        startDivisions,
        durationDivisions,
      };
    })
    .sort(
      (a, b) =>
        a.startDivisions - b.startDivisions ||
        a.durationDivisions - b.durationDivisions ||
        getDraftEventSortMidi(a) - getDraftEventSortMidi(b),
    );
};

const sortDraftEvents = (events: LiveComposerEvent[]) =>
  getDraftTimelineEvents(events).map((event) => ({
    ...event,
    restBeforeDivisions: event.startDivisions,
  }));

const getTimelineEndDivisions = (events: LiveComposerEvent[]) =>
  getDraftTimelineEvents(events).reduce(
    (max, event) => Math.max(max, event.startDivisions + event.durationDivisions),
    0,
  );

const formatRecordingCursorLabel = (cursorDivisions: number, measureDurationDivisions: number) => {
  const safeMeasureDuration = Math.max(MIN_EVENT_DURATION_DIVISIONS, measureDurationDivisions);
  const safeCursor = Math.max(0, Math.round(cursorDivisions));
  const measureNumber = Math.floor(safeCursor / safeMeasureDuration) + 1;
  const measureOffset = safeCursor % safeMeasureDuration;
  const beatNumber = Math.floor(measureOffset / DIVISIONS_PER_BEAT) + 1;
  const subdivisionNumber = (measureOffset % DIVISIONS_PER_BEAT) + 1;

  return `Measure ${measureNumber}, Beat ${beatNumber}.${subdivisionNumber}`;
};

const getMidiFromEditorY = (y: number, height: number) => {
  const safeHeight = Math.max(height, 1);
  const ratio = Math.min(1, Math.max(0, y / safeHeight));
  return Math.max(
    EDITOR_BOTTOM_MIDI,
    Math.min(
      EDITOR_TOP_MIDI,
      Math.round(EDITOR_TOP_MIDI - ratio * (EDITOR_TOP_MIDI - EDITOR_BOTTOM_MIDI)),
    ),
  );
};

const getEditorYPercentFromMidi = (midiNotes: number[]) => {
  const normalizedNotes =
    normalizeMidiNotes(midiNotes).length > 0
      ? normalizeMidiNotes(midiNotes)
      : [DEFAULT_MANUAL_NOTE_MIDI];
  const averageMidi =
    normalizedNotes.reduce((sum, note) => sum + note, 0) / Math.max(normalizedNotes.length, 1);
  return (
    ((EDITOR_TOP_MIDI - averageMidi) / Math.max(EDITOR_TOP_MIDI - EDITOR_BOTTOM_MIDI, 1)) * 100
  );
};

const getDurationBucketDivisions = (durationDivisions: number, measureDurationDivisions: number) =>
  Math.max(
    MIN_EVENT_DURATION_DIVISIONS,
    Math.min(
      Math.max(MIN_EVENT_DURATION_DIVISIONS, Math.round(durationDivisions)),
      measureDurationDivisions,
    ),
  );

const snapStartDivisionsToDurationBucket = (
  startDivisions: number,
  durationDivisions: number,
  measureDurationDivisions: number,
) => {
  const safeMeasureDuration = Math.max(MIN_EVENT_DURATION_DIVISIONS, measureDurationDivisions);
  const measureIndex = Math.max(0, Math.floor(Math.max(0, startDivisions) / safeMeasureDuration));
  const measureStart = measureIndex * safeMeasureDuration;
  const measureOffset = Math.max(0, startDivisions - measureStart);
  const durationBucket = getDurationBucketDivisions(durationDivisions, safeMeasureDuration);
  const maxStartOffset = Math.max(
    0,
    safeMeasureDuration - Math.max(1, Math.round(durationDivisions)),
  );
  const snappedOffset = Math.min(
    maxStartOffset,
    Math.floor(measureOffset / durationBucket) * durationBucket,
  );

  return measureStart + snappedOffset;
};

const getStartDivisionsSlotCenter = (
  startDivisions: number,
  durationDivisions: number,
  measureDurationDivisions: number,
) => {
  const safeMeasureDuration = Math.max(MIN_EVENT_DURATION_DIVISIONS, measureDurationDivisions);
  const durationBucket = getDurationBucketDivisions(durationDivisions, safeMeasureDuration);
  const measureIndex = Math.max(0, Math.floor(Math.max(0, startDivisions) / safeMeasureDuration));
  const measureStart = measureIndex * safeMeasureDuration;
  const maxStartOffset = Math.max(
    0,
    safeMeasureDuration - Math.max(1, Math.round(durationDivisions)),
  );
  const startOffset = Math.min(maxStartOffset, Math.max(0, startDivisions - measureStart));

  return measureStart + startOffset + durationBucket / 2;
};

const snapCenterDivisionsToDurationBucket = (
  centerDivisions: number,
  durationDivisions: number,
  measureDurationDivisions: number,
) => {
  const safeMeasureDuration = Math.max(MIN_EVENT_DURATION_DIVISIONS, measureDurationDivisions);
  const safeCenter = Math.max(0, centerDivisions);
  const measureIndex = Math.max(0, Math.floor(safeCenter / safeMeasureDuration));
  const measureStart = measureIndex * safeMeasureDuration;
  const measureOffset = Math.max(0, safeCenter - measureStart);
  const durationBucket = getDurationBucketDivisions(durationDivisions, safeMeasureDuration);
  const maxStartOffset = Math.max(
    0,
    safeMeasureDuration - Math.max(1, Math.round(durationDivisions)),
  );
  const snappedOffset = Math.min(
    maxStartOffset,
    Math.floor(measureOffset / durationBucket) * durationBucket,
  );

  return measureStart + snappedOffset;
};

const getNumberValue = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const NATURAL_MIDI_BY_DIATONIC_STEP = [0, 2, 4, 5, 7, 9, 11];
const DEFAULT_MIDI_PITCH_CLASS_TO_DIATONIC_STEP = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6];
const ACCIDENTAL_SPELLING_TO_ALTER: Record<Exclude<AccidentalSpelling, 'auto'>, number> = {
  'double-flat': -2,
  flat: -1,
  natural: 0,
  sharp: 1,
  'double-sharp': 2,
};

const getDiatonicIndex = (octave: number, naturalStepIndex: number) =>
  octave * NATURAL_MIDI_BY_DIATONIC_STEP.length + naturalStepIndex;
const TREBLE_TOP_LINE_DIATONIC_INDEX = getDiatonicIndex(5, 3); // F5
const BASS_TOP_LINE_DIATONIC_INDEX = getDiatonicIndex(3, 5); // A3

const getDiatonicIndexFromMidi = (midi: number, spelling: AccidentalSpelling = 'auto') => {
  const normalizedMidi = Math.max(0, Math.min(127, Math.round(midi)));
  const pitchClass = ((normalizedMidi % 12) + 12) % 12;

  if (spelling !== 'auto') {
    const targetAlter = ACCIDENTAL_SPELLING_TO_ALTER[spelling];
    const preferredStepIndex = NATURAL_MIDI_BY_DIATONIC_STEP.findIndex(
      (naturalPitchClass) => (naturalPitchClass + targetAlter + 120) % 12 === pitchClass,
    );

    if (preferredStepIndex >= 0) {
      const octave =
        (normalizedMidi - targetAlter - NATURAL_MIDI_BY_DIATONIC_STEP[preferredStepIndex]) / 12 - 1;
      if (Number.isInteger(octave)) return getDiatonicIndex(octave, preferredStepIndex);
    }
  }

  return getDiatonicIndex(
    Math.floor(normalizedMidi / 12) - 1,
    DEFAULT_MIDI_PITCH_CLASS_TO_DIATONIC_STEP[pitchClass],
  );
};

const getFractionDivisions = (fraction?: FractionLike) => {
  if (!fraction) return null;

  const realValue = getNumberValue(fraction.RealValue);
  if (realValue != null) {
    return Math.round(realValue * DIVISIONS_PER_WHOLE_NOTE);
  }

  const denominator = getNumberValue(fraction.Denominator);
  if (!denominator) return null;

  const expandedNumerator =
    typeof fraction.GetExpandedNumerator === 'function'
      ? getNumberValue(fraction.GetExpandedNumerator())
      : null;
  const numerator =
    expandedNumerator ??
    (getNumberValue(fraction.WholeValue) ?? 0) * denominator +
      (getNumberValue(fraction.Numerator) ?? 0);

  return Math.round((numerator / denominator) * DIVISIONS_PER_WHOLE_NOTE);
};

const getSourceNoteMidi = (sourceNote?: SourceNoteLike) => {
  if (!sourceNote || sourceNote.isRest?.()) return null;

  const directHalfTone = getNumberValue(sourceNote.halfTone);
  if (directHalfTone != null) return Math.round(directHalfTone);

  const pitch = sourceNote.Pitch;
  const pitchHalfTone = getNumberValue(pitch?.getHalfTone?.());
  if (pitchHalfTone != null) return Math.round(pitchHalfTone);

  const octave = getNumberValue(pitch?.Octave);
  const fundamentalNote = getNumberValue(pitch?.FundamentalNote);
  if (octave == null || fundamentalNote == null) return null;

  return Math.round(
    (octave + 1) * 12 + fundamentalNote + (getNumberValue(pitch?.AccidentalHalfTones) ?? 0),
  );
};

const getElementBounds = (elements: Element[]) => {
  const rects = elements
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);
  if (rects.length === 0) return null;

  return rects.reduce(
    (bounds, rect) => ({
      left: Math.min(bounds.left, rect.left),
      right: Math.max(bounds.right, rect.right),
      top: Math.min(bounds.top, rect.top),
      bottom: Math.max(bounds.bottom, rect.bottom),
    }),
    {
      left: rects[0].left,
      right: rects[0].right,
      top: rects[0].top,
      bottom: rects[0].bottom,
    },
  );
};

const getElementCenterPosition = (element: Element, editorElement: HTMLDivElement) => {
  const editorRect = editorElement.getBoundingClientRect();
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return null;

  return {
    leftPx: (rect.left + rect.right) / 2 - editorRect.left + editorElement.scrollLeft,
    topPx: (rect.top + rect.bottom) / 2 - editorRect.top + editorElement.scrollTop,
  };
};

const getGraphicalNoteShapePosition = (note: GraphicalNoteLike, editorElement: HTMLDivElement) => {
  const svgElement = editorElement.querySelector('svg');
  if (!(svgElement instanceof SVGSVGElement)) return null;

  const svgCoordinateMapper = getSvgCoordinateMapper(svgElement, editorElement);
  const absolutePosition = note.PositionAndShape?.AbsolutePosition;
  if (!svgCoordinateMapper || !absolutePosition) return null;

  const x = getNumberValue(absolutePosition.x);
  const y = getNumberValue(absolutePosition.y);
  if (x == null || y == null) return null;

  return svgCoordinateMapper({ x, y });
};

const getClosestElementToPosition = (
  elements: Element[],
  targetPosition: NoteHandlePosition,
  editorElement: HTMLDivElement,
) =>
  elements
    .map((element) => {
      const elementPosition = getElementCenterPosition(element, editorElement);
      if (!elementPosition) return null;
      return {
        element,
        distance:
          (elementPosition.leftPx - targetPosition.leftPx) ** 2 +
          (elementPosition.topPx - targetPosition.topPx) ** 2,
      };
    })
    .filter((candidate): candidate is { element: Element; distance: number } => candidate != null)
    .sort((a, b) => a.distance - b.distance)[0]?.element ?? null;

const getGraphicalNoteElements = (
  note: GraphicalNoteLike,
  editorElement: HTMLDivElement,
  shapePosition: NoteHandlePosition | null,
) => {
  try {
    const noteheadElements = note.getNoteheadSVGs?.() ?? [];
    if (noteheadElements.length > 0) {
      const noteheadIndex = getNumberValue(note.vfnoteIndex);
      const indexedNotehead =
        noteheadIndex == null ? null : noteheadElements[Math.max(0, Math.round(noteheadIndex))];
      if (indexedNotehead) return [indexedNotehead];

      if (shapePosition && noteheadElements.length > 1) {
        const closestNotehead = getClosestElementToPosition(
          noteheadElements,
          shapePosition,
          editorElement,
        );
        if (closestNotehead) return [closestNotehead];
      }

      return [noteheadElements[0]];
    }
  } catch {
    // OSMD can throw for non-SVG backends or incomplete rest graphics; fall back to the note group.
  }

  try {
    const noteElement = note.getSVGGElement?.();
    return noteElement ? [noteElement] : [];
  } catch {
    return [];
  }
};

const getLiveDraftEventIdFromTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return null;
  return (
    target.closest('[data-live-draft-event-id]')?.getAttribute('data-live-draft-event-id') ?? null
  );
};

const getLiveDraftMidiFromTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return null;
  const rawMidi = target.closest('[data-live-draft-midi]')?.getAttribute('data-live-draft-midi');
  if (rawMidi == null) return null;

  const midi = Number(rawMidi);
  return Number.isFinite(midi) ? Math.round(midi) : null;
};

const getEventMidiPositionKey = (eventId: string, midi: number) => `${eventId}:${midi}`;

const getRenderedNoteAnchors = (osmd: OSMD, editorElement: HTMLDivElement) => {
  const editorRect = editorElement.getBoundingClientRect();
  const graphicSheet = osmd.GraphicSheet as unknown as GraphicSheetLike;
  const anchors: RenderedNoteAnchor[] = [];

  graphicSheet.VerticalGraphicalStaffEntryContainers?.forEach((container) => {
    container.StaffEntries?.forEach((staffEntry) => {
      staffEntry?.graphicalVoiceEntries?.forEach((voiceEntry) => {
        voiceEntry.notes?.forEach((note) => {
          const midi = getSourceNoteMidi(note.sourceNote);
          const startDivisions = getFractionDivisions(note.sourceNote?.getAbsoluteTimestamp?.());
          const shapePosition = getGraphicalNoteShapePosition(note, editorElement);
          const elements = getGraphicalNoteElements(note, editorElement, shapePosition);
          const bounds = getElementBounds(elements);
          if (midi == null || startDivisions == null || (!shapePosition && !bounds)) return;
          const elementPosition = bounds
            ? {
                leftPx:
                  (bounds.left + bounds.right) / 2 - editorRect.left + editorElement.scrollLeft,
                topPx: (bounds.top + bounds.bottom) / 2 - editorRect.top + editorElement.scrollTop,
              }
            : null;
          const anchorPosition = elementPosition ?? shapePosition;
          if (!anchorPosition) return;

          anchors.push({
            midi,
            startDivisions,
            elements,
            leftPx: anchorPosition.leftPx,
            topPx: anchorPosition.topPx,
          });
        });
      });
    });
  });

  return anchors;
};

const getClampedRatio = (value: number) => Math.min(1, Math.max(0, value));

const getNaturalMidiFromDiatonicIndex = (diatonicIndex: number) => {
  const octave = Math.floor(diatonicIndex / NATURAL_MIDI_BY_DIATONIC_STEP.length);
  const naturalStepIndex =
    ((diatonicIndex % NATURAL_MIDI_BY_DIATONIC_STEP.length) +
      NATURAL_MIDI_BY_DIATONIC_STEP.length) %
    NATURAL_MIDI_BY_DIATONIC_STEP.length;

  return (octave + 1) * 12 + NATURAL_MIDI_BY_DIATONIC_STEP[naturalStepIndex];
};

const getSvgViewBox = (svgElement: SVGSVGElement) => {
  const baseValue = svgElement.viewBox?.baseVal;
  if (baseValue && baseValue.width > 0 && baseValue.height > 0) {
    return {
      x: baseValue.x,
      y: baseValue.y,
      width: baseValue.width,
      height: baseValue.height,
    };
  }

  const rawViewBox = svgElement.getAttribute('viewBox');
  const viewBoxParts =
    rawViewBox
      ?.trim()
      .split(/[\s,]+/)
      .map(Number) ?? [];
  if (
    viewBoxParts.length === 4 &&
    viewBoxParts.every((value) => Number.isFinite(value)) &&
    viewBoxParts[2] > 0 &&
    viewBoxParts[3] > 0
  ) {
    return {
      x: viewBoxParts[0],
      y: viewBoxParts[1],
      width: viewBoxParts[2],
      height: viewBoxParts[3],
    };
  }

  const width = Number(svgElement.getAttribute('width'));
  const height = Number(svgElement.getAttribute('height'));
  return {
    x: 0,
    y: 0,
    width: Number.isFinite(width) && width > 0 ? width : Math.max(svgElement.clientWidth, 1),
    height: Number.isFinite(height) && height > 0 ? height : Math.max(svgElement.clientHeight, 1),
  };
};

const getSvgCoordinateMapper = (
  svgElement: SVGSVGElement,
  editorElement: HTMLDivElement,
): SvgCoordinateMapper | null => {
  const editorRect = editorElement.getBoundingClientRect();
  const svgRect = svgElement.getBoundingClientRect();
  const viewBox = getSvgViewBox(svgElement);

  if (svgRect.width <= 0 || svgRect.height <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
    return null;
  }

  return (point) => ({
    leftPx:
      svgRect.left -
      editorRect.left +
      editorElement.scrollLeft +
      (point.x * OSMD_UNIT_IN_PIXELS - viewBox.x) * (svgRect.width / viewBox.width),
    topPx:
      svgRect.top -
      editorRect.top +
      editorElement.scrollTop +
      (point.y * OSMD_UNIT_IN_PIXELS - viewBox.y) * (svgRect.height / viewBox.height),
  });
};

const getStaffLineYCenters = (staffLine: StaffLineLike, editorElement: HTMLDivElement) => {
  const editorRect = editorElement.getBoundingClientRect();

  return (staffLine.StaffLines ?? [])
    .map((line) => {
      if (!(line.SVGElement instanceof Element)) return null;
      const rect = line.SVGElement.getBoundingClientRect();
      if (rect.height <= 0 && rect.width <= 0) return null;
      return (rect.top + rect.bottom) / 2 - editorRect.top + editorElement.scrollTop;
    })
    .filter((value): value is number => value != null && Number.isFinite(value))
    .sort((a, b) => a - b);
};

const getStaffLineGeometry = (
  staffLine: StaffLineLike,
  editorElement: HTMLDivElement,
  svgCoordinateMapper: SvgCoordinateMapper,
) => {
  const lineCenters = getStaffLineYCenters(staffLine, editorElement);
  if (lineCenters.length >= 2) {
    const gaps = lineCenters
      .slice(1)
      .map((lineCenter, index) => lineCenter - lineCenters[index])
      .filter((gap) => gap > 0)
      .sort((a, b) => a - b);
    const medianGap = gaps[Math.floor(gaps.length / 2)];
    if (medianGap && Number.isFinite(medianGap)) {
      return {
        topLinePx: lineCenters[0],
        lineGapPx: medianGap,
      };
    }
  }

  const absolutePosition = staffLine.PositionAndShape?.AbsolutePosition;
  if (!absolutePosition) return null;

  const topLinePosition = svgCoordinateMapper({
    x: getNumberValue(absolutePosition.x) ?? 0,
    y: getNumberValue(absolutePosition.y) ?? 0,
  });
  const staffHeight = getNumberValue(staffLine.StaffHeight) ?? 4;
  const bottomLinePosition = svgCoordinateMapper({
    x: getNumberValue(absolutePosition.x) ?? 0,
    y: (getNumberValue(absolutePosition.y) ?? 0) + staffHeight,
  });

  return {
    topLinePx: topLinePosition.topPx,
    lineGapPx: Math.max(1, Math.abs(bottomLinePosition.topPx - topLinePosition.topPx) / 4),
  };
};

const getStaffNumberFromStaffLine = (
  staffLine: StaffLineLike,
  staffLineIndex: number,
): StaffNumber => {
  const sourceStaffIndex = getNumberValue(staffLine.ParentStaff?.idInMusicSheet);
  const normalizedStaffIndex =
    sourceStaffIndex == null ? staffLineIndex : Math.max(0, Math.round(sourceStaffIndex));

  return normalizedStaffIndex % 2 === 0 ? 1 : 2;
};

const getMeasureStartDivisions = (
  measure: GraphicalMeasureLike,
  fallbackMeasureIndex: number,
  measureDurationDivisions: number,
) => {
  const absoluteTimestampDivisions = getFractionDivisions(
    measure.parentSourceMeasure?.AbsoluteTimestamp,
  );
  if (absoluteTimestampDivisions != null) return absoluteTimestampDivisions;

  const sourceMeasureIndex = getNumberValue(measure.parentSourceMeasure?.measureListIndex);
  if (sourceMeasureIndex != null) {
    return Math.max(0, Math.round(sourceMeasureIndex)) * measureDurationDivisions;
  }

  const measureNumber = getNumberValue(
    measure.MeasureNumber ?? measure.parentSourceMeasure?.MeasureNumber,
  );
  if (measureNumber != null && measureNumber > 0) {
    return (Math.round(measureNumber) - 1) * measureDurationDivisions;
  }

  return fallbackMeasureIndex * measureDurationDivisions;
};

const getRenderedScoreGeometry = (
  osmd: OSMD,
  editorElement: HTMLDivElement,
  measureDurationDivisions: number,
): ScoreGeometry | null => {
  const svgElement = editorElement.querySelector('svg');
  if (!(svgElement instanceof SVGSVGElement)) return null;

  const svgCoordinateMapper = getSvgCoordinateMapper(svgElement, editorElement);
  if (!svgCoordinateMapper) return null;

  const graphicSheet = osmd.GraphicSheet as unknown as GraphicSheetLike;
  const staves: ScoreStaffGeometry[] = [];

  let systemIndex = 0;

  graphicSheet.MusicPages?.forEach((page) => {
    page.MusicSystems?.forEach((system) => {
      const currentSystemIndex = systemIndex++;

      system.StaffLines?.forEach((staffLine, staffLineIndex) => {
        const staffLineGeometry = getStaffLineGeometry(
          staffLine,
          editorElement,
          svgCoordinateMapper,
        );
        if (!staffLineGeometry) return;

        const measures = (staffLine.Measures ?? [])
          .map((measure, measureIndex) => {
            const absolutePosition = measure.PositionAndShape?.AbsolutePosition;
            const size = measure.PositionAndShape?.Size;
            const leftUnit = getNumberValue(absolutePosition?.x);
            const topUnit = getNumberValue(absolutePosition?.y);
            const widthUnit = getNumberValue(size?.width);
            if (leftUnit == null || topUnit == null || widthUnit == null || widthUnit <= 0) {
              return null;
            }

            const beginInstructionsWidth = Math.max(
              0,
              getNumberValue(measure.beginInstructionsWidth) ?? 0,
            );
            const endInstructionsWidth = Math.max(
              0,
              getNumberValue(measure.endInstructionsWidth) ?? 0,
            );
            const left = svgCoordinateMapper({ x: leftUnit, y: topUnit }).leftPx;
            const right = svgCoordinateMapper({ x: leftUnit + widthUnit, y: topUnit }).leftPx;
            const contentLeft = svgCoordinateMapper({
              x: leftUnit + Math.min(beginInstructionsWidth, widthUnit),
              y: topUnit,
            }).leftPx;
            const contentRight = svgCoordinateMapper({
              x: leftUnit + Math.max(beginInstructionsWidth, widthUnit - endInstructionsWidth),
              y: topUnit,
            }).leftPx;

            return {
              startDivisions: getMeasureStartDivisions(
                measure,
                measureIndex,
                measureDurationDivisions,
              ),
              durationDivisions: measureDurationDivisions,
              leftPx: Math.min(left, right),
              rightPx: Math.max(left, right),
              contentLeftPx: Math.min(contentLeft, contentRight),
              contentRightPx: Math.max(contentLeft, contentRight),
            };
          })
          .filter((measure): measure is ScoreMeasureGeometry => measure != null)
          .sort((a, b) => a.startDivisions - b.startDivisions);

        if (measures.length === 0) return;

        const { topLinePx, lineGapPx } = staffLineGeometry;
        staves.push({
          systemIndex: currentSystemIndex,
          staff: getStaffNumberFromStaffLine(staffLine, staffLineIndex),
          topLinePx,
          lineGapPx,
          centerYPx: topLinePx + lineGapPx * 2,
          topBoundPx: topLinePx - lineGapPx * 4,
          bottomBoundPx: topLinePx + lineGapPx * 8,
          measures,
        });
      });
    });
  });

  return staves.length > 0 ? { staves } : null;
};

const getMidiFromScoreStaffY = (yPx: number, staffGeometry: ScoreStaffGeometry) => {
  const diatonicStepsFromTop = Math.round(
    (yPx - staffGeometry.topLinePx) / Math.max(1, staffGeometry.lineGapPx / 2),
  );
  const topLineDiatonicIndex =
    staffGeometry.staff === 2 ? BASS_TOP_LINE_DIATONIC_INDEX : TREBLE_TOP_LINE_DIATONIC_INDEX;

  return Math.max(
    EDITOR_BOTTOM_MIDI,
    Math.min(
      EDITOR_TOP_MIDI,
      getNaturalMidiFromDiatonicIndex(topLineDiatonicIndex - diatonicStepsFromTop),
    ),
  );
};

const getClosestScoreStaff = (yPx: number, scoreGeometry: ScoreGeometry) =>
  scoreGeometry.staves
    .map((staff) => ({
      staff,
      distance:
        yPx < staff.topBoundPx
          ? staff.topBoundPx - yPx
          : yPx > staff.bottomBoundPx
            ? yPx - staff.bottomBoundPx
            : Math.abs(yPx - staff.centerYPx) * 0.01,
    }))
    .sort((a, b) => a.distance - b.distance)[0]?.staff ?? null;

const getClosestScoreMeasure = (xPx: number, staffGeometry: ScoreStaffGeometry) =>
  staffGeometry.measures
    .map((measure) => ({
      measure,
      distance:
        xPx < measure.leftPx
          ? measure.leftPx - xPx
          : xPx > measure.rightPx
            ? xPx - measure.rightPx
            : 0,
    }))
    .sort((a, b) => a.distance - b.distance)[0]?.measure ?? null;

const getEventStaffForMidi = (event: Pick<TimelineDraftEvent, 'staff'>, midi: number) => {
  if (event.staff === 1 || event.staff === 2) return event.staff;
  return midi >= STAFF_SPLIT_MIDI ? 1 : 2;
};

const getScoreStaffAndMeasureAtDivisions = (
  scoreGeometry: ScoreGeometry,
  staff: StaffNumber,
  divisions: number,
) =>
  scoreGeometry.staves
    .filter((staffGeometry) => staffGeometry.staff === staff)
    .flatMap((staffGeometry) =>
      staffGeometry.measures.map((measure) => {
        const measureStart = measure.startDivisions;
        const measureEnd = measure.startDivisions + measure.durationDivisions;
        const distance =
          divisions >= measureStart && divisions < measureEnd
            ? 0
            : divisions === measureEnd
              ? 0.01
              : divisions < measureStart
                ? measureStart - divisions
                : divisions - measureEnd;

        return {
          staffGeometry,
          measure,
          distance,
        };
      }),
    )
    .sort((a, b) => a.distance - b.distance)[0] ?? null;

const getFallbackNoteHandlePosition = (
  scoreGeometry: ScoreGeometry | null,
  event: TimelineDraftEvent,
  midi: number,
  renderedAnchorsAtBeat: RenderedNoteAnchor[] = [],
): NoteHandlePosition | null => {
  if (!scoreGeometry) return null;

  const staff = getEventStaffForMidi(event, midi);
  const target = getScoreStaffAndMeasureAtDivisions(scoreGeometry, staff, event.startDivisions);
  if (!target) return null;

  const measureStart = target.measure.startDivisions;
  const measureDuration = Math.max(1, target.measure.durationDivisions);
  const ratio = getClampedRatio((event.startDivisions - measureStart) / measureDuration);
  const contentWidth = Math.max(1, target.measure.contentRightPx - target.measure.contentLeftPx);
  const topLineDiatonicIndex =
    staff === 2 ? BASS_TOP_LINE_DIATONIC_INDEX : TREBLE_TOP_LINE_DIATONIC_INDEX;
  const midiDiatonicIndex = getDiatonicIndexFromMidi(
    midi,
    event.accidentalSpellings?.[midi] ?? 'auto',
  );

  const geometryPosition = {
    leftPx: target.measure.contentLeftPx + ratio * contentWidth,
    topPx:
      target.staffGeometry.topLinePx +
      (topLineDiatonicIndex - midiDiatonicIndex) * Math.max(1, target.staffGeometry.lineGapPx / 2),
  };

  const nearestRenderedAnchor = renderedAnchorsAtBeat
    .map((anchor) => ({
      anchor,
      score:
        Math.abs(anchor.midi - midi) * 4 +
        Math.abs(anchor.topPx - geometryPosition.topPx) +
        Math.abs(anchor.leftPx - geometryPosition.leftPx) * 0.1,
    }))
    .sort((a, b) => a.score - b.score)[0]?.anchor;

  return {
    leftPx: nearestRenderedAnchor?.leftPx ?? geometryPosition.leftPx,
    topPx: geometryPosition.topPx,
  };
};

const getFallbackRestHandlePosition = (
  scoreGeometry: ScoreGeometry | null,
  event: TimelineDraftEvent,
): NoteHandlePosition | null => {
  if (!scoreGeometry) return null;

  const staff = event.staff ?? 1;
  const target = getScoreStaffAndMeasureAtDivisions(scoreGeometry, staff, event.startDivisions);
  if (!target) return null;

  const measureStart = target.measure.startDivisions;
  const measureDuration = Math.max(1, target.measure.durationDivisions);
  const ratio = getClampedRatio((event.startDivisions - measureStart) / measureDuration);
  const contentWidth = Math.max(1, target.measure.contentRightPx - target.measure.contentLeftPx);

  return {
    leftPx: target.measure.contentLeftPx + ratio * contentWidth,
    topPx: target.staffGeometry.centerYPx,
  };
};

const getScoreCursorGeometryFromDivisions = (
  scoreGeometry: ScoreGeometry,
  divisions: number,
): ScoreCursorGeometry | null => {
  const safeDivisions = Math.max(0, divisions);
  const candidates = scoreGeometry.staves.flatMap((staffGeometry) =>
    staffGeometry.measures.map((measure) => {
      const systemStaves = scoreGeometry.staves.filter(
        (systemStaff) => systemStaff.systemIndex === staffGeometry.systemIndex,
      );
      const measureStart = measure.startDivisions;
      const measureEnd = measure.startDivisions + measure.durationDivisions;
      const clampedDivisions = Math.min(measureEnd, Math.max(measureStart, safeDivisions));
      const ratio = getClampedRatio(
        (clampedDivisions - measureStart) / Math.max(1, measure.durationDivisions),
      );
      const contentWidth = Math.max(1, measure.contentRightPx - measure.contentLeftPx);
      const insideMeasure = safeDivisions >= measureStart && safeDivisions < measureEnd;
      const distance = insideMeasure
        ? 0
        : safeDivisions === measureEnd
          ? 0.01
          : safeDivisions < measureStart
            ? measureStart - safeDivisions
            : safeDivisions - measureEnd;

      return {
        leftPx: measure.contentLeftPx + ratio * contentWidth,
        topPx: Math.min(...systemStaves.map((systemStaff) => systemStaff.topBoundPx)),
        heightPx:
          Math.max(...systemStaves.map((systemStaff) => systemStaff.bottomBoundPx)) -
          Math.min(...systemStaves.map((systemStaff) => systemStaff.topBoundPx)),
        distance,
        startsAtPointer: safeDivisions === measureStart ? 0 : 1,
      };
    }),
  );

  return (
    candidates.sort(
      (a, b) => a.distance - b.distance || a.startsAtPointer - b.startsAtPointer,
    )[0] ?? null
  );
};

const getPlaybackBeatDurationSeconds = (tempoBpm: number) => 60 / Math.max(tempoBpm, 1);

const getDivisionDurationSeconds = (tempoBpm: number) =>
  getPlaybackBeatDurationSeconds(tempoBpm) / DIVISIONS_PER_BEAT;

const getScoreDragGlyphColors = (scoreTheme: ScoreTheme) => {
  const scorePalette = getScoreThemePalette(scoreTheme);
  const practiceDarkMode = scoreThemeToPracticeDarkMode(scoreTheme);

  if (practiceDarkMode) {
    return {
      fillColor: 'rgba(243, 247, 255, 0.68)',
      borderColor: 'rgba(243, 247, 255, 0.84)',
      stemColor: 'rgba(243, 247, 255, 0.76)',
    };
  }

  if (scoreTheme === 'dimPaper') {
    return {
      fillColor: scorePalette.disabledNoteColor,
      borderColor: scorePalette.notationColor,
      stemColor: scorePalette.disabledNoteColor,
    };
  }

  return {
    fillColor: 'rgba(17, 24, 39, 0.58)',
    borderColor: 'rgba(17, 24, 39, 0.72)',
    stemColor: 'rgba(17, 24, 39, 0.64)',
  };
};

let transparentDragImageCanvas: HTMLCanvasElement | null = null;

const getTransparentDragImage = () => {
  if (!transparentDragImageCanvas) {
    transparentDragImageCanvas = document.createElement('canvas');
    transparentDragImageCanvas.width = 1;
    transparentDragImageCanvas.height = 1;
  }

  return transparentDragImageCanvas;
};

const getDraftFileName = (title: string) => {
  const normalizedTitle = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${normalizedTitle || 'repertoire-draft'}.musicxml`;
};

const upsertById = <T extends { id: number }>(items: T[], nextItem: T) => [
  nextItem,
  ...items.filter((item) => item.id !== nextItem.id),
];

const cloneLiveComposerEvents = (events: LiveComposerEvent[]) =>
  events.map((event) => ({
    ...event,
    midiNotes: [...event.midiNotes],
    accidentalSpellings: event.accidentalSpellings ? { ...event.accidentalSpellings } : undefined,
  }));

const cloneLiveComposerPedalEvents = (events: LiveComposerPedalEvent[]) =>
  events.map((event) => ({ ...event }));

const cloneDraftSnapshot = (snapshot: ComposerDraftSnapshot): ComposerDraftSnapshot => ({
  ...snapshot,
  capturedEvents: cloneLiveComposerEvents(snapshot.capturedEvents),
  capturedPedalEvents: cloneLiveComposerPedalEvents(snapshot.capturedPedalEvents),
});

const isRecordingHandMode = (value: unknown): value is RecordingHandMode =>
  value === 'both' || value === 'right' || value === 'left';

const clampDraftTempo = (tempo: unknown) =>
  typeof tempo === 'number' && Number.isFinite(tempo)
    ? Math.max(MIN_PLAYBACK_TEMPO_BPM, Math.min(MAX_PLAYBACK_TEMPO_BPM, Math.round(tempo)))
    : DEFAULT_PLAYBACK_TEMPO_BPM;

const clampStoredCaptureTempo = (tempo: unknown) =>
  typeof tempo === 'number' && Number.isFinite(tempo)
    ? clampTempoBpm(tempo, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
    : DEFAULT_PLAYBACK_TEMPO_BPM;

const clampStoredCaptureConfidence = (confidence: unknown) =>
  typeof confidence === 'number' && Number.isFinite(confidence)
    ? Math.max(0, Math.min(1, confidence))
    : 0;

const normalizeDraftSnapshot = (value: unknown): ComposerDraftSnapshot | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<ComposerDraftSnapshot>;
  const selectedDuration = DRAFT_DURATION_OPTIONS.some(
    (option) => option.divisions === raw.selectedDurationDivisions,
  )
    ? Number(raw.selectedDurationDivisions)
    : DRAFT_DURATION_OPTIONS[2].divisions;
  const timeSignatureId = COMMON_TIME_SIGNATURES.some(
    (timeSignature) => timeSignature.id === raw.timeSignatureId,
  )
    ? String(raw.timeSignatureId)
    : DEFAULT_TIME_SIGNATURE.id;

  return {
    capturedEvents: Array.isArray(raw.capturedEvents)
      ? cloneLiveComposerEvents(raw.capturedEvents)
      : [],
    capturedPedalEvents: Array.isArray(raw.capturedPedalEvents)
      ? cloneLiveComposerPedalEvents(raw.capturedPedalEvents)
      : [],
    draftTitle:
      typeof raw.draftTitle === 'string' && raw.draftTitle.trim()
        ? raw.draftTitle
        : DEFAULT_DRAFT_TITLE,
    timeSignatureId,
    selectedDurationDivisions: selectedDuration,
    recordingCursorDivisions:
      typeof raw.recordingCursorDivisions === 'number' &&
      Number.isFinite(raw.recordingCursorDivisions)
        ? Math.max(0, Math.round(raw.recordingCursorDivisions))
        : 0,
    recordingHandMode: isRecordingHandMode(raw.recordingHandMode) ? raw.recordingHandMode : 'both',
    playbackTempo: clampDraftTempo(raw.playbackTempo),
    metronomeEnabled: Boolean(raw.metronomeEnabled),
    captureTempoAutoEnabled:
      typeof raw.captureTempoAutoEnabled === 'boolean' ? raw.captureTempoAutoEnabled : true,
    captureTempoBpm: clampStoredCaptureTempo(raw.captureTempoBpm),
    captureTempoMinBpm:
      typeof raw.captureTempoMinBpm === 'number' && Number.isFinite(raw.captureTempoMinBpm)
        ? clampTempoBpm(raw.captureTempoMinBpm, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
        : DEFAULT_CAPTURE_TEMPO_MIN_BPM,
    captureTempoMaxBpm:
      typeof raw.captureTempoMaxBpm === 'number' && Number.isFinite(raw.captureTempoMaxBpm)
        ? clampTempoBpm(raw.captureTempoMaxBpm, MIN_PLAYBACK_TEMPO_BPM, MAX_PLAYBACK_TEMPO_BPM)
        : DEFAULT_CAPTURE_TEMPO_MAX_BPM,
    captureTempoConfidence: clampStoredCaptureConfidence(raw.captureTempoConfidence),
  };
};

const readStoredDraftSnapshot = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(DRAFT_AUTOSAVE_KEY);
    return raw ? normalizeDraftSnapshot(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const writeStoredDraftSnapshot = (snapshot: ComposerDraftSnapshot) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(DRAFT_AUTOSAVE_KEY, JSON.stringify(snapshot));
  } catch {
    // Draft recovery is a convenience; storage failures should not block composing.
  }
};

const clearStoredDraftSnapshot = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(DRAFT_AUTOSAVE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const draftHasMaterial = (snapshot: ComposerDraftSnapshot) =>
  snapshot.capturedEvents.length > 0 ||
  snapshot.capturedPedalEvents.length > 0 ||
  snapshot.draftTitle !== DEFAULT_DRAFT_TITLE ||
  snapshot.timeSignatureId !== DEFAULT_TIME_SIGNATURE.id;

const getDraftSnapshotSignature = (snapshot: ComposerDraftSnapshot) =>
  JSON.stringify({
    events: snapshot.capturedEvents.map((event) => ({
      id: event.id,
      midiNotes: event.midiNotes,
      accidentalSpellings: event.accidentalSpellings,
      source: event.source,
      restBeforeDivisions: event.restBeforeDivisions,
      durationDivisions: event.durationDivisions,
      startDivisions: event.startDivisions,
      staff: event.staff,
      endedAtMs: event.endedAtMs,
      adaptiveTiming: event.adaptiveTiming,
      rawStartedAtMs: event.rawStartedAtMs,
      rawEndedAtMs: event.rawEndedAtMs,
      rawCaptureSessionStartedAtMs: event.rawCaptureSessionStartedAtMs,
      rawCaptureSessionStartDivisions: event.rawCaptureSessionStartDivisions,
    })),
    pedalEvents: snapshot.capturedPedalEvents.map((event) => ({
      id: event.id,
      pedalDown: event.pedalDown,
      positionDivisions: event.positionDivisions,
      adaptiveTiming: event.adaptiveTiming,
      rawCaptureSessionStartedAtMs: event.rawCaptureSessionStartedAtMs,
      rawCaptureSessionStartDivisions: event.rawCaptureSessionStartDivisions,
    })),
    draftTitle: snapshot.draftTitle,
    timeSignatureId: snapshot.timeSignatureId,
    selectedDurationDivisions: snapshot.selectedDurationDivisions,
    recordingCursorDivisions: snapshot.recordingCursorDivisions,
    recordingHandMode: snapshot.recordingHandMode,
    playbackTempo: snapshot.playbackTempo,
    metronomeEnabled: snapshot.metronomeEnabled,
    captureTempoAutoEnabled: snapshot.captureTempoAutoEnabled,
    captureTempoBpm: snapshot.captureTempoBpm,
    captureTempoMinBpm: snapshot.captureTempoMinBpm,
    captureTempoMaxBpm: snapshot.captureTempoMaxBpm,
    captureTempoConfidence: snapshot.captureTempoConfidence,
  });

const LiveComposerSheet = ({
  draftTitle,
  musicXml,
  measureCount,
  draftEvents,
  selectedEventIds,
  selectedNoteTarget,
  timeSignatureLabel,
  measureDurationDivisions,
  selectedDurationDivisions,
  recordingCursorDivisions,
  playbackCursorStartDivisions,
  playbackSessionKey,
  playbackTempo,
  metronomeEnabled,
  canPlay,
  isPlayingBack,
  showPlaybackCursor,
  scoreEditingEnabled,
  onTempoChange,
  onMetronomeToggle,
  onPlaybackToggle,
  onPlaybackRestart,
  onManualNoteDrop,
  onExistingNoteDrop,
  onEventSelect,
  onRecordingCursorChange,
  onManualNotePointerDragHandlerChange,
  audioDiagnosticsVisible,
  audioDiagnosticsSnapshot,
  audioDiagnosticsFrames,
  scoreTheme,
  notationToolbar,
}: {
  draftTitle: string;
  musicXml: string;
  measureCount: number;
  draftEvents: LiveComposerEvent[];
  selectedEventIds: string[];
  selectedNoteTarget: SelectedNoteTarget | null;
  timeSignatureLabel: string;
  measureDurationDivisions: number;
  selectedDurationDivisions: number;
  recordingCursorDivisions: number;
  playbackCursorStartDivisions: number;
  playbackSessionKey: number;
  playbackTempo: number;
  metronomeEnabled: boolean;
  canPlay: boolean;
  isPlayingBack: boolean;
  showPlaybackCursor: boolean;
  scoreEditingEnabled: boolean;
  onTempoChange: (tempo: number) => void;
  onMetronomeToggle: () => void;
  onPlaybackToggle: () => void;
  onPlaybackRestart: () => void;
  onManualNoteDrop: (drop: ManualNoteDrop) => void;
  onExistingNoteDrop: (eventId: string, drop: ExistingNoteDrop) => void;
  onEventSelect: (eventId: string, midi?: number | null, options?: EventSelectOptions) => void;
  onRecordingCursorChange: (divisions: number) => void;
  onManualNotePointerDragHandlerChange?: (handler: ManualNotePointerDragHandler | null) => void;
  audioDiagnosticsVisible: boolean;
  audioDiagnosticsSnapshot: AudioDiagnosticsSnapshot | null;
  audioDiagnosticsFrames: AudioDiagnosticsFrame[];
  scoreTheme: ScoreTheme;
  notationToolbar?: ReactNode;
}) => {
  const scorePalette = getScoreThemePalette(scoreTheme);
  const practiceDarkMode = scoreThemeToPracticeDarkMode(scoreTheme);
  const paperRectSelector =
    '& svg rect[fill="#fff"], & svg rect[fill="#FFF"], & svg rect[fill="#ffffff"], & svg rect[fill="#FFFFFF"], & svg rect[fill="white"]';
  const nonPaperRectSelector =
    '& svg rect:not([fill="#fff"]):not([fill="#FFF"]):not([fill="#ffffff"]):not([fill="#FFFFFF"]):not([fill="white"]):not([fill="none"]):not([fill="transparent"])';
  const sheetCardRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const osmdRef = useRef<OSMD | null>(null);
  const scoreGeometryRef = useRef<ScoreGeometry | null>(null);
  const playbackCursorRef = useRef<HTMLDivElement | null>(null);
  const playbackCursorDivisionsRef = useRef(playbackCursorStartDivisions);
  const updatePlaybackCursorElementRef = useRef<(divisions: number) => void>(() => {});
  const activeNoteDragPointerIdRef = useRef<number | null>(null);
  const activePaletteDragPointerIdRef = useRef<number | null>(null);
  const [isScoreReady, setIsScoreReady] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [noteHandlePositions, setNoteHandlePositions] = useState<
    Record<string, NoteHandlePosition>
  >({});
  const [noteHandlePositionsByMidi, setNoteHandlePositionsByMidi] = useState<
    Record<string, NoteHandlePosition>
  >({});
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  const [movingEventId, setMovingEventId] = useState<string | null>(null);
  const [movingNoteMidi, setMovingNoteMidi] = useState<number | null>(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState<
    (NoteHandlePosition & { eventId: string }) | null
  >(null);
  const [dragPreviewDrop, setDragPreviewDrop] = useState<
    (ExistingNoteDrop & { eventId: string }) | null
  >(null);
  const [manualDragPreviewDrop, setManualDragPreviewDrop] = useState<ManualNoteDrop | null>(null);
  const [manualDragPreviewPosition, setManualDragPreviewPosition] =
    useState<NoteHandlePosition | null>(null);
  const [transportInset, setTransportInset] = useState({ rightPx: 48, bottomPx: 72 });
  const suppressNextClickRef = useRef(false);
  const selectedEventIdSet = useMemo(() => new Set(selectedEventIds), [selectedEventIds]);
  const timelineEvents = useMemo(() => getDraftTimelineEvents(draftEvents), [draftEvents]);
  const totalEditorDivisions = Math.max(
    measureDurationDivisions,
    measureCount * measureDurationDivisions,
    getTimelineEndDivisions(draftEvents) + measureDurationDivisions,
  );
  const playbackTimeKeeper = useDraftPlaybackTimeKeeper({
    bpm: playbackTempo,
    isPlaying: isPlayingBack,
    cursorIsVisible: showPlaybackCursor,
    sessionKey: playbackSessionKey,
  });

  const getInteractionState = useCallback(
    (eventId: string, midi?: number | null) => {
      if (
        movingEventId === eventId &&
        (movingNoteMidi == null || midi == null || movingNoteMidi === midi)
      ) {
        return 'moving';
      }
      if (selectedEventIdSet.has(eventId)) {
        if (
          selectedNoteTarget?.eventId === eventId &&
          midi != null &&
          selectedNoteTarget.midi !== midi
        ) {
          return 'idle';
        }
        return 'selected';
      }
      if (hoveredEventId === eventId) return 'hover';
      return 'idle';
    },
    [hoveredEventId, movingEventId, movingNoteMidi, selectedEventIdSet, selectedNoteTarget],
  );

  const applyRenderedNoteStates = useCallback(() => {
    const sheetElement = sheetRef.current;
    if (!sheetElement) return;

    sheetElement.querySelectorAll('[data-live-draft-event-id]').forEach((element) => {
      const eventId = element.getAttribute('data-live-draft-event-id');
      if (!eventId) return;
      const rawMidi = element.getAttribute('data-live-draft-midi');
      const midi = rawMidi == null ? null : Number(rawMidi);
      const normalizedMidi =
        typeof midi === 'number' && Number.isFinite(midi) ? Math.round(midi) : null;
      element.setAttribute(
        'data-live-draft-note-state',
        getInteractionState(eventId, normalizedMidi),
      );
    });
  }, [getInteractionState]);

  useEffect(() => {
    if (!sheetRef.current) return;

    const osmd = createOSMD(sheetRef as RefObject<HTMLDivElement>, true);
    osmd.EngravingRules.PageTopMargin = 2;
    osmd.EngravingRules.PageBottomMargin = 2;
    osmd.EngravingRules.PageLeftMargin = 1;
    osmd.EngravingRules.PageRightMargin = 1;
    osmd.EngravingRules.SystemLeftMargin = 0;
    osmd.EngravingRules.SystemRightMargin = 0;
    osmd.EngravingRules.StaffDistance = 3;
    osmd.EngravingRules.BetweenStaffDistance = 2.5;
    osmd.EngravingRules.PageBackgroundColor = scorePalette.scoreBackground;
    osmd.EngravingRules.StaffLineColor = scorePalette.staffLineColor;
    osmd.EngravingRules.DefaultColorMusic = scorePalette.notationColor;
    osmd.EngravingRules.DefaultColorNotehead = scorePalette.notationColor;
    osmd.EngravingRules.DefaultColorRest = scorePalette.notationColor;
    osmd.EngravingRules.DefaultColorStem = scorePalette.notationColor;
    osmd.EngravingRules.DefaultColorLabel = scorePalette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorLyrics = scorePalette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorChordSymbol = scorePalette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorTitle = scorePalette.musicMarkingColor;
    osmd.EngravingRules.DefaultColorCursor = scorePalette.playbackCursor;
    osmdRef.current = osmd;

    return () => {
      osmdRef.current = null;
      scoreGeometryRef.current = null;
      if (sheetRef.current) {
        sheetRef.current.innerHTML = '';
      }
    };
  }, [scorePalette, scoreTheme]);

  const refreshScoreGeometry = useCallback(() => {
    const editorElement = editorRef.current;
    const osmd = osmdRef.current;
    if (!editorElement || !osmd) return scoreGeometryRef.current;

    const nextGeometry = getRenderedScoreGeometry(osmd, editorElement, measureDurationDivisions);
    if (nextGeometry) {
      scoreGeometryRef.current = nextGeometry;
    }

    return scoreGeometryRef.current;
  }, [measureDurationDivisions]);

  useEffect(() => {
    const osmd = osmdRef.current;
    if (!osmd) return;

    let cancelled = false;
    setRenderError(null);
    setIsScoreReady(false);

    const render = async () => {
      try {
        await osmd.load(musicXml);
        if (cancelled) return;
        osmd.render();
        applyScoreThemeToSvgElement(sheetRef.current, scoreTheme);
        refreshScoreGeometry();
        setIsScoreReady(true);
      } catch (error) {
        if (cancelled) return;
        setRenderError(error instanceof Error ? error.message : 'Failed to render score');
      }
    };

    void render();

    return () => {
      cancelled = true;
      setIsScoreReady(false);
    };
  }, [musicXml, refreshScoreGeometry, scoreTheme]);

  const updateNoteHandlePositions = useCallback(() => {
    const editorElement = editorRef.current;
    const osmd = osmdRef.current;
    if (!editorElement || !osmd || timelineEvents.length === 0) {
      setNoteHandlePositions({});
      setNoteHandlePositionsByMidi({});
      return;
    }

    const renderedAnchors = getRenderedNoteAnchors(osmd, editorElement);
    const scoreGeometry = scoreGeometryRef.current ?? refreshScoreGeometry();
    const nextPositions: Record<string, NoteHandlePosition> = {};
    const nextMidiPositions: Record<string, NoteHandlePosition> = {};

    sheetRef.current?.querySelectorAll('[data-live-draft-event-id]').forEach((element) => {
      element.removeAttribute('data-live-draft-event-id');
      element.removeAttribute('data-live-draft-midi');
      element.removeAttribute('data-live-draft-note-state');
    });

    timelineEvents.forEach((event) => {
      const eventMidiNotes = new Set(normalizeMidiNotes(event.midiNotes));
      if (eventMidiNotes.size === 0) {
        const fallbackPosition = getFallbackRestHandlePosition(scoreGeometry, event);
        if (fallbackPosition) {
          nextPositions[event.id] = fallbackPosition;
        }
        return;
      }

      const renderedAnchorsAtBeat = renderedAnchors.filter(
        (anchor) => Math.abs(anchor.startDivisions - event.startDivisions) <= 1,
      );
      let matchedAnchors = renderedAnchors.filter(
        (anchor) =>
          eventMidiNotes.has(anchor.midi) &&
          Math.abs(anchor.startDivisions - event.startDivisions) <= 1,
      );

      if (matchedAnchors.length === 0) {
        const eventMidiCenter =
          event.midiNotes.reduce((sum, midi) => sum + midi, 0) /
          Math.max(event.midiNotes.length, 1);
        const nearestAnchor = renderedAnchors
          .map((anchor) => ({
            anchor,
            score:
              Math.abs(anchor.startDivisions - event.startDivisions) * 8 +
              Math.abs(anchor.midi - eventMidiCenter),
          }))
          .filter((candidate) => candidate.score <= 24)
          .sort((a, b) => a.score - b.score)[0]?.anchor;

        matchedAnchors =
          nearestAnchor && eventMidiNotes.has(nearestAnchor.midi) ? [nearestAnchor] : [];
      }

      matchedAnchors.forEach((anchor) => {
        anchor.elements.forEach((element) => {
          element.setAttribute('data-live-draft-event-id', event.id);
          element.setAttribute('data-live-draft-midi', String(anchor.midi));
          element.setAttribute('data-live-draft-note-state', 'idle');
        });
        nextMidiPositions[getEventMidiPositionKey(event.id, anchor.midi)] = {
          leftPx: anchor.leftPx,
          topPx: anchor.topPx,
        };
      });

      eventMidiNotes.forEach((midi) => {
        const key = getEventMidiPositionKey(event.id, midi);
        const renderedPosition = nextMidiPositions[key];
        const fallbackPosition = getFallbackNoteHandlePosition(
          scoreGeometry,
          event,
          midi,
          renderedAnchorsAtBeat,
        );
        if (!fallbackPosition) return;

        if (!renderedPosition) {
          nextMidiPositions[key] = fallbackPosition;
          return;
        }

        if (
          Math.abs(renderedPosition.topPx - fallbackPosition.topPx) >
          NOTE_ANCHOR_VERTICAL_MISMATCH_PX
        ) {
          nextMidiPositions[key] = {
            leftPx: renderedPosition.leftPx,
            topPx: fallbackPosition.topPx,
          };
        }
      });

      const eventPositions = Array.from(eventMidiNotes)
        .map((midi) => nextMidiPositions[getEventMidiPositionKey(event.id, midi)])
        .filter((position): position is NoteHandlePosition => position != null);
      if (eventPositions.length === 0) return;

      nextPositions[event.id] = {
        leftPx:
          eventPositions.reduce((sum, position) => sum + position.leftPx, 0) /
          eventPositions.length,
        topPx:
          eventPositions.reduce((sum, position) => sum + position.topPx, 0) / eventPositions.length,
      };
    });

    setNoteHandlePositions(nextPositions);
    setNoteHandlePositionsByMidi(nextMidiPositions);
  }, [refreshScoreGeometry, timelineEvents]);

  useEffect(() => {
    applyRenderedNoteStates();
  }, [applyRenderedNoteStates, noteHandlePositions]);

  useEffect(() => {
    if (!isScoreReady) {
      setNoteHandlePositions({});
      setNoteHandlePositionsByMidi({});
      return;
    }

    let animationFrameId: number | null = null;
    const schedulePositionUpdate = () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        refreshScoreGeometry();
        updateNoteHandlePositions();
      });
    };

    schedulePositionUpdate();
    window.addEventListener('resize', schedulePositionUpdate);

    return () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', schedulePositionUpdate);
    };
  }, [isScoreReady, musicXml, refreshScoreGeometry, updateNoteHandlePositions]);

  useEffect(() => {
    const sheetCardElement = sheetCardRef.current;
    if (!sheetCardElement) return undefined;

    let animationFrameId: number | null = null;

    const updateTransportInset = () => {
      animationFrameId = null;
      const rect = sheetCardElement.getBoundingClientRect();
      const isDesktop = window.matchMedia('(min-width: 900px)').matches;
      const inlineInset = isDesktop ? 24 : 16;
      const blockInset = isDesktop ? 24 : 18;
      const nextInset = {
        rightPx: Math.round(Math.max(inlineInset, window.innerWidth - rect.right + inlineInset)),
        bottomPx: Math.round(Math.max(blockInset, window.innerHeight - rect.bottom + blockInset)),
      };

      setTransportInset((currentInset) =>
        currentInset.rightPx === nextInset.rightPx && currentInset.bottomPx === nextInset.bottomPx
          ? currentInset
          : nextInset,
      );
    };

    const scheduleTransportInsetUpdate = () => {
      if (animationFrameId != null) return;
      animationFrameId = window.requestAnimationFrame(updateTransportInset);
    };

    updateTransportInset();
    window.addEventListener('resize', scheduleTransportInsetUpdate);
    window.addEventListener('scroll', scheduleTransportInsetUpdate, true);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleTransportInsetUpdate)
        : null;
    resizeObserver?.observe(sheetCardElement);

    return () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', scheduleTransportInsetUpdate);
      window.removeEventListener('scroll', scheduleTransportInsetUpdate, true);
      resizeObserver?.disconnect();
    };
  }, []);

  const getPointerScorePlacement = useCallback(
    (clientX: number, clientY: number): PointerScorePlacement | null => {
      const editorElement = editorRef.current;
      const editorRect = editorElement?.getBoundingClientRect();
      if (!editorElement || !editorRect) return null;

      const editorPoint = {
        leftPx: clientX - editorRect.left + editorElement.scrollLeft,
        topPx: clientY - editorRect.top + editorElement.scrollTop,
      };
      const scoreGeometry = scoreGeometryRef.current ?? refreshScoreGeometry();

      if (scoreGeometry) {
        const staffGeometry = getClosestScoreStaff(editorPoint.topPx, scoreGeometry);
        const measureGeometry = staffGeometry
          ? getClosestScoreMeasure(editorPoint.leftPx, staffGeometry)
          : null;

        if (staffGeometry && measureGeometry) {
          const contentWidth = Math.max(
            1,
            measureGeometry.contentRightPx - measureGeometry.contentLeftPx,
          );
          const measureRatio = getClampedRatio(
            (editorPoint.leftPx - measureGeometry.contentLeftPx) / contentWidth,
          );

          return {
            midi: getMidiFromScoreStaffY(editorPoint.topPx, staffGeometry),
            rawStartDivisions:
              measureGeometry.startDivisions + measureRatio * measureGeometry.durationDivisions,
            staff: staffGeometry.staff,
          };
        }
      }

      const scoreElement = sheetRef.current?.querySelector('svg') ?? sheetRef.current;
      const scoreRect = scoreElement?.getBoundingClientRect() ?? editorRect;
      const editorWidth = Math.max(editorElement.scrollWidth, editorRect.width, 1);
      const scoreHeight = Math.max(scoreRect.height, 1);
      const relativeX = Math.min(
        editorWidth,
        Math.max(0, clientX - editorRect.left + editorElement.scrollLeft),
      );
      const relativeY = Math.min(scoreHeight, Math.max(0, clientY - scoreRect.top));

      return {
        midi: getMidiFromEditorY(relativeY, scoreHeight),
        rawStartDivisions: Math.max(0, (relativeX / editorWidth) * totalEditorDivisions),
      };
    },
    [refreshScoreGeometry, totalEditorDivisions],
  );

  const getRecordingCursorDivisionsFromPoint = useCallback(
    (clientX: number, clientY: number) => {
      const placement = getPointerScorePlacement(clientX, clientY);
      if (!placement) return null;

      return Math.max(0, Math.round(placement.rawStartDivisions));
    },
    [getPointerScorePlacement],
  );

  const getCursorPositionFromDivisions = useCallback(
    (divisions: number) => {
      const editorElement = editorRef.current;
      const editorRect = editorElement?.getBoundingClientRect();
      if (!editorElement || !editorRect) return null;

      const scoreElement = sheetRef.current?.querySelector('svg') ?? sheetRef.current;
      const scoreRect = scoreElement?.getBoundingClientRect() ?? editorRect;
      const editorWidth = Math.max(editorElement.scrollWidth, editorRect.width, 1);
      const scoreGeometry = scoreGeometryRef.current ?? refreshScoreGeometry();
      const renderedCursorGeometry = scoreGeometry
        ? getScoreCursorGeometryFromDivisions(scoreGeometry, divisions)
        : null;

      return {
        leftPx:
          renderedCursorGeometry?.leftPx ??
          (Math.max(0, divisions) / Math.max(totalEditorDivisions, 1)) * editorWidth,
        topPx:
          renderedCursorGeometry?.topPx ?? scoreRect.top - editorRect.top + editorElement.scrollTop,
        heightPx: Math.max(renderedCursorGeometry?.heightPx ?? scoreRect.height, 1),
      };
    },
    [refreshScoreGeometry, totalEditorDivisions],
  );

  const updatePlaybackCursorElement = useCallback(
    (divisions: number) => {
      playbackCursorDivisionsRef.current = divisions;
      const cursorElement = playbackCursorRef.current;
      if (!cursorElement) return;

      if (!showPlaybackCursor) {
        cursorElement.style.opacity = '0';
        return;
      }

      const cursorPosition = getCursorPositionFromDivisions(divisions);
      if (!cursorPosition) {
        cursorElement.style.opacity = '0';
        return;
      }

      cursorElement.style.transform = `translate3d(${cursorPosition.leftPx}px, ${cursorPosition.topPx}px, 0) translateX(-50%)`;
      cursorElement.style.height = `${cursorPosition.heightPx}px`;
      cursorElement.style.backgroundColor = scorePalette.playbackCursor;
      cursorElement.style.opacity = '0.7';
    },
    [getCursorPositionFromDivisions, scorePalette.playbackCursor, showPlaybackCursor],
  );

  useEffect(() => {
    updatePlaybackCursorElementRef.current = updatePlaybackCursorElement;
  }, [updatePlaybackCursorElement]);

  useEffect(() => {
    playbackCursorDivisionsRef.current = playbackCursorStartDivisions;
    updatePlaybackCursorElementRef.current(playbackCursorStartDivisions);
  }, [playbackCursorStartDivisions, playbackSessionKey]);

  useEffect(() => {
    if (!showPlaybackCursor) {
      const cursorElement = playbackCursorRef.current;
      if (cursorElement) {
        cursorElement.style.opacity = '0';
      }
      return;
    }

    updatePlaybackCursorElement(playbackCursorDivisionsRef.current);
  }, [showPlaybackCursor, updatePlaybackCursorElement]);

  useEffect(() => {
    if (!isScoreReady) return undefined;

    let animationFrameId: number | null = null;

    const scheduleCursorSync = () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        refreshScoreGeometry();
        updatePlaybackCursorElementRef.current(playbackCursorDivisionsRef.current);
      });
    };

    scheduleCursorSync();
    window.addEventListener('resize', scheduleCursorSync);

    return () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', scheduleCursorSync);
    };
  }, [isScoreReady, musicXml, refreshScoreGeometry]);

  useEffect(() => {
    if (!showPlaybackCursor || !playbackTimeKeeper) return undefined;

    let animationFrameId: number | null = null;

    const updatePlaybackCursor = () => {
      const elapsedDivisions =
        playbackTimeKeeper.audioTimeToMeasureTimestamp() * measureDurationDivisions;
      updatePlaybackCursorElementRef.current(
        playbackCursorStartDivisions + Math.max(0, elapsedDivisions),
      );

      if (isPlayingBack) {
        animationFrameId = window.requestAnimationFrame(updatePlaybackCursor);
      }
    };

    updatePlaybackCursor();

    return () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    isPlayingBack,
    measureDurationDivisions,
    playbackCursorStartDivisions,
    playbackTimeKeeper,
    showPlaybackCursor,
  ]);

  const getEditorPositionFromClientPoint = useCallback((clientX: number, clientY: number) => {
    const editorElement = editorRef.current;
    const editorRect = editorElement?.getBoundingClientRect();
    if (!editorElement || !editorRect) return null;

    return {
      leftPx: clientX - editorRect.left + editorElement.scrollLeft,
      topPx: clientY - editorRect.top + editorElement.scrollTop,
    };
  }, []);

  const getNearestRenderedNoteTarget = useCallback(
    (
      clientX: number,
      clientY: number,
      fallbackEventId?: string | null,
      fallbackMidi?: number | null,
    ) => {
      const pointerPosition = getEditorPositionFromClientPoint(clientX, clientY);
      const normalizedFallbackMidi = fallbackMidi == null ? null : Math.round(fallbackMidi);
      const fallbackTarget = fallbackEventId
        ? {
            eventId: fallbackEventId,
            midi: normalizedFallbackMidi,
          }
        : null;
      if (!pointerPosition) return fallbackTarget;

      const nearestTarget = timelineEvents
        .flatMap((event) =>
          normalizeMidiNotes(event.midiNotes).map((midi) => {
            const position = noteHandlePositionsByMidi[getEventMidiPositionKey(event.id, midi)];
            if (!position) return null;
            return {
              eventId: event.id,
              midi,
              distance:
                (position.leftPx - pointerPosition.leftPx) ** 2 +
                (position.topPx - pointerPosition.topPx) ** 2,
            };
          }),
        )
        .filter(
          (target): target is { eventId: string; midi: number; distance: number } => target != null,
        )
        .sort((a, b) => a.distance - b.distance)[0];

      if (!nearestTarget || nearestTarget.distance > 32 ** 2) return fallbackTarget;

      return {
        eventId: nearestTarget.eventId,
        midi: nearestTarget.midi,
      };
    },
    [getEditorPositionFromClientPoint, noteHandlePositionsByMidi, timelineEvents],
  );

  const getScorePixelsPerMidi = useCallback(() => {
    const editorElement = editorRef.current;
    const editorRect = editorElement?.getBoundingClientRect();
    if (!editorElement || !editorRect) return 1;

    const scoreElement = sheetRef.current?.querySelector('svg') ?? sheetRef.current;
    const scoreRect = scoreElement?.getBoundingClientRect() ?? editorRect;
    return Math.max(1, scoreRect.height / Math.max(1, EDITOR_TOP_MIDI - EDITOR_BOTTOM_MIDI));
  }, []);

  const handleRecordingCursorPointerDown = useCallback(
    (pointerEvent: ReactPointerEvent<HTMLDivElement>) => {
      if (pointerEvent.button !== 0) return;

      pointerEvent.preventDefault();
      pointerEvent.stopPropagation();
      const pointerId = pointerEvent.pointerId;
      const captureTarget = pointerEvent.currentTarget;

      try {
        captureTarget.setPointerCapture(pointerId);
      } catch {
        // Safari/iOS can reject capture on some WebView targets; window listeners still handle drag.
      }

      const updateCursor = (clientX: number, clientY: number) => {
        const nextDivisions = getRecordingCursorDivisionsFromPoint(clientX, clientY);
        if (nextDivisions == null) return;
        onRecordingCursorChange(nextDivisions);
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        updateCursor(moveEvent.clientX, moveEvent.clientY);
      };

      function cleanupCursorDrag() {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerCancel);
        try {
          captureTarget.releasePointerCapture(pointerId);
        } catch {
          // Capture can already be released after cancellation.
        }
      }

      function handlePointerUp(upEvent: PointerEvent) {
        if (upEvent.pointerId !== pointerId) return;
        cleanupCursorDrag();
      }

      function handlePointerCancel(cancelEvent: PointerEvent) {
        if (cancelEvent.pointerId !== pointerId) return;
        cleanupCursorDrag();
      }

      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerCancel);
    },
    [getRecordingCursorDivisionsFromPoint, onRecordingCursorChange],
  );

  const getPlacementFromClientPoint = useCallback(
    (clientX: number, clientY: number, durationDivisions: number) => {
      const placement = getPointerScorePlacement(clientX, clientY);
      if (!placement) return null;

      return {
        midi: placement.midi,
        startDivisions: snapStartDivisionsToDurationBucket(
          placement.rawStartDivisions,
          durationDivisions,
          measureDurationDivisions,
        ),
        durationDivisions,
        staff: placement.staff,
      };
    },
    [getPointerScorePlacement, measureDurationDivisions],
  );

  const getDropPlacement = useCallback(
    (event: DragEvent<HTMLDivElement>) =>
      getPlacementFromClientPoint(event.clientX, event.clientY, selectedDurationDivisions),
    [getPlacementFromClientPoint, selectedDurationDivisions],
  );

  const handleEditorBackgroundClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    if (getLiveDraftEventIdFromTarget(event.target)) return;
  }, []);

  const getManualDragDurationDivisions = useCallback(
    (dataTransfer: DataTransfer) => {
      const rawDuration = dataTransfer.getData(MANUAL_NOTE_DRAG_TYPE);
      const parsedDuration = Number(rawDuration);
      return Number.isFinite(parsedDuration) && parsedDuration > 0
        ? Math.max(MIN_EVENT_DURATION_DIVISIONS, Math.round(parsedDuration))
        : selectedDurationDivisions;
    },
    [selectedDurationDivisions],
  );

  const clearManualDragPreview = useCallback(() => {
    setManualDragPreviewDrop(null);
    setManualDragPreviewPosition(null);
  }, []);

  const beginManualNotePointerDrag = useCallback(
    (durationDivisions: number, pointerEvent: ReactPointerEvent<HTMLElement>) => {
      if (!scoreEditingEnabled || pointerEvent.button !== 0) return;

      pointerEvent.preventDefault();
      pointerEvent.stopPropagation();

      const pointerId = pointerEvent.pointerId;
      const captureTarget = pointerEvent.currentTarget;
      const previousBodyTouchAction = document.body.style.touchAction;
      const previousBodyUserSelect = document.body.style.userSelect;
      const previousBodyWebkitUserSelect =
        document.body.style.getPropertyValue('-webkit-user-select');
      const previousEditorTouchAction = editorRef.current?.style.touchAction ?? '';
      const previousEditorOverscrollBehavior = editorRef.current?.style.overscrollBehavior ?? '';
      const previousEditorScrollSnapType = editorRef.current?.style.scrollSnapType ?? '';

      activePaletteDragPointerIdRef.current = pointerId;

      try {
        captureTarget.setPointerCapture(pointerId);
      } catch {
        // iOS WebViews can reject capture on interactive elements; window listeners remain active.
      }

      document.body.style.touchAction = 'none';
      document.body.style.userSelect = 'none';
      document.body.style.setProperty('-webkit-user-select', 'none');
      if (editorRef.current) {
        editorRef.current.style.touchAction = 'none';
        editorRef.current.style.overscrollBehavior = 'contain';
        editorRef.current.style.scrollSnapType = 'none';
      }

      const dragState = {
        moved: false,
        latestDrop: null as ManualNoteDrop | null,
      };

      const getEditorBoundedDrop = (clientX: number, clientY: number) => {
        const editorRect = editorRef.current?.getBoundingClientRect();
        if (
          !editorRect ||
          clientX < editorRect.left ||
          clientX > editorRect.right ||
          clientY < editorRect.top ||
          clientY > editorRect.bottom
        ) {
          return null;
        }

        return getPlacementFromClientPoint(clientX, clientY, durationDivisions);
      };

      const updatePreview = (clientX: number, clientY: number) => {
        const drop = getEditorBoundedDrop(clientX, clientY);
        const editorPosition = getEditorPositionFromClientPoint(clientX, clientY);
        dragState.latestDrop = drop;

        if (!drop || !editorPosition) {
          clearManualDragPreview();
          return;
        }

        setManualDragPreviewDrop(drop);
        setManualDragPreviewPosition(editorPosition);
      };

      const cleanup = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerCancel);
        try {
          captureTarget.releasePointerCapture(pointerId);
        } catch {
          // Pointer capture may already be gone after cancellation.
        }
        if (activePaletteDragPointerIdRef.current === pointerId) {
          activePaletteDragPointerIdRef.current = null;
        }
        document.body.style.touchAction = previousBodyTouchAction;
        document.body.style.userSelect = previousBodyUserSelect;
        document.body.style.setProperty('-webkit-user-select', previousBodyWebkitUserSelect);
        if (editorRef.current) {
          editorRef.current.style.touchAction = previousEditorTouchAction;
          editorRef.current.style.overscrollBehavior = previousEditorOverscrollBehavior;
          editorRef.current.style.scrollSnapType = previousEditorScrollSnapType;
        }
        clearManualDragPreview();
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        if (
          Math.hypot(
            moveEvent.clientX - pointerEvent.clientX,
            moveEvent.clientY - pointerEvent.clientY,
          ) >= 3
        ) {
          dragState.moved = true;
        }

        updatePreview(moveEvent.clientX, moveEvent.clientY);
      };

      const handlePointerUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) return;
        upEvent.preventDefault();
        upEvent.stopPropagation();

        const finalDrop =
          dragState.latestDrop ?? getEditorBoundedDrop(upEvent.clientX, upEvent.clientY);
        cleanup();

        if (dragState.moved && finalDrop) {
          suppressNextClickRef.current = true;
          onManualNoteDrop(finalDrop);
        }
      };

      const handlePointerCancel = (cancelEvent: PointerEvent) => {
        if (cancelEvent.pointerId !== pointerId) return;
        cleanup();
      };

      updatePreview(pointerEvent.clientX, pointerEvent.clientY);
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerCancel);
    },
    [
      clearManualDragPreview,
      getEditorPositionFromClientPoint,
      getPlacementFromClientPoint,
      onManualNoteDrop,
      scoreEditingEnabled,
    ],
  );

  useEffect(() => {
    onManualNotePointerDragHandlerChange?.(beginManualNotePointerDrag);
    return () => {
      onManualNotePointerDragHandlerChange?.(null);
    };
  }, [beginManualNotePointerDrag, onManualNotePointerDragHandlerChange]);

  const beginExistingNoteMove = useCallback(
    (
      eventId: string,
      startClientX: number,
      startClientY: number,
      sourceMidi?: number | null,
      selectOptions?: EventSelectOptions,
      pointerOptions?: {
        pointerId?: number;
        captureTarget?: EventTarget | null;
      },
    ) => {
      const draftEvent = timelineEvents.find((event) => event.id === eventId);
      const startPlacement = getPointerScorePlacement(startClientX, startClientY);
      if (!draftEvent) return;
      if (!startPlacement) return;

      const pointerId = pointerOptions?.pointerId;
      const captureTarget =
        pointerOptions?.captureTarget instanceof Element ? pointerOptions.captureTarget : null;
      const editorElement = editorRef.current;
      const previousEditorTouchAction = editorElement?.style.touchAction ?? '';
      const previousEditorOverscrollBehavior = editorElement?.style.overscrollBehavior ?? '';
      const previousEditorScrollSnapType = editorElement?.style.scrollSnapType ?? '';

      if (pointerId != null) {
        activeNoteDragPointerIdRef.current = pointerId;
        try {
          captureTarget?.setPointerCapture?.(pointerId);
        } catch {
          // Some SVG descendants on Safari refuse capture; window listeners still own the fallback.
        }
      }

      if (editorElement) {
        editorElement.style.touchAction = 'none';
        editorElement.style.overscrollBehavior = 'contain';
        editorElement.style.scrollSnapType = 'none';
      }

      const normalizedEventNotes = normalizeMidiNotes(draftEvent.midiNotes);
      const moveSourceMidi =
        sourceMidi != null && normalizedEventNotes.includes(Math.round(sourceMidi))
          ? Math.round(sourceMidi)
          : null;
      const currentAverageMidi =
        draftEvent.midiNotes.reduce((sum, note) => sum + note, 0) /
        Math.max(draftEvent.midiNotes.length, 1);
      const movingPitchAnchorMidi =
        moveSourceMidi ??
        (Number.isFinite(currentAverageMidi)
          ? Math.round(currentAverageMidi)
          : startPlacement.midi);
      const startPointerPosition = getEditorPositionFromClientPoint(startClientX, startClientY);
      const measuredPosition =
        moveSourceMidi == null
          ? noteHandlePositions[eventId]
          : (noteHandlePositionsByMidi[getEventMidiPositionKey(eventId, moveSourceMidi)] ??
            noteHandlePositions[eventId]);
      const visualGrabOffset =
        startPointerPosition && measuredPosition
          ? {
              leftPx: startPointerPosition.leftPx - measuredPosition.leftPx,
              topPx: startPointerPosition.topPx - measuredPosition.topPx,
            }
          : { leftPx: 0, topPx: 0 };
      const startSlotCenterDivisions = getStartDivisionsSlotCenter(
        draftEvent.startDivisions,
        draftEvent.durationDivisions,
        measureDurationDivisions,
      );
      const pitchGrabOffset = startPlacement.midi - movingPitchAnchorMidi;

      const getPointerDrop = (clientX: number, clientY: number): ExistingNoteDrop | null => {
        const placement = getPointerScorePlacement(clientX, clientY);
        if (!placement) return null;

        const draggedSlotCenterDivisions =
          startSlotCenterDivisions + placement.rawStartDivisions - startPlacement.rawStartDivisions;

        return {
          midi: Math.max(
            EDITOR_BOTTOM_MIDI,
            Math.min(EDITOR_TOP_MIDI, Math.round(placement.midi - pitchGrabOffset)),
          ),
          startDivisions: snapCenterDivisionsToDurationBucket(
            draggedSlotCenterDivisions,
            draftEvent.durationDivisions,
            measureDurationDivisions,
          ),
          durationDivisions: draftEvent.durationDivisions,
          sourceMidi: moveSourceMidi ?? undefined,
        };
      };

      const dragState = {
        moved: false,
        latestDrop: getPointerDrop(startClientX, startClientY),
      };

      const updateDragPreview = (
        drop: ExistingNoteDrop | null,
        clientX: number,
        clientY: number,
      ) => {
        if (!drop) return;
        const pointerPosition = getEditorPositionFromClientPoint(clientX, clientY);
        if (!pointerPosition) return;
        setDragPreviewPosition({
          eventId,
          leftPx: pointerPosition.leftPx - visualGrabOffset.leftPx,
          topPx: pointerPosition.topPx - visualGrabOffset.topPx,
        });
        setDragPreviewDrop({
          eventId,
          ...drop,
        });
      };

      setMovingEventId(eventId);
      setMovingNoteMidi(moveSourceMidi);
      updateDragPreview(dragState.latestDrop, startClientX, startClientY);

      const cleanup = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerCancel);
        if (pointerId != null) {
          try {
            captureTarget?.releasePointerCapture?.(pointerId);
          } catch {
            // The pointer can already be released by Safari after cancellation.
          }
          if (activeNoteDragPointerIdRef.current === pointerId) {
            activeNoteDragPointerIdRef.current = null;
          }
        }
        if (editorElement) {
          editorElement.style.touchAction = previousEditorTouchAction;
          editorElement.style.overscrollBehavior = previousEditorOverscrollBehavior;
          editorElement.style.scrollSnapType = previousEditorScrollSnapType;
        }
        setMovingEventId(null);
        setMovingNoteMidi(null);
        setDragPreviewPosition(null);
        setDragPreviewDrop(null);
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (pointerId != null && moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        const distanceMoved = Math.hypot(
          moveEvent.clientX - startClientX,
          moveEvent.clientY - startClientY,
        );
        if (distanceMoved >= 3) {
          dragState.moved = true;
        }

        dragState.latestDrop = getPointerDrop(moveEvent.clientX, moveEvent.clientY);
        updateDragPreview(dragState.latestDrop, moveEvent.clientX, moveEvent.clientY);
      };

      const handlePointerUp = (upEvent: PointerEvent) => {
        if (pointerId != null && upEvent.pointerId !== pointerId) return;
        upEvent.preventDefault();
        upEvent.stopPropagation();
        cleanup();

        suppressNextClickRef.current = true;
        if (!dragState.moved || !dragState.latestDrop) {
          onEventSelect(eventId, moveSourceMidi, selectOptions);
          return;
        }

        onExistingNoteDrop(eventId, dragState.latestDrop);
      };

      const handlePointerCancel = (cancelEvent: PointerEvent) => {
        if (pointerId != null && cancelEvent.pointerId !== pointerId) return;
        cleanup();
      };

      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerCancel);
    },
    [
      getEditorPositionFromClientPoint,
      getPointerScorePlacement,
      measureDurationDivisions,
      noteHandlePositions,
      noteHandlePositionsByMidi,
      onExistingNoteDrop,
      onEventSelect,
      timelineEvents,
    ],
  );

  const handleExistingNotePointerDown = useCallback(
    (
      eventId: string,
      pointerEvent: ReactPointerEvent<HTMLDivElement>,
      sourceMidi?: number | null,
    ) => {
      if (!scoreEditingEnabled || pointerEvent.button !== 0) return;
      pointerEvent.preventDefault();
      pointerEvent.stopPropagation();
      const noteTarget = getNearestRenderedNoteTarget(
        pointerEvent.clientX,
        pointerEvent.clientY,
        eventId,
        sourceMidi,
      );
      beginExistingNoteMove(
        noteTarget?.eventId ?? eventId,
        pointerEvent.clientX,
        pointerEvent.clientY,
        noteTarget?.midi ?? sourceMidi ?? null,
        {
          toggleSelection: pointerEvent.shiftKey || pointerEvent.metaKey || pointerEvent.ctrlKey,
        },
        {
          pointerId: pointerEvent.pointerId,
          captureTarget: pointerEvent.currentTarget,
        },
      );
    },
    [beginExistingNoteMove, getNearestRenderedNoteTarget, scoreEditingEnabled],
  );

  useEffect(() => {
    const sheetElement = sheetRef.current;
    if (!sheetElement || !isScoreReady || !scoreEditingEnabled) return;

    const handleNoteClick = (event: MouseEvent) => {
      const eventId = getLiveDraftEventIdFromTarget(event.target);
      if (!eventId) return;

      event.preventDefault();
      event.stopPropagation();
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }

      const noteTarget = getNearestRenderedNoteTarget(
        event.clientX,
        event.clientY,
        eventId,
        getLiveDraftMidiFromTarget(event.target),
      );
      if (!noteTarget) return;
      onEventSelect(noteTarget.eventId, noteTarget.midi, {
        toggleSelection: event.shiftKey || event.metaKey || event.ctrlKey,
      });
    };

    const handleNotePointerDown = (event: PointerEvent) => {
      const eventId = getLiveDraftEventIdFromTarget(event.target);
      if (!eventId || event.button !== 0) return;
      const noteTarget = getNearestRenderedNoteTarget(
        event.clientX,
        event.clientY,
        eventId,
        getLiveDraftMidiFromTarget(event.target),
      );
      if (!noteTarget) return;

      event.preventDefault();
      event.stopPropagation();
      beginExistingNoteMove(
        noteTarget.eventId,
        event.clientX,
        event.clientY,
        noteTarget.midi,
        {
          toggleSelection: event.shiftKey || event.metaKey || event.ctrlKey,
        },
        {
          pointerId: event.pointerId,
          captureTarget: event.target,
        },
      );
    };

    const handleNoteMouseOver = (event: MouseEvent) => {
      const eventId = getLiveDraftEventIdFromTarget(event.target);
      if (eventId) {
        setHoveredEventId(eventId);
      }
    };

    const handleNoteMouseOut = (event: MouseEvent) => {
      const eventId = getLiveDraftEventIdFromTarget(event.target);
      if (!eventId) return;
      if (getLiveDraftEventIdFromTarget(event.relatedTarget) === eventId) {
        return;
      }
      setHoveredEventId((currentEventId) => (currentEventId === eventId ? null : currentEventId));
    };

    sheetElement.addEventListener('click', handleNoteClick, true);
    sheetElement.addEventListener('pointerdown', handleNotePointerDown, true);
    sheetElement.addEventListener('mouseover', handleNoteMouseOver, true);
    sheetElement.addEventListener('mouseout', handleNoteMouseOut, true);

    return () => {
      sheetElement.removeEventListener('click', handleNoteClick, true);
      sheetElement.removeEventListener('pointerdown', handleNotePointerDown, true);
      sheetElement.removeEventListener('mouseover', handleNoteMouseOver, true);
      sheetElement.removeEventListener('mouseout', handleNoteMouseOut, true);
    };
  }, [
    beginExistingNoteMove,
    getNearestRenderedNoteTarget,
    isScoreReady,
    onEventSelect,
    scoreEditingEnabled,
  ]);

  const handleEditorDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!scoreEditingEnabled) return;

      const hasManualNote = event.dataTransfer.types.includes(MANUAL_NOTE_DRAG_TYPE);
      const hasExistingNote = event.dataTransfer.types.includes(EXISTING_NOTE_DRAG_TYPE);
      if (!hasManualNote && !hasExistingNote) return;

      event.preventDefault();

      if (hasExistingNote) {
        event.dataTransfer.dropEffect = 'move';
        clearManualDragPreview();
        return;
      }

      const durationDivisions = getManualDragDurationDivisions(event.dataTransfer);
      const drop = getPlacementFromClientPoint(event.clientX, event.clientY, durationDivisions);
      const pointerPosition = getEditorPositionFromClientPoint(event.clientX, event.clientY);
      event.dataTransfer.dropEffect = 'copy';

      if (!drop || !pointerPosition) {
        clearManualDragPreview();
        return;
      }

      setManualDragPreviewDrop(drop);
      setManualDragPreviewPosition(pointerPosition);
    },
    [
      clearManualDragPreview,
      getEditorPositionFromClientPoint,
      getManualDragDurationDivisions,
      getPlacementFromClientPoint,
      scoreEditingEnabled,
    ],
  );

  const handleEditorDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) return;
      clearManualDragPreview();
    },
    [clearManualDragPreview],
  );

  const handleEditorDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!scoreEditingEnabled) return;

      const hasManualNote = event.dataTransfer.types.includes(MANUAL_NOTE_DRAG_TYPE);
      const existingEventId = event.dataTransfer.getData(EXISTING_NOTE_DRAG_TYPE);
      if (!hasManualNote && !existingEventId) return;

      event.preventDefault();
      clearManualDragPreview();

      const drop = existingEventId
        ? getDropPlacement(event)
        : getPlacementFromClientPoint(
            event.clientX,
            event.clientY,
            getManualDragDurationDivisions(event.dataTransfer),
          );
      if (!drop) return;

      if (existingEventId) {
        onExistingNoteDrop(existingEventId, drop);
        return;
      }

      onManualNoteDrop(drop);
    },
    [
      clearManualDragPreview,
      getDropPlacement,
      getManualDragDurationDivisions,
      getPlacementFromClientPoint,
      onExistingNoteDrop,
      onManualNoteDrop,
      scoreEditingEnabled,
    ],
  );

  const recordingCursorPosition = getCursorPositionFromDivisions(recordingCursorDivisions);

  return (
    <Box
      ref={sheetCardRef}
      sx={{
        borderRadius: '24px',
        background: practiceDarkMode
          ? '#050B13'
          : scoreTheme === 'dimPaper'
            ? scorePalette.scoreBackground
            : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,249,253,1) 100%)',
        boxShadow: '0 24px 52px rgba(0, 0, 0, 0.18)',
        minHeight: '720px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        border: practiceDarkMode
          ? '1px solid rgba(243, 247, 255, 0.12)'
          : scoreTheme === 'dimPaper'
            ? '1px solid rgba(16, 16, 13, 0.18)'
            : '1px solid rgba(10, 15, 26, 0.08)',
        overflow: 'hidden',
        [TABLET_WORKBENCH_MEDIA]: {
          minHeight: 'max(640px, calc(100vh - 148px))',
        },
      }}
    >
      <Box
        sx={{
          borderBottom: practiceDarkMode
            ? '1px solid rgba(243, 247, 255, 0.12)'
            : '1px solid rgba(9, 19, 36, 0.08)',
          padding: '18px 24px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          alignItems: 'start',
          gap: '14px',
          [TABLET_WORKBENCH_MEDIA]: {
            padding: '16px 18px',
          },
          [WIDE_WORKBENCH_MEDIA]: {
            gridTemplateColumns: 'minmax(250px, 1fr) auto',
            alignItems: 'center',
            padding: '18px 24px',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              color: practiceDarkMode ? '#F3F7FF' : '#111827',
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '0.01em',
              [TABLET_WORKBENCH_MEDIA]: {
                fontSize: '19px',
              },
              [WIDE_WORKBENCH_MEDIA]: {
                fontSize: '20px',
              },
            }}
          >
            {draftTitle}
          </Box>
          <Box
            sx={{
              color: practiceDarkMode ? 'rgba(243, 247, 255, 0.68)' : 'rgba(17, 24, 39, 0.64)',
              fontSize: '12px',
              marginTop: '4px',
              lineHeight: 1.45,
            }}
          >
            {timeSignatureLabel} · sixteenth-note grid · piano draft
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            rowGap: '10px',
            flexWrap: 'wrap',
            minWidth: 0,
            width: '100%',
            [WIDE_WORKBENCH_MEDIA]: {
              width: 'auto',
              justifyContent: 'flex-end',
            },
          }}
        >
          <StatPill label="Measures" value={`${measureCount}`} darkMode={practiceDarkMode} />
          <PlaybackTempoControl
            tempo={playbackTempo}
            defaultTempo={DEFAULT_PLAYBACK_TEMPO_BPM}
            minTempo={MIN_PLAYBACK_TEMPO_BPM}
            maxTempo={MAX_PLAYBACK_TEMPO_BPM}
            onTempoChange={onTempoChange}
          />
          <Box sx={metronomeSwitchRowStyles(practiceDarkMode)}>
            <Box sx={metronomeSwitchLabelStyles(practiceDarkMode)}>Metronome</Box>
            <Box sx={metronomeSwitchControlRowStyles}>
              <CustomSwitch
                checked={metronomeEnabled}
                onChange={() => onMetronomeToggle()}
                inputProps={{ 'aria-label': 'Metronome' }}
                sx={metronomeSwitchControlStyles}
              />
              <Box sx={metronomeSwitchValueStyles(practiceDarkMode)}>
                {metronomeEnabled ? 'On' : 'Off'}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {notationToolbar}

      {renderError && (
        <Box
          sx={{
            padding: '12px 24px',
            color: '#8B1E1E',
            backgroundColor: 'rgba(189, 57, 57, 0.08)',
            borderBottom: '1px solid rgba(189, 57, 57, 0.12)',
            fontSize: '13px',
          }}
        >
          {renderError}
        </Box>
      )}

      {audioDiagnosticsVisible && (
        <AudioDiagnosticsPanel
          snapshot={audioDiagnosticsSnapshot}
          frames={audioDiagnosticsFrames}
          darkMode={practiceDarkMode}
        />
      )}

      <Box
        sx={{
          padding: { xs: '18px 14px 160px', md: '26px 28px 172px' },
          overflowX: 'auto',
          minHeight: '560px',
          flex: '1 1 auto',
          position: 'relative',
          touchAction: movingEventId ? 'none' : 'auto',
          overscrollBehavior: movingEventId ? 'contain' : 'auto',
          backgroundColor: scoreTheme !== 'light' ? scorePalette.scoreBackground : undefined,
          '& svg': {
            maxWidth: '100%',
            height: 'auto',
            backgroundColor: scoreTheme !== 'light' ? scorePalette.scoreBackground : undefined,
          },
          ...(practiceDarkMode
            ? {
                [paperRectSelector]: {
                  fill: `${scorePalette.scoreBackground} !important`,
                  stroke: `${scorePalette.scoreBackground} !important`,
                },
                '& svg path, & svg ellipse, & svg polygon, & svg circle, & svg use, & svg text, & svg tspan, & svg [class*="rest"], & svg [id*="rest"]':
                  {
                    color: `${scorePalette.notationColor} !important`,
                    fill: `${scorePalette.notationColor} !important`,
                    stroke: `${scorePalette.notationColor} !important`,
                  },
                [nonPaperRectSelector]: {
                  color: `${scorePalette.notationColor} !important`,
                  fill: `${scorePalette.notationColor} !important`,
                  stroke: `${scorePalette.notationColor} !important`,
                },
                '& svg line, & svg polyline': {
                  color: `${scorePalette.staffLineColor} !important`,
                  fill: `${scorePalette.staffLineColor} !important`,
                  stroke: `${scorePalette.staffLineColor} !important`,
                },
              }
            : scoreTheme === 'dimPaper'
              ? {
                  [paperRectSelector]: {
                    fill: `${scorePalette.scoreBackground} !important`,
                    stroke: `${scorePalette.scoreBackground} !important`,
                  },
                  '& svg path, & svg ellipse, & svg polygon, & svg circle, & svg use, & svg [class*="rest"], & svg [id*="rest"]':
                    {
                      color: `${scorePalette.notationColor} !important`,
                      fill: `${scorePalette.notationColor} !important`,
                      stroke: `${scorePalette.notationColor} !important`,
                    },
                  [nonPaperRectSelector]: {
                    color: `${scorePalette.notationColor} !important`,
                    fill: `${scorePalette.notationColor} !important`,
                    stroke: `${scorePalette.notationColor} !important`,
                  },
                  '& svg line, & svg polyline': {
                    color: `${scorePalette.staffLineColor} !important`,
                    fill: `${scorePalette.staffLineColor} !important`,
                    stroke: `${scorePalette.staffLineColor} !important`,
                  },
                  '& svg text': {
                    color: `${scorePalette.musicMarkingColor} !important`,
                    fill: `${scorePalette.musicMarkingColor} !important`,
                    stroke: `${scorePalette.musicMarkingColor} !important`,
                  },
                }
              : {}),
          '& [data-live-draft-event-id]': {
            cursor: scoreEditingEnabled ? 'grab' : 'default',
            pointerEvents: scoreEditingEnabled ? 'auto' : 'none',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitUserDrag: 'none',
          },
          '& [data-live-draft-event-id] *': {
            cursor: scoreEditingEnabled ? 'grab' : 'default',
            pointerEvents: scoreEditingEnabled ? 'auto' : 'none',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitUserDrag: 'none',
          },
          '& path[data-live-draft-note-state="hover"], & ellipse[data-live-draft-note-state="hover"], & [data-live-draft-note-state="hover"] path, & [data-live-draft-note-state="hover"] ellipse':
            {
              fill: '#A7F3D0 !important',
              stroke: '#059669 !important',
            },
          '& path[data-live-draft-note-state="selected"], & ellipse[data-live-draft-note-state="selected"], & [data-live-draft-note-state="selected"] path, & [data-live-draft-note-state="selected"] ellipse':
            {
              fill: '#34D399 !important',
              stroke: '#047857 !important',
            },
          '& path[data-live-draft-note-state="moving"], & ellipse[data-live-draft-note-state="moving"], & [data-live-draft-note-state="moving"] path, & [data-live-draft-note-state="moving"] ellipse':
            {
              fill: '#9CA3AF !important',
              stroke: '#4B5563 !important',
              filter: 'drop-shadow(0 0 4px rgba(75, 85, 99, 0.34))',
              opacity: 0.42,
            },
        }}
        ref={editorRef}
        onClick={handleEditorBackgroundClick}
        onDragOver={handleEditorDragOver}
        onDragLeave={handleEditorDragLeave}
        onDrop={handleEditorDrop}
      >
        <Box
          ref={sheetRef}
          sx={{
            minWidth: '720px',
            color: practiceDarkMode ? '#F3F7FF' : '#111827',
            position: 'relative',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: 'max(100%, 720px)',
            height: '100%',
            minWidth: '720px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <Box
            ref={playbackCursorRef}
            aria-hidden="true"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: 0,
              width: '16px',
              transform: 'translate3d(-9999px, 0, 0) translateX(-50%)',
              borderRadius: '999px',
              backgroundColor: scorePalette.playbackCursor,
              opacity: 0,
              zIndex: 3,
              pointerEvents: 'none',
              willChange: 'transform, height, opacity',
              transition: 'opacity 120ms ease-out',
            }}
          />
          {recordingCursorPosition && (
            <Box
              sx={{
                position: 'absolute',
                left: `${recordingCursorPosition.leftPx}px`,
                top: `${recordingCursorPosition.topPx}px`,
                height: `${recordingCursorPosition.heightPx}px`,
                width: '22px',
                transform: 'translateX(-50%)',
                zIndex: 4,
                pointerEvents: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '10px',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  borderRadius: '999px',
                  backgroundColor: 'rgba(111, 135, 241, 0.9)',
                  boxShadow: '0 0 0 3px rgba(111, 135, 241, 0.16)',
                }}
              />
              <Box
                role="slider"
                aria-label="Recording cursor"
                aria-valuemin={0}
                aria-valuemax={totalEditorDivisions}
                aria-valuenow={recordingCursorDivisions}
                tabIndex={0}
                onPointerDown={handleRecordingCursorPointerDown}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    onRecordingCursorChange(
                      Math.max(0, recordingCursorDivisions - selectedDurationDivisions),
                    );
                  }
                  if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    onRecordingCursorChange(recordingCursorDivisions + selectedDurationDivisions);
                  }
                }}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '-8px',
                  width: '30px',
                  height: '30px',
                  borderRadius: '999px',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'grab',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  outline: 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    top: '6px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '999px',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#6F87F1',
                    border: '2px solid #FFFFFF',
                    boxSizing: 'border-box',
                    boxShadow: '0 8px 18px rgba(22, 30, 54, 0.22)',
                    transition:
                      'background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                  },
                  '&:hover::before, &:focus-visible::before': {
                    backgroundColor: '#5E78F0',
                    transform: 'translateX(-50%) scale(1.08)',
                    boxShadow:
                      '0 0 0 5px rgba(111, 135, 241, 0.24), 0 8px 18px rgba(22, 30, 54, 0.22)',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                  '&:active::before': {
                    transform: 'translateX(-50%) scale(1.02)',
                  },
                }}
              />
            </Box>
          )}
          {timelineEvents.map((event) => {
            const measuredPosition = noteHandlePositions[event.id];
            const renderedMidiTargets = normalizeMidiNotes(event.midiNotes)
              .map((midi) => ({
                midi,
                position: noteHandlePositionsByMidi[getEventMidiPositionKey(event.id, midi)],
              }))
              .filter(
                (target): target is { midi: number; position: NoteHandlePosition } =>
                  target.position != null,
              );
            const activeDragPosition =
              dragPreviewPosition?.eventId === event.id ? dragPreviewPosition : null;
            const renderedPosition = activeDragPosition ?? measuredPosition;
            const left = Math.min(
              98,
              Math.max(2, (event.startDivisions / Math.max(totalEditorDivisions, 1)) * 100),
            );
            const top = Math.min(90, Math.max(10, getEditorYPercentFromMidi(event.midiNotes)));
            const interactionState = getInteractionState(event.id);
            const hasRenderedMidiTargets = renderedMidiTargets.length > 0;
            const hasRenderedNoteheadTarget = Boolean(measuredPosition) && hasRenderedMidiTargets;
            const isRestEvent = isDraftRestEvent(event);
            const stateVisible =
              Boolean(renderedPosition) &&
              !hasRenderedMidiTargets &&
              (isRestEvent || interactionState !== 'idle');
            const stateFill =
              interactionState === 'moving'
                ? 'rgba(107, 114, 128, 0.18)'
                : interactionState === 'selected'
                  ? 'rgba(52, 211, 153, 0.18)'
                  : isRestEvent && interactionState === 'idle'
                    ? 'rgba(111, 135, 241, 0.08)'
                    : 'rgba(167, 243, 208, 0.16)';
            const stateBorder =
              interactionState === 'moving'
                ? 'rgba(55, 65, 81, 0.72)'
                : interactionState === 'selected'
                  ? 'rgba(4, 120, 87, 0.86)'
                  : isRestEvent && interactionState === 'idle'
                    ? 'rgba(111, 135, 241, 0.44)'
                    : 'rgba(5, 150, 105, 0.64)';

            return (
              <Box key={event.id} component="span">
                <Box
                  role="button"
                  tabIndex={scoreEditingEnabled ? 0 : -1}
                  aria-label={`Select ${formatChordLabel(event.midiNotes)}`}
                  aria-disabled={!scoreEditingEnabled}
                  onClick={(clickEvent) => {
                    if (!scoreEditingEnabled) return;
                    clickEvent.stopPropagation();
                    if (suppressNextClickRef.current) {
                      suppressNextClickRef.current = false;
                      return;
                    }
                    onEventSelect(event.id, null, {
                      toggleSelection:
                        clickEvent.shiftKey || clickEvent.metaKey || clickEvent.ctrlKey,
                    });
                  }}
                  onKeyDown={(keyEvent) => {
                    if (!scoreEditingEnabled) return;
                    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                      keyEvent.preventDefault();
                      onEventSelect(event.id, null);
                    }
                  }}
                  onMouseEnter={() => setHoveredEventId(event.id)}
                  onMouseLeave={() =>
                    setHoveredEventId((currentEventId) =>
                      currentEventId === event.id ? null : currentEventId,
                    )
                  }
                  onPointerDown={(pointerEvent) =>
                    handleExistingNotePointerDown(event.id, pointerEvent)
                  }
                  sx={{
                    position: 'absolute',
                    left: renderedPosition ? `${renderedPosition.leftPx}px` : `${left}%`,
                    top: renderedPosition ? `${renderedPosition.topPx}px` : `${top}%`,
                    width: '36px',
                    height: '36px',
                    borderRadius: '999px',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: stateVisible ? stateFill : 'transparent',
                    border: stateVisible ? `2px solid ${stateBorder}` : '1px solid transparent',
                    boxShadow:
                      interactionState === 'moving' && renderedPosition
                        ? '0 0 0 5px rgba(107, 114, 128, 0.14), 0 8px 18px rgba(0,0,0,0.14)'
                        : stateVisible
                          ? '0 0 0 4px rgba(52, 211, 153, 0.15)'
                          : 'none',
                    cursor: scoreEditingEnabled
                      ? interactionState === 'moving'
                        ? 'grabbing'
                        : 'grab'
                      : 'default',
                    pointerEvents:
                      hasRenderedNoteheadTarget || !scoreEditingEnabled ? 'none' : 'auto',
                    touchAction: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitUserDrag: 'none',
                    transition:
                      'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                    '&:hover':
                      scoreEditingEnabled && renderedPosition
                        ? {
                            backgroundColor: 'rgba(167, 243, 208, 0.16)',
                            borderColor: 'rgba(5, 150, 105, 0.64)',
                            boxShadow: '0 0 0 4px rgba(52, 211, 153, 0.12)',
                          }
                        : undefined,
                    '&:active': {
                      cursor: 'grabbing',
                    },
                  }}
                />
                {renderedMidiTargets.map(({ midi, position }) => {
                  const noteInteractionState = getInteractionState(event.id, midi);
                  const noteStateFill =
                    noteInteractionState === 'moving'
                      ? 'rgba(107, 114, 128, 0.18)'
                      : noteInteractionState === 'selected'
                        ? 'rgba(52, 211, 153, 0.18)'
                        : 'rgba(167, 243, 208, 0.16)';
                  const noteStateBorder =
                    noteInteractionState === 'moving'
                      ? 'rgba(55, 65, 81, 0.72)'
                      : noteInteractionState === 'selected'
                        ? 'rgba(4, 120, 87, 0.86)'
                        : 'rgba(5, 150, 105, 0.64)';
                  const noteStateVisible = noteInteractionState !== 'idle';

                  return (
                    <Box
                      key={`${event.id}-${midi}`}
                      data-live-draft-event-id={event.id}
                      data-live-draft-midi={midi}
                      role="button"
                      tabIndex={-1}
                      aria-label={`Select ${midiToName(midi)}`}
                      aria-disabled={!scoreEditingEnabled}
                      onClick={(clickEvent) => {
                        if (!scoreEditingEnabled) return;
                        clickEvent.stopPropagation();
                        if (suppressNextClickRef.current) {
                          suppressNextClickRef.current = false;
                          return;
                        }
                        const noteTarget = getNearestRenderedNoteTarget(
                          clickEvent.clientX,
                          clickEvent.clientY,
                          event.id,
                          midi,
                        );
                        onEventSelect(noteTarget?.eventId ?? event.id, noteTarget?.midi ?? midi, {
                          toggleSelection:
                            clickEvent.shiftKey || clickEvent.metaKey || clickEvent.ctrlKey,
                        });
                      }}
                      onKeyDown={(keyEvent) => {
                        if (!scoreEditingEnabled) return;
                        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                          keyEvent.preventDefault();
                          onEventSelect(event.id, midi);
                        }
                      }}
                      onMouseEnter={() => setHoveredEventId(event.id)}
                      onMouseLeave={() =>
                        setHoveredEventId((currentEventId) =>
                          currentEventId === event.id ? null : currentEventId,
                        )
                      }
                      onPointerDown={(pointerEvent) =>
                        handleExistingNotePointerDown(event.id, pointerEvent, midi)
                      }
                      sx={{
                        position: 'absolute',
                        left: `${position.leftPx}px`,
                        top: `${position.topPx}px`,
                        width: '28px',
                        height: '28px',
                        borderRadius: '999px',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: noteStateVisible ? noteStateFill : 'transparent',
                        border: noteStateVisible
                          ? `2px solid ${noteStateBorder}`
                          : '1px solid transparent',
                        boxShadow:
                          noteInteractionState === 'moving'
                            ? '0 0 0 5px rgba(107, 114, 128, 0.14), 0 8px 18px rgba(0,0,0,0.14)'
                            : noteStateVisible
                              ? '0 0 0 4px rgba(52, 211, 153, 0.15)'
                              : 'none',
                        cursor: scoreEditingEnabled
                          ? noteInteractionState === 'moving'
                            ? 'grabbing'
                            : 'grab'
                          : 'default',
                        pointerEvents: scoreEditingEnabled ? 'auto' : 'none',
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitUserDrag: 'none',
                        transition:
                          'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                        zIndex: 3,
                        '&:hover': scoreEditingEnabled
                          ? {
                              backgroundColor: 'rgba(167, 243, 208, 0.16)',
                              borderColor: 'rgba(5, 150, 105, 0.64)',
                              boxShadow: '0 0 0 4px rgba(52, 211, 153, 0.12)',
                            }
                          : undefined,
                        '&:active': {
                          cursor: 'grabbing',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            );
          })}
          {manualDragPreviewDrop && manualDragPreviewPosition && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: `${manualDragPreviewPosition.leftPx}px`,
                top: `${manualDragPreviewPosition.topPx}px`,
                width: 0,
                height: 0,
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <ScoreDragNoteGlyph
                durationDivisions={manualDragPreviewDrop.durationDivisions}
                {...getScoreDragGlyphColors(scoreTheme)}
              />
            </Box>
          )}
          {dragPreviewDrop &&
            (() => {
              const previewEvent = timelineEvents.find(
                (event) => event.id === dragPreviewDrop.eventId,
              );
              const previewPosition =
                dragPreviewPosition?.eventId === dragPreviewDrop.eventId
                  ? dragPreviewPosition
                  : null;
              if (!previewEvent || !previewPosition) return null;

              const currentAverageMidi =
                previewEvent.midiNotes.reduce((sum, note) => sum + note, 0) /
                Math.max(previewEvent.midiNotes.length, 1);
              const pitchDelta = dragPreviewDrop.midi - Math.round(currentAverageMidi);
              const isSplittingChord =
                dragPreviewDrop.sourceMidi != null && previewEvent.midiNotes.length > 1;
              const previewMidiNotes = isSplittingChord
                ? [dragPreviewDrop.midi]
                : normalizeMidiNotes(previewEvent.midiNotes.map((note) => note + pitchDelta));
              const pixelsPerMidi = getScorePixelsPerMidi();

              return previewMidiNotes.map((midi, index) => {
                const leftPx = previewPosition.leftPx;
                const topPx = previewPosition.topPx - (midi - dragPreviewDrop.midi) * pixelsPerMidi;

                return (
                  <Box
                    key={`${dragPreviewDrop.eventId}-${midi}-${index}`}
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      left: `${leftPx}px`,
                      top: `${topPx}px`,
                      width: 0,
                      height: 0,
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  >
                    <ScoreDragNoteGlyph
                      durationDivisions={dragPreviewDrop.durationDivisions}
                      {...getScoreDragGlyphColors(scoreTheme)}
                    />
                  </Box>
                );
              });
            })()}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          right: `${transportInset.rightPx}px`,
          bottom: `calc(${transportInset.bottomPx}px + env(safe-area-inset-bottom))`,
          zIndex: 20,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          width: 'fit-content',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '18px',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            onClick={canPlay ? onPlaybackRestart : undefined}
            aria-label="Replay draft from the beginning"
            sx={{
              ...transportRestartButtonChrome,
              ...(scoreTheme === 'dimPaper' ? dimPaperRestartButtonChrome : {}),
              ...restartButtonOpaqueStyles(scoreTheme),
              opacity: canPlay ? 1 : 0.42,
              pointerEvents: canPlay ? 'auto' : 'none',
            }}
          >
            {scoreTheme === 'dimPaper' ? (
              <ReplayIcon sx={dimPaperRestartIconChrome} />
            ) : (
              <img style={{ maxWidth: '100%' }} src={RestartIcon} />
            )}
          </Box>
          <Box
            onClick={canPlay ? onPlaybackToggle : undefined}
            aria-label={isPlayingBack ? 'Pause draft playback' : 'Play draft playback'}
            sx={{
              ...(isPlayingBack ? pauseButtonStyles : playbackButtonStyles),
              opacity: canPlay ? 1 : 0.42,
              pointerEvents: canPlay ? 'auto' : 'none',
            }}
          >
            {isPlayingBack ? (
              <Box component="span" aria-hidden="true" sx={pauseIconStyles}>
                <Box component="span" sx={pauseBarStyles} />
                <Box component="span" sx={pauseBarStyles} />
              </Box>
            ) : (
              <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={PlayIcon} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const playbackButtonStyles = {
  ...transportPlayButtonChrome,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  overflow: 'visible',
};

const restartButtonOpaqueStyles = (scoreTheme: ScoreTheme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor:
    scoreTheme === 'dimPaper' ? 'rgba(184, 175, 153, 0.92)' : 'rgba(236, 238, 242, 0.94)',
  border:
    scoreTheme === 'dimPaper'
      ? '1px solid rgba(16, 16, 13, 0.18)'
      : '1px solid rgba(17, 24, 39, 0.08)',
  boxShadow: '0px 8px 18px rgba(17, 24, 39, 0.18)',
  '&:hover': {
    cursor: 'pointer',
    transition: '0.3s',
    backgroundColor:
      scoreTheme === 'dimPaper' ? 'rgba(184, 175, 153, 0.98)' : 'rgba(246, 247, 249, 0.98)',
    boxShadow: '0px 0px 5px 5px rgba(196, 198, 202, 0.5)',
  },
});

const pauseButtonStyles = {
  ...playbackButtonStyles,
  backgroundColor: '#CA7C62',
  boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.28)',
};

const pauseIconStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  height: '54px',
};

const pauseBarStyles = {
  display: 'block',
  width: '15px',
  flex: '0 0 15px',
  height: '54px',
  borderRadius: '8px',
  backgroundColor: '#E7C4B9',
};

const metronomeSwitchRowStyles = (darkMode: boolean) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  backgroundColor: darkMode ? 'rgba(243, 247, 255, 0.08)' : 'rgba(9, 19, 36, 0.06)',
  color: darkMode ? '#F3F7FF' : '#10203A',
  padding: '7px 14px',
  minWidth: '148px',
  [TABLET_WORKBENCH_MEDIA]: {
    padding: '6px 12px',
    minWidth: '136px',
  },
  [WIDE_WORKBENCH_MEDIA]: {
    padding: '7px 14px',
    minWidth: '148px',
  },
});

const metronomeSwitchLabelStyles = (darkMode: boolean) => ({
  color: darkMode ? 'rgba(243, 247, 255, 0.56)' : 'rgba(16, 32, 58, 0.52)',
  fontFamily: 'Lato',
  fontSize: '10px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

const metronomeSwitchControlRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  marginTop: '-2px',
};

const metronomeSwitchControlStyles = {
  transform: 'scale(1.1)',
  transformOrigin: 'center',
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#CA7C62',
  },
  '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
    backgroundColor: '#CA7C62',
  },
};

const metronomeSwitchValueStyles = (darkMode: boolean) => ({
  color: darkMode ? '#F3F7FF' : '#10203A',
  fontFamily: 'Lato',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1,
});

const Create = () => {
  const scoreTheme = usePracticeScoreTheme();
  const scorePalette = getScoreThemePalette(scoreTheme);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAuthToken } = useAuthWrapperContext();
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const userRepertoireData = repertoireReducerData.userRepertoireData ?? [];
  const repertoireData = repertoireReducerData.repertoireData ?? [];
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const {
    addListener: addMidiListener,
    removeListener: removeMidiListener,
    isConnected,
    showMidiModal,
    preferredInputMode,
    setPreferredInputMode,
    openInputSetupModal,
  } = useMidiContext();
  const {
    addListener: addAudioListener,
    removeListener: removeAudioListener,
    start,
    stop,
    setRecognitionHints,
    calibrationProfile,
  } = useAudioRecognition();

  const [inputMode, setInputMode] = useState<ComposerInputMode>(preferredInputMode);
  const [builderMode, setBuilderMode] = useState<BuilderMode>('capture');
  const [menuOpen, setMenuOpen] = useState(false);
  // On non-desktop devices (iPad, phones) the sidebar becomes a slidable drawer,
  // so the content fills the full width and the nav shows the menu toggle button.
  const isDrawer = useMenuIsDrawer(true);
  const [capturedEvents, setCapturedEvents] = useState<LiveComposerEvent[]>([]);
  const [capturedPedalEvents, setCapturedPedalEvents] = useState<LiveComposerPedalEvent[]>([]);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [playbackTempo, setPlaybackTempo] = useState(DEFAULT_PLAYBACK_TEMPO_BPM);
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [showPlaybackCursor, setShowPlaybackCursor] = useState(false);
  const [playbackCursorStartDivisions, setPlaybackCursorStartDivisions] = useState(0);
  const [playbackSessionKey, setPlaybackSessionKey] = useState(0);
  const [draftTitle, setDraftTitle] = useState(DEFAULT_DRAFT_TITLE);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveTitleInput, setSaveTitleInput] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [timeSignatureId, setTimeSignatureId] = useState(DEFAULT_TIME_SIGNATURE.id);
  const [selectedDurationDivisions, setSelectedDurationDivisions] = useState(
    DRAFT_DURATION_OPTIONS[2].divisions,
  );
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [selectedNoteTarget, setSelectedNoteTarget] = useState<SelectedNoteTarget | null>(null);
  const [recordingCursorDivisions, setRecordingCursorDivisions] = useState(0);
  const [recordingHandMode, setRecordingHandMode] = useState<RecordingHandMode>('both');
  const [captureTempoAutoEnabled, setCaptureTempoAutoEnabled] = useState(true);
  const [captureTempoBpm, setCaptureTempoBpm] = useState(DEFAULT_PLAYBACK_TEMPO_BPM);
  const [captureTempoMinBpm, setCaptureTempoMinBpm] = useState(DEFAULT_CAPTURE_TEMPO_MIN_BPM);
  const [captureTempoMaxBpm, setCaptureTempoMaxBpm] = useState(DEFAULT_CAPTURE_TEMPO_MAX_BPM);
  const [captureTempoConfidence, setCaptureTempoConfidence] = useState(0);
  const [audioDiagnosticsVisible, setAudioDiagnosticsVisible] = useState(() =>
    readStoredBoolean(AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY),
  );
  const [audioDiagnosticsSnapshot, setAudioDiagnosticsSnapshot] =
    useState<AudioDiagnosticsSnapshot | null>(null);
  const [advancedToolsOpen, setAdvancedToolsOpen] = useState(() =>
    readStoredBoolean(ADVANCED_TOOLS_OPEN_STORAGE_KEY),
  );
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [undoStack, setUndoStack] = useState<ComposerDraftSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<ComposerDraftSnapshot[]>([]);
  const [saveSuccessRepertoire, setSaveSuccessRepertoire] = useState<UserRepertoireData | null>(
    null,
  );

  const nextEventIdRef = useRef(1);
  const pendingChordRef = useRef<PendingChord | null>(null);
  const activeCapturedEventsRef = useRef<Map<string, ActiveCapturedEvent>>(new Map());
  const activeNoteOwnerRef = useRef<Map<number, string>>(new Map());
  const recentAudioOnsetsRef = useRef<Map<number, number>>(new Map());
  const audioMissingSinceRef = useRef<Map<number, number>>(new Map());
  const audioStartRequestRef = useRef(0);
  const audioDiagnosticsFramesRef = useRef<AudioDiagnosticsFrame[]>([]);
  const audioDiagnosticsVisibleRef = useRef(false);
  const latestAudioDiagnosticsSnapshotRef = useRef<AudioDiagnosticsSnapshot | null>(null);
  const lastAudioCaptureCandidatesRef = useRef<AudioCaptureCandidate[]>([]);
  const latestAudioFrameCaptureCandidatesRef = useRef<AudioCaptureCandidate[]>([]);
  const pendingAudioOnsetBurstRef = useRef<PendingAudioOnsetBurst | null>(null);
  const pendingAudioSparseOnsetUntilMsRef = useRef(0);
  const lastAudioDiagnosticsUpdateMsRef = useRef(0);
  const captureSourceReadyRef = useRef<Record<'midi' | 'audio', boolean>>({
    midi: false,
    audio: false,
  });
  const captureTimingSessionRef = useRef<CaptureTimingSession | null>(null);
  const adaptiveTempoOnsetsRef = useRef<number[]>([]);
  const hasInitializedInputModeRef = useRef(false);
  const lastReleasedAtMsRef = useRef<number | null>(null);
  const recordingCursorDivisionsRef = useRef(0);
  const recordingHandModeRef = useRef<RecordingHandMode>('both');
  const captureTempoRef = useRef(captureTempoBpm);
  const captureTempoAutoEnabledRef = useRef(captureTempoAutoEnabled);
  const captureTempoRangeRef = useRef({
    min: captureTempoMinBpm,
    max: captureTempoMaxBpm,
  });
  const playbackSynthRef = useRef<Tone.PolySynth<Tone.Synth> | null>(null);
  const metronomeSamplesRef = useRef<MetronomeSamples | null>(null);
  const metronomeSamplesPromiseRef = useRef<Promise<MetronomeSamples> | null>(null);
  const metronomeTimeoutRef = useRef<number | null>(null);
  const metronomeBeatIndexRef = useRef(0);
  const playbackInstructionsRef = useRef<PlaybackInstruction[]>([]);
  const playbackSnapshotSignatureRef = useRef('');
  const playbackNextInstructionIndexRef = useRef(0);
  const playbackNextInstructionTimeoutRef = useRef<number | null>(null);
  const playbackNextInstructionDueAtMsRef = useRef<number | null>(null);
  const playbackResumeDelayMsRef = useRef(0);
  const playbackTempoRef = useRef(playbackTempo);
  const playbackPedalDownRef = useRef(false);
  const playbackSustainedNotesRef = useRef<Set<number>>(new Set());
  const mergeKeyActiveRef = useRef(false);
  const manualNotePointerDragHandlerRef = useRef<ManualNotePointerDragHandler | null>(null);
  const skipNextHistoryCaptureRef = useRef(false);
  const currentDraftSignatureRef = useRef('');
  const currentDraftSnapshotRef = useRef<ComposerDraftSnapshot | null>(null);
  const hasInitializedDraftHistoryRef = useRef(false);

  useEffect(() => {
    playbackTempoRef.current = playbackTempo;
  }, [playbackTempo]);

  useEffect(() => {
    audioDiagnosticsVisibleRef.current = audioDiagnosticsVisible;
    writeStoredBoolean(AUDIO_DIAGNOSTICS_VISIBLE_STORAGE_KEY, audioDiagnosticsVisible);
    if (audioDiagnosticsVisible) {
      setAudioDiagnosticsSnapshot(latestAudioDiagnosticsSnapshotRef.current);
    }
  }, [audioDiagnosticsVisible]);

  useEffect(() => {
    writeStoredBoolean(ADVANCED_TOOLS_OPEN_STORAGE_KEY, advancedToolsOpen);
  }, [advancedToolsOpen]);

  const recordAudioDiagnosticsFrame = useCallback(
    (frame: RecognitionFrame, captureCandidates: AudioCaptureCandidate[]) => {
      if (!audioDiagnosticsVisibleRef.current) return;

      const capturedAtMs = Date.now();
      if (
        capturedAtMs - lastAudioDiagnosticsUpdateMsRef.current <
        AUDIO_DIAGNOSTICS_UI_INTERVAL_MS
      ) {
        return;
      }

      const diagnosticFrame = buildAudioDiagnosticsFrame(
        frame,
        captureCandidates,
        lastAudioCaptureCandidatesRef.current,
        capturedAtMs,
      );
      const nextFrames = [...audioDiagnosticsFramesRef.current, diagnosticFrame].slice(
        -AUDIO_DIAGNOSTICS_MAX_FRAMES,
      );
      const snapshot = buildAudioDiagnosticsSnapshot(nextFrames);
      audioDiagnosticsFramesRef.current = nextFrames;
      latestAudioDiagnosticsSnapshotRef.current = snapshot;

      if (snapshot) {
        lastAudioDiagnosticsUpdateMsRef.current = capturedAtMs;
        setAudioDiagnosticsSnapshot(snapshot);
      }
    },
    [],
  );

  const handleRunAudioDiagnostics = useCallback(() => {
    audioDiagnosticsFramesRef.current = [];
    latestAudioDiagnosticsSnapshotRef.current = null;
    lastAudioDiagnosticsUpdateMsRef.current = 0;
    audioDiagnosticsVisibleRef.current = true;
    setAudioDiagnosticsSnapshot(null);
    setAudioDiagnosticsVisible(true);
  }, []);

  useEffect(() => {
    captureTempoRef.current = captureTempoBpm;
  }, [captureTempoBpm]);

  useEffect(() => {
    captureTempoAutoEnabledRef.current = captureTempoAutoEnabled;
  }, [captureTempoAutoEnabled]);

  useEffect(() => {
    const min = Math.min(captureTempoMinBpm, captureTempoMaxBpm);
    const max = Math.max(captureTempoMinBpm, captureTempoMaxBpm);
    captureTempoRangeRef.current = { min, max };
    setCaptureTempoBpm((tempo) => clampTempoBpm(tempo, min, max));
  }, [captureTempoMinBpm, captureTempoMaxBpm]);

  useEffect(() => {
    if (captureTempoAutoEnabled) return;
    setCaptureTempoBpm(clampTempoBpm(playbackTempo, captureTempoMinBpm, captureTempoMaxBpm));
    setCaptureTempoConfidence(0);
  }, [captureTempoAutoEnabled, captureTempoMaxBpm, captureTempoMinBpm, playbackTempo]);

  useEffect(() => {
    recordingHandModeRef.current = recordingHandMode;
  }, [recordingHandMode]);

  const selectedTimeSignature = useMemo(
    () =>
      COMMON_TIME_SIGNATURES.find((timeSignature) => timeSignature.id === timeSignatureId) ??
      DEFAULT_TIME_SIGNATURE,
    [timeSignatureId],
  );
  const measureDurationDivisions = useMemo(
    () => getMeasureDurationDivisions(selectedTimeSignature),
    [selectedTimeSignature],
  );
  const recordingCursorMaxDivisions = useMemo(
    () =>
      Math.max(
        PRERENDERED_MEASURE_COUNT * measureDurationDivisions,
        getTimelineEndDivisions(capturedEvents) + measureDurationDivisions,
      ),
    [capturedEvents, measureDurationDivisions],
  );
  const draftTimelineEvents = useMemo(
    () => getDraftTimelineEvents(capturedEvents),
    [capturedEvents],
  );
  const selectedEvents = useMemo(
    () => capturedEvents.filter((event) => selectedEventIds.includes(event.id)),
    [capturedEvents, selectedEventIds],
  );
  const selectedTimelineEvents = useMemo(
    () => draftTimelineEvents.filter((event) => selectedEventIds.includes(event.id)),
    [draftTimelineEvents, selectedEventIds],
  );
  const selectedAccidentalTargetLabel = useMemo(() => {
    if (!selectedNoteTarget) return `${selectedEventIds.length} selected`;
    return `${midiToName(selectedNoteTarget.midi)} selected`;
  }, [selectedEventIds.length, selectedNoteTarget]);
  const selectedAccidentalSpelling = useMemo(() => {
    const targetSpellings = selectedEvents.flatMap((event) => {
      const targetNotes =
        selectedNoteTarget?.eventId === event.id
          ? normalizeMidiNotes(event.midiNotes).filter((midi) => midi === selectedNoteTarget.midi)
          : normalizeMidiNotes(event.midiNotes);

      return targetNotes.map((midi) => event.accidentalSpellings?.[midi] ?? 'auto');
    });

    if (targetSpellings.length === 0) return null;
    const firstSpelling = targetSpellings[0];
    return targetSpellings.every((spelling) => spelling === firstSpelling) ? firstSpelling : null;
  }, [selectedEvents, selectedNoteTarget]);

  const resetAdaptiveTempoLearning = useCallback(() => {
    adaptiveTempoOnsetsRef.current = [];
    captureTimingSessionRef.current = null;
    setCaptureTempoConfidence(0);
  }, []);

  const setRecordingCursor = useCallback(
    (divisions: number, options?: { preserveReleaseClock?: boolean }) => {
      const nextDivisions = Math.max(
        0,
        Math.min(recordingCursorMaxDivisions, Math.round(divisions)),
      );
      if (!options?.preserveReleaseClock) {
        lastReleasedAtMsRef.current = null;
      }
      recordingCursorDivisionsRef.current = nextDivisions;
      setRecordingCursorDivisions(nextDivisions);
    },
    [recordingCursorMaxDivisions],
  );

  const handleRecordingCursorChange = useCallback(
    (divisions: number) => {
      resetAdaptiveTempoLearning();
      setRecordingCursor(divisions);
    },
    [resetAdaptiveTempoLearning, setRecordingCursor],
  );

  useEffect(() => {
    if (recordingCursorDivisions > recordingCursorMaxDivisions) {
      setRecordingCursor(recordingCursorMaxDivisions);
    }
  }, [recordingCursorDivisions, recordingCursorMaxDivisions, setRecordingCursor]);

  const quantizeDurationDivisions = useCallback((durationMs: number) => {
    return quantizeDurationDivisionsForTempo(durationMs, captureTempoRef.current);
  }, []);

  const quantizeElapsedDivisions = useCallback((durationMs: number) => {
    return quantizeElapsedDivisionsForTempo(durationMs, captureTempoRef.current);
  }, []);

  const getOrCreateCaptureTimingSession = useCallback(
    (capturedAtMs: number, source: 'midi' | 'audio') => {
      const existingSession = captureTimingSessionRef.current;
      if (existingSession && existingSession.source === source) return existingSession;

      const nextSession: CaptureTimingSession = {
        source,
        startedAtMs: capturedAtMs,
        startDivisions: recordingCursorDivisionsRef.current,
      };
      captureTimingSessionRef.current = nextSession;
      adaptiveTempoOnsetsRef.current = [];
      setCaptureTempoConfidence(0);
      return nextSession;
    },
    [],
  );

  const updateActiveCaptureTimingForTempo = useCallback((tempoBpm: number) => {
    activeCapturedEventsRef.current.forEach((activeEvent) => {
      activeEvent.startDivisions = getCaptureSessionDivisionsAtMs(
        activeEvent.startedAtMs,
        activeEvent.rawCaptureSessionStartedAtMs,
        activeEvent.rawCaptureSessionStartDivisions,
        tempoBpm,
      );
    });

    const pendingChord = pendingChordRef.current;
    if (pendingChord) {
      pendingChord.startDivisions = getCaptureSessionDivisionsAtMs(
        pendingChord.startedAtMs,
        pendingChord.rawCaptureSessionStartedAtMs,
        pendingChord.rawCaptureSessionStartDivisions,
        tempoBpm,
      );
    }
  }, []);

  const requantizeAdaptiveCaptureForTempo = useCallback(
    (tempoBpm: number) => {
      updateActiveCaptureTimingForTempo(tempoBpm);
      setCapturedEvents((prev) => {
        const adaptiveEvents = prev
          .filter(hasAdaptiveEventTiming)
          .sort((a, b) => a.rawStartedAtMs! - b.rawStartedAtMs!)
          .slice(-AUTO_TEMPO_MAX_REQUANTIZED_EVENTS);
        const adaptiveIds = new Set(adaptiveEvents.map((event) => event.id));
        if (adaptiveIds.size === 0) return prev;

        return sortDraftEvents(
          prev.map((event) =>
            adaptiveIds.has(event.id) ? requantizeAdaptiveEventForTempo(event, tempoBpm) : event,
          ),
        );
      });
      setCapturedPedalEvents((prev) =>
        prev.map((event) => requantizeAdaptivePedalForTempo(event, tempoBpm)),
      );
    },
    [updateActiveCaptureTimingForTempo],
  );

  const recordAdaptiveTempoOnset = useCallback(
    (capturedAtMs: number) => {
      if (!captureTempoAutoEnabledRef.current) return;

      const currentOnsets = adaptiveTempoOnsetsRef.current;
      const previousOnset = currentOnsets[currentOnsets.length - 1];
      const nextOnsets =
        previousOnset != null && capturedAtMs - previousOnset > AUTO_TEMPO_MAX_INTERVAL_MS
          ? [capturedAtMs]
          : [...currentOnsets, capturedAtMs].slice(-AUTO_TEMPO_MAX_ONSETS);
      adaptiveTempoOnsetsRef.current = nextOnsets;

      const { min, max } = captureTempoRangeRef.current;
      const estimate = estimateAdaptiveCaptureTempo(
        nextOnsets,
        captureTempoRef.current,
        selectedDurationDivisions,
        min,
        max,
      );

      if (!estimate) {
        setCaptureTempoConfidence(0);
        return;
      }

      setCaptureTempoConfidence(estimate.confidence);
      if (estimate.confidence < AUTO_TEMPO_MIN_CONFIDENCE) return;

      const currentTempo = captureTempoRef.current;
      const smoothing = estimate.confidence >= 0.72 ? 0.56 : 0.34;
      const maxStep = estimate.confidence >= 0.72 ? 18 : 10;
      const smoothedDelta = (estimate.tempoBpm - currentTempo) * smoothing;
      const nextTempo = clampTempoBpm(
        currentTempo + Math.max(-maxStep, Math.min(maxStep, smoothedDelta)),
        min,
        max,
      );

      if (Math.abs(nextTempo - currentTempo) < 1) return;

      captureTempoRef.current = nextTempo;
      setCaptureTempoBpm(nextTempo);
      requantizeAdaptiveCaptureForTempo(nextTempo);
    },
    [requantizeAdaptiveCaptureForTempo, selectedDurationDivisions],
  );

  const clearActiveCaptureState = useCallback(() => {
    activeCapturedEventsRef.current.clear();
    activeNoteOwnerRef.current.clear();
    audioMissingSinceRef.current.clear();
    lastReleasedAtMsRef.current = null;
  }, []);

  const isCaptureMode = builderMode === 'capture';
  const isEditMode = builderMode === 'edit';
  const isPracticeMode = builderMode === 'practice';

  const inputGateSatisfied = isConnected();
  const canCaptureMidi =
    isCaptureMode && inputMode === 'midi' && inputGateSatisfied && !showMidiModal;
  const canCaptureAudio =
    isCaptureMode &&
    inputMode === 'audio' &&
    audioRecognitionEnabled &&
    calibrationProfile != null &&
    inputGateSatisfied &&
    !showMidiModal;
  const canOpenAudioCalibrationSetup = inputMode === 'audio' && audioRecognitionEnabled;
  const inputStatus = useMemo(() => {
    if (showMidiModal) {
      return {
        label: 'Input setup open',
        detail: 'Choose a capture source',
        tone: 'warning' as const,
      };
    }

    if (inputMode === 'audio') {
      if (!audioRecognitionEnabled) {
        return {
          label: 'Audio capture unavailable',
          detail: 'Use MIDI capture',
          tone: 'warning' as const,
        };
      }
      if (!calibrationProfile) {
        return {
          label: 'Audio needs calibration',
          detail: 'Calibration required',
          tone: 'warning' as const,
        };
      }
      if (inputGateSatisfied) {
        return {
          label: 'Listening to piano',
          detail: 'Audio capture ready',
          tone: 'ready' as const,
        };
      }
      return {
        label: 'Audio input disconnected',
        detail: 'Connect input',
        tone: 'idle' as const,
      };
    }

    if (inputGateSatisfied) {
      return {
        label: 'MIDI connected',
        detail: 'MIDI capture ready',
        tone: 'ready' as const,
      };
    }

    return {
      label: 'MIDI disconnected',
      detail: 'Connect keyboard',
      tone: 'idle' as const,
    };
  }, [audioRecognitionEnabled, calibrationProfile, inputGateSatisfied, inputMode, showMidiModal]);
  const canRunAudioCalibration = inputMode === 'audio' && audioRecognitionEnabled;

  const getCaptureStartDivisions = useCallback(
    (capturedAtMs: number, source: 'midi' | 'audio', timingSession: CaptureTimingSession) => {
      const sessionStartDivisions = getCaptureSessionDivisionsAtMs(
        capturedAtMs,
        timingSession.startedAtMs,
        timingSession.startDivisions,
        captureTempoRef.current,
      );
      const activeEvents = Array.from(activeCapturedEventsRef.current.values()).filter(
        (activeEvent) => activeEvent.source === source,
      );
      if (activeEvents.length > 0) {
        const latestActiveEvent = activeEvents.reduce((selected, candidate) =>
          candidate.startedAtMs > selected.startedAtMs ? candidate : selected,
        );
        const elapsedDivisions = quantizeElapsedDivisions(
          capturedAtMs - latestActiveEvent.startedAtMs,
        );
        return Math.max(sessionStartDivisions, latestActiveEvent.startDivisions + elapsedDivisions);
      }

      return sessionStartDivisions;
    },
    [quantizeElapsedDivisions],
  );

  const registerCapturedEvent = useCallback(
    (
      midiNotes: number[],
      capturedAtMs: number,
      source: 'midi' | 'audio',
      startDivisions = recordingCursorDivisionsRef.current,
      staff = getStaffForRecordingHand(recordingHandModeRef.current),
      timingSession = getOrCreateCaptureTimingSession(capturedAtMs, source),
    ) => {
      const id = `live-composer-${nextEventIdRef.current++}`;
      const normalizedMidiNotes = normalizeMidiNotes(midiNotes);
      const normalizedStartDivisions = Math.max(0, Math.round(startDivisions));
      const rawCaptureSessionStartedAtMs = timingSession.startedAtMs;
      const rawCaptureSessionStartDivisions = timingSession.startDivisions;

      setCapturedEvents((prev) =>
        sortDraftEvents([
          ...prev,
          {
            id,
            midiNotes: normalizedMidiNotes,
            capturedAtMs,
            source,
            restBeforeDivisions: 0,
            durationDivisions: MIN_EVENT_DURATION_DIVISIONS,
            startDivisions: normalizedStartDivisions,
            staff,
            adaptiveTiming: true,
            rawStartedAtMs: capturedAtMs,
            rawCaptureSessionStartedAtMs,
            rawCaptureSessionStartDivisions,
          },
        ]),
      );

      const activeEvent: ActiveCapturedEvent = {
        id,
        startedAtMs: capturedAtMs,
        startDivisions: normalizedStartDivisions,
        source,
        allNotes: new Set(normalizedMidiNotes),
        activeNotes: new Set(normalizedMidiNotes),
        rawCaptureSessionStartedAtMs,
        rawCaptureSessionStartDivisions,
      };

      activeCapturedEventsRef.current.set(id, activeEvent);
      normalizedMidiNotes.forEach((note) => {
        activeNoteOwnerRef.current.set(note, id);
        audioMissingSinceRef.current.delete(note);
      });
    },
    [getOrCreateCaptureTimingSession],
  );

  const finalizeCapturedEvent = useCallback(
    (eventId: string, endedAtMs: number) => {
      const activeEvent = activeCapturedEventsRef.current.get(eventId);
      if (!activeEvent) return;

      const durationDivisions = quantizeDurationDivisions(endedAtMs - activeEvent.startedAtMs);
      const startDivisions = getCaptureSessionDivisionsAtMs(
        activeEvent.startedAtMs,
        activeEvent.rawCaptureSessionStartedAtMs,
        activeEvent.rawCaptureSessionStartDivisions,
        captureTempoRef.current,
      );
      setCapturedEvents((prev) =>
        prev.map((event) =>
          event.id === eventId
            ? {
                ...event,
                startDivisions,
                durationDivisions,
                endedAtMs,
                rawEndedAtMs: endedAtMs,
              }
            : event,
        ),
      );

      activeEvent.startDivisions = startDivisions;
      setRecordingCursor(startDivisions + durationDivisions, {
        preserveReleaseClock: true,
      });

      activeEvent.allNotes.forEach((note) => {
        if (activeNoteOwnerRef.current.get(note) === eventId) {
          activeNoteOwnerRef.current.delete(note);
        }
        audioMissingSinceRef.current.delete(note);
      });

      activeCapturedEventsRef.current.delete(eventId);
      lastReleasedAtMsRef.current = endedAtMs;
    },
    [quantizeDurationDivisions, setRecordingCursor],
  );

  const closeActiveEventsForSequentialOnset = useCallback(
    (source: 'midi' | 'audio', endDivisions: number, endedAtMs: number) => {
      const activeEvents = Array.from(activeCapturedEventsRef.current.values()).filter(
        (activeEvent) => activeEvent.source === source,
      );
      if (activeEvents.length === 0) return;

      const safeEndDivisions = Math.max(0, Math.round(endDivisions));
      setCapturedEvents((prev) =>
        prev.map((event) => {
          const activeEvent = activeEvents.find((candidate) => candidate.id === event.id);
          if (!activeEvent) return event;

          return {
            ...event,
            durationDivisions: Math.max(
              MIN_EVENT_DURATION_DIVISIONS,
              safeEndDivisions - activeEvent.startDivisions,
            ),
            endedAtMs,
            rawEndedAtMs: endedAtMs,
          };
        }),
      );

      activeEvents.forEach((activeEvent) => {
        activeEvent.allNotes.forEach((note) => {
          if (activeNoteOwnerRef.current.get(note) === activeEvent.id) {
            activeNoteOwnerRef.current.delete(note);
          }
          audioMissingSinceRef.current.delete(note);
        });
        activeCapturedEventsRef.current.delete(activeEvent.id);
      });

      setRecordingCursor(safeEndDivisions, { preserveReleaseClock: true });
      lastReleasedAtMsRef.current = endedAtMs;
    },
    [setRecordingCursor],
  );

  const releaseCapturedNote = useCallback(
    (note: number, endedAtMs: number, source: 'midi' | 'audio') => {
      const pendingChord = pendingChordRef.current;
      if (pendingChord && pendingChord.source === source && pendingChord.midiNotes.has(note)) {
        window.clearTimeout(pendingChord.timeoutId);
        pendingChordRef.current = null;
        const pendingNotes = getPendingChordNotes(pendingChord);
        if (pendingNotes.length > 0) {
          registerCapturedEvent(
            pendingNotes,
            pendingChord.startedAtMs,
            pendingChord.source,
            pendingChord.startDivisions,
            pendingChord.staff,
            {
              source: pendingChord.source,
              startedAtMs: pendingChord.rawCaptureSessionStartedAtMs,
              startDivisions: pendingChord.rawCaptureSessionStartDivisions,
            },
          );
        }
      }

      const eventId = activeNoteOwnerRef.current.get(note);
      if (!eventId) return;

      const activeEvent = activeCapturedEventsRef.current.get(eventId);
      if (!activeEvent) {
        activeNoteOwnerRef.current.delete(note);
        return;
      }

      activeEvent.activeNotes.delete(note);
      activeNoteOwnerRef.current.delete(note);
      audioMissingSinceRef.current.delete(note);

      if (activeEvent.activeNotes.size === 0) {
        finalizeCapturedEvent(eventId, endedAtMs);
      }
    },
    [finalizeCapturedEvent, registerCapturedEvent],
  );

  const computePedalPositionDivisions = useCallback(() => {
    const activeEvents = Array.from(activeCapturedEventsRef.current.values());
    if (activeEvents.length > 0) {
      const activeEvent = activeEvents.reduce((selected, candidate) =>
        candidate.startedAtMs > selected.startedAtMs ? candidate : selected,
      );
      return activeEvent.startDivisions;
    }

    return recordingCursorDivisionsRef.current;
  }, []);

  const registerPedalEvent = useCallback(
    (pedalDown: boolean, capturedAtMs: number) => {
      if (!captureSourceReadyRef.current.midi) return;

      let forcedPositionDivisions: number | null = null;
      let timingSession = getOrCreateCaptureTimingSession(capturedAtMs, 'midi');
      const pendingChord = pendingChordRef.current;
      if (pendingChord) {
        forcedPositionDivisions = pendingChord.startDivisions;
        timingSession = {
          source: pendingChord.source,
          startedAtMs: pendingChord.rawCaptureSessionStartedAtMs,
          startDivisions: pendingChord.rawCaptureSessionStartDivisions,
        };
        window.clearTimeout(pendingChord.timeoutId);
        pendingChordRef.current = null;
        const pendingNotes = getPendingChordNotes(pendingChord);
        if (pendingNotes.length > 0) {
          registerCapturedEvent(
            pendingNotes,
            pendingChord.startedAtMs,
            pendingChord.source,
            pendingChord.startDivisions,
            pendingChord.staff,
            {
              source: pendingChord.source,
              startedAtMs: pendingChord.rawCaptureSessionStartedAtMs,
              startDivisions: pendingChord.rawCaptureSessionStartDivisions,
            },
          );
        }
      }
      const positionDivisions = forcedPositionDivisions ?? computePedalPositionDivisions();

      setCapturedPedalEvents((prev) => {
        const lastEvent = prev[prev.length - 1];
        if (
          lastEvent &&
          lastEvent.pedalDown === pedalDown &&
          lastEvent.positionDivisions === positionDivisions
        ) {
          return prev;
        }

        return [
          ...prev,
          {
            id: `live-composer-pedal-${nextEventIdRef.current++}`,
            capturedAtMs,
            source: 'midi',
            pedalDown,
            positionDivisions,
            adaptiveTiming: true,
            rawCaptureSessionStartedAtMs: timingSession.startedAtMs,
            rawCaptureSessionStartDivisions: timingSession.startDivisions,
          },
        ];
      });
    },
    [computePedalPositionDivisions, getOrCreateCaptureTimingSession, registerCapturedEvent],
  );

  const stopPlayback = useCallback(
    ({
      resetProgress = false,
      hideCursor = false,
    }: { resetProgress?: boolean; hideCursor?: boolean } = {}) => {
      if (playbackNextInstructionTimeoutRef.current != null) {
        window.clearTimeout(playbackNextInstructionTimeoutRef.current);
        playbackNextInstructionTimeoutRef.current = null;
      }
      if (playbackNextInstructionDueAtMsRef.current != null && !resetProgress) {
        playbackResumeDelayMsRef.current = Math.max(
          0,
          playbackNextInstructionDueAtMsRef.current - performance.now(),
        );
      } else if (resetProgress) {
        playbackResumeDelayMsRef.current = 0;
      }
      playbackNextInstructionDueAtMsRef.current = null;
      playbackPedalDownRef.current = false;
      playbackSustainedNotesRef.current.clear();
      playbackSynthRef.current?.releaseAll();
      (window as PlaybackSamplerWindow).__museflowSampler?.releaseAll?.();
      setIsPlayingBack(false);
      if (resetProgress) {
        playbackInstructionsRef.current = [];
        playbackSnapshotSignatureRef.current = '';
        playbackNextInstructionIndexRef.current = 0;
      }
      if (hideCursor || resetProgress) {
        setShowPlaybackCursor(false);
      }
    },
    [],
  );

  const ensurePlaybackSynth = useCallback(() => {
    if (playbackSynthRef.current) return playbackSynthRef.current;

    const synth = new Tone.PolySynth(Tone.Synth).toDestination();
    synth.set({
      volume: -7,
      oscillator: {
        type: 'triangle',
      },
      envelope: {
        attack: 0.01,
        decay: 0.12,
        sustain: 0.28,
        release: 0.7,
      },
    });

    playbackSynthRef.current = synth;
    return synth;
  }, []);

  const ensureMetronomeSamples = useCallback(async () => {
    if (metronomeSamplesRef.current) return metronomeSamplesRef.current;
    if (metronomeSamplesPromiseRef.current) return metronomeSamplesPromiseRef.current;

    metronomeSamplesPromiseRef.current = (async () => {
      await Tone.start();
      const audioContext = Tone.getContext().rawContext as AudioContext;
      const [downBeatBuffer, upBeatBuffer] = await Promise.all([
        decodeBase64ToAudioBuffer(audioContext, DOWNBEAT_B64),
        decodeBase64ToAudioBuffer(audioContext, UPBEAT_B64),
      ]);
      const samples = {
        downBeatSample: new Tone.Player(downBeatBuffer).toDestination(),
        upBeatSample: new Tone.Player(upBeatBuffer).toDestination(),
      };

      metronomeSamplesRef.current = samples;
      metronomeSamplesPromiseRef.current = null;
      return samples;
    })().catch((error) => {
      metronomeSamplesPromiseRef.current = null;
      throw error;
    });

    return metronomeSamplesPromiseRef.current;
  }, []);

  const stopMetronome = useCallback(() => {
    if (metronomeTimeoutRef.current != null) {
      window.clearTimeout(metronomeTimeoutRef.current);
      metronomeTimeoutRef.current = null;
    }
    try {
      metronomeSamplesRef.current?.downBeatSample.stop();
      metronomeSamplesRef.current?.upBeatSample.stop();
    } catch {
      // The click is non-critical; stopping can race with teardown during navigation.
    }
  }, []);

  const triggerMetronomeClick = useCallback((downbeat: boolean) => {
    const sample = downbeat
      ? metronomeSamplesRef.current?.downBeatSample
      : metronomeSamplesRef.current?.upBeatSample;

    try {
      sample?.start(Tone.immediate());
    } catch {
      // The click is non-critical; scheduling can race with teardown during navigation.
    }
  }, []);

  useEffect(() => {
    if (!metronomeEnabled) {
      stopMetronome();
      return undefined;
    }

    let cancelled = false;
    metronomeBeatIndexRef.current = 0;

    const scheduleNextClick = () => {
      const beatTypeRatio = 4 / Math.max(1, selectedTimeSignature.beatType);
      const beatDurationMs = Math.max(
        40,
        (60_000 / Math.max(playbackTempoRef.current, 1)) * beatTypeRatio,
      );

      metronomeTimeoutRef.current = window.setTimeout(runClick, beatDurationMs);
    };

    const runClick = () => {
      if (cancelled) return;

      const beatIndex = metronomeBeatIndexRef.current;
      metronomeBeatIndexRef.current += 1;
      triggerMetronomeClick(beatIndex % Math.max(1, selectedTimeSignature.beats) === 0);
      scheduleNextClick();
    };

    void ensureMetronomeSamples()
      .then(() => {
        if (!cancelled) {
          runClick();
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      stopMetronome();
    };
  }, [
    metronomeEnabled,
    ensureMetronomeSamples,
    selectedTimeSignature.beats,
    selectedTimeSignature.beatType,
    stopMetronome,
    triggerMetronomeClick,
  ]);

  const flushPendingChord = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (!pendingChord) return;

    window.clearTimeout(pendingChord.timeoutId);
    pendingChordRef.current = null;

    const midiNotes = getPendingChordNotes(pendingChord);
    if (midiNotes.length === 0) return;

    registerCapturedEvent(
      midiNotes,
      pendingChord.startedAtMs,
      pendingChord.source,
      pendingChord.startDivisions,
      pendingChord.staff,
      {
        source: pendingChord.source,
        startedAtMs: pendingChord.rawCaptureSessionStartedAtMs,
        startDivisions: pendingChord.rawCaptureSessionStartDivisions,
      },
    );
  }, [registerCapturedEvent]);

  const clearPendingChord = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (pendingChord) {
      window.clearTimeout(pendingChord.timeoutId);
      pendingChordRef.current = null;
    }
  }, []);

  const getCurrentDraftSnapshot = useCallback(
    (): ComposerDraftSnapshot => ({
      capturedEvents: cloneLiveComposerEvents(capturedEvents),
      capturedPedalEvents: cloneLiveComposerPedalEvents(capturedPedalEvents),
      draftTitle,
      timeSignatureId,
      selectedDurationDivisions,
      recordingCursorDivisions,
      recordingHandMode,
      playbackTempo,
      metronomeEnabled,
      captureTempoAutoEnabled,
      captureTempoBpm,
      captureTempoMinBpm,
      captureTempoMaxBpm,
      captureTempoConfidence,
    }),
    [
      captureTempoAutoEnabled,
      captureTempoBpm,
      captureTempoConfidence,
      captureTempoMaxBpm,
      captureTempoMinBpm,
      capturedEvents,
      capturedPedalEvents,
      draftTitle,
      metronomeEnabled,
      playbackTempo,
      recordingCursorDivisions,
      recordingHandMode,
      selectedDurationDivisions,
      timeSignatureId,
    ],
  );

  const applyDraftSnapshot = useCallback(
    (snapshot: ComposerDraftSnapshot) => {
      const nextSnapshot = cloneDraftSnapshot(snapshot);
      skipNextHistoryCaptureRef.current = true;
      stopPlayback({ resetProgress: true, hideCursor: true });
      clearPendingChord();
      clearActiveCaptureState();
      setCapturedEvents(nextSnapshot.capturedEvents);
      setCapturedPedalEvents(nextSnapshot.capturedPedalEvents);
      setDraftTitle(nextSnapshot.draftTitle);
      setTimeSignatureId(nextSnapshot.timeSignatureId);
      setSelectedDurationDivisions(nextSnapshot.selectedDurationDivisions);
      setRecordingHandMode(nextSnapshot.recordingHandMode);
      setPlaybackTempo(nextSnapshot.playbackTempo);
      setMetronomeEnabled(nextSnapshot.metronomeEnabled);
      setCaptureTempoAutoEnabled(nextSnapshot.captureTempoAutoEnabled);
      setCaptureTempoBpm(nextSnapshot.captureTempoBpm);
      setCaptureTempoMinBpm(nextSnapshot.captureTempoMinBpm);
      setCaptureTempoMaxBpm(nextSnapshot.captureTempoMaxBpm);
      setCaptureTempoConfidence(nextSnapshot.captureTempoConfidence);
      captureTempoRef.current = nextSnapshot.captureTempoBpm;
      captureTempoAutoEnabledRef.current = nextSnapshot.captureTempoAutoEnabled;
      captureTempoRangeRef.current = {
        min: nextSnapshot.captureTempoMinBpm,
        max: nextSnapshot.captureTempoMaxBpm,
      };
      adaptiveTempoOnsetsRef.current = getAdaptiveTempoOnsetsFromEvents(
        nextSnapshot.capturedEvents,
      );
      captureTimingSessionRef.current = null;
      recordingCursorDivisionsRef.current = nextSnapshot.recordingCursorDivisions;
      setRecordingCursorDivisions(nextSnapshot.recordingCursorDivisions);
      lastReleasedAtMsRef.current = null;
      recentAudioOnsetsRef.current.clear();
      pendingAudioOnsetBurstRef.current = null;
      pendingAudioSparseOnsetUntilMsRef.current = 0;
      lastAudioCaptureCandidatesRef.current = [];
      latestAudioFrameCaptureCandidatesRef.current = [];
      setSelectedEventIds([]);
      setSelectedNoteTarget(null);
    },
    [clearActiveCaptureState, clearPendingChord, stopPlayback],
  );

  const restoreStoredDraft = useCallback(() => {
    const storedDraft = readStoredDraftSnapshot();
    if (!storedDraft || !draftHasMaterial(storedDraft)) return;
    applyDraftSnapshot(storedDraft);
  }, [applyDraftSnapshot]);

  useEffect(() => {
    restoreStoredDraft();
  }, [restoreStoredDraft]);

  useEffect(() => {
    const snapshot = getCurrentDraftSnapshot();
    const signature = getDraftSnapshotSignature(snapshot);

    if (!hasInitializedDraftHistoryRef.current) {
      hasInitializedDraftHistoryRef.current = true;
      currentDraftSnapshotRef.current = cloneDraftSnapshot(snapshot);
      currentDraftSignatureRef.current = signature;
      return;
    }

    if (skipNextHistoryCaptureRef.current) {
      skipNextHistoryCaptureRef.current = false;
      currentDraftSnapshotRef.current = cloneDraftSnapshot(snapshot);
      currentDraftSignatureRef.current = signature;
      return;
    }

    if (signature === currentDraftSignatureRef.current) return;

    const previousSnapshot = currentDraftSnapshotRef.current;
    if (previousSnapshot) {
      setUndoStack((prev) =>
        [...prev, cloneDraftSnapshot(previousSnapshot)].slice(-DRAFT_HISTORY_LIMIT),
      );
      setRedoStack([]);
    }

    currentDraftSnapshotRef.current = cloneDraftSnapshot(snapshot);
    currentDraftSignatureRef.current = signature;
  }, [getCurrentDraftSnapshot]);

  useEffect(() => {
    const snapshot = getCurrentDraftSnapshot();
    if (draftHasMaterial(snapshot)) {
      writeStoredDraftSnapshot(snapshot);
    } else {
      clearStoredDraftSnapshot();
    }
  }, [getCurrentDraftSnapshot]);

  const captureInputNotes = useCallback(
    (
      incomingNotes: number[],
      capturedAtMs: number,
      source: 'midi' | 'audio',
      noteScores?: Map<number, number>,
    ) => {
      if (!captureSourceReadyRef.current[source]) return;

      let midiNotes =
        source === 'audio'
          ? normalizeMidiNotesPreservingOrder(incomingNotes)
          : normalizeMidiNotes(incomingNotes);
      if (midiNotes.length === 0) return;

      if (source === 'audio') {
        midiNotes = midiNotes.filter((note) => !activeNoteOwnerRef.current.has(note));
        if (midiNotes.length === 0) return;

        midiNotes = midiNotes.filter((note) => {
          const previous = recentAudioOnsetsRef.current.get(note);
          if (previous != null && capturedAtMs - previous < AUDIO_ONSET_DEDUP_MS) {
            return false;
          }
          recentAudioOnsetsRef.current.set(note, capturedAtMs);
          return true;
        });
      }

      if (midiNotes.length === 0) return;

      const pendingChord = pendingChordRef.current;
      if (pendingChord && capturedAtMs - pendingChord.startedAtMs <= CHORD_GROUP_WINDOW_MS) {
        midiNotes.forEach((note) => {
          pendingChord.midiNotes.add(note);
          if (source === 'audio') {
            const score = noteScores?.get(note) ?? 0;
            const previous = pendingChord.noteScores?.get(note) ?? 0;
            pendingChord.noteScores?.set(note, Math.max(previous, score));
          }
        });
        return;
      }

      if (pendingChord) {
        flushPendingChord();
      }

      const timingSession = getOrCreateCaptureTimingSession(capturedAtMs, source);
      recordAdaptiveTempoOnset(capturedAtMs);
      const startDivisions = getCaptureStartDivisions(capturedAtMs, source, timingSession);
      closeActiveEventsForSequentialOnset(source, startDivisions, capturedAtMs);

      pendingChordRef.current = {
        startedAtMs: capturedAtMs,
        startDivisions,
        staff: getStaffForRecordingHand(recordingHandModeRef.current),
        midiNotes: new Set(midiNotes),
        noteScores:
          source === 'audio'
            ? new Map(midiNotes.map((note) => [note, noteScores?.get(note) ?? 0]))
            : undefined,
        source,
        timeoutId: window.setTimeout(() => {
          flushPendingChord();
        }, CHORD_GROUP_WINDOW_MS),
        rawCaptureSessionStartedAtMs: timingSession.startedAtMs,
        rawCaptureSessionStartDivisions: timingSession.startDivisions,
      };
    },
    [
      closeActiveEventsForSequentialOnset,
      flushPendingChord,
      getCaptureStartDivisions,
      getOrCreateCaptureTimingSession,
      recordAdaptiveTempoOnset,
    ],
  );

  const applyInputMode = useCallback(
    async (nextMode: PreferredInputMode) => {
      flushPendingChord();
      clearActiveCaptureState();
      resetAdaptiveTempoLearning();
      setInputMode(nextMode);
      audioStartRequestRef.current += 1;

      if (nextMode === 'audio') {
        return;
      }

      void stop();
    },
    [clearActiveCaptureState, flushPendingChord, resetAdaptiveTempoLearning, stop],
  );

  const openAudioCalibrationSetup = useCallback(() => {
    setPreferredInputMode('audio');
    void applyInputMode('audio');
    openInputSetupModal();
  }, [applyInputMode, openInputSetupModal, setPreferredInputMode]);

  const addManualNote = useCallback(
    (drop: ManualNoteDrop) => {
      if (!isEditMode) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const id = `live-composer-manual-${nextEventIdRef.current++}`;
      const newEvent: LiveComposerEvent = {
        id,
        midiNotes: [drop.midi],
        capturedAtMs: performance.now(),
        source: 'manual',
        restBeforeDivisions: 0,
        durationDivisions: Math.max(MIN_EVENT_DURATION_DIVISIONS, drop.durationDivisions),
        startDivisions: Math.max(0, Math.round(drop.startDivisions)),
        staff: drop.staff,
      };

      setCapturedEvents((prev) => sortDraftEvents([...prev, newEvent]));
      setSelectedEventIds([id]);
      setSelectedNoteTarget({ eventId: id, midi: drop.midi });
    },
    [flushPendingChord, isEditMode, stopPlayback],
  );

  const moveExistingNote = useCallback(
    (eventId: string, drop: ExistingNoteDrop) => {
      if (!isEditMode) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const sourceMidi = drop.sourceMidi == null ? null : Math.round(drop.sourceMidi);
      const currentEvent = capturedEvents.find((event) => event.id === eventId);
      const currentEventNotes = currentEvent ? normalizeMidiNotes(currentEvent.midiNotes) : [];
      const isMovingRest = currentEvent ? isDraftRestEvent(currentEvent) : false;
      const shouldSplitEvent =
        sourceMidi != null &&
        currentEventNotes.length > 1 &&
        currentEventNotes.includes(sourceMidi);
      const splitEventId = shouldSplitEvent
        ? `live-composer-split-${nextEventIdRef.current++}`
        : null;
      const movedMidi = Math.max(0, Math.min(127, Math.round(drop.midi)));
      const movedStartDivisions = Math.max(0, Math.round(drop.startDivisions));
      const movedDurationDivisions = Math.max(
        MIN_EVENT_DURATION_DIVISIONS,
        Math.round(drop.durationDivisions),
      );

      setCapturedEvents((prev) =>
        sortDraftEvents(
          prev.flatMap((event) => {
            if (event.id !== eventId) return event;

            if (isMovingRest) {
              return {
                ...freezeAdaptiveEventTiming(event),
                source: 'manual',
                restBeforeDivisions: 0,
                startDivisions: movedStartDivisions,
                durationDivisions: movedDurationDivisions,
                staff: drop.staff ?? event.staff,
              };
            }

            const normalizedEventNotes = normalizeMidiNotes(event.midiNotes);
            if (
              splitEventId &&
              sourceMidi != null &&
              normalizedEventNotes.length > 1 &&
              normalizedEventNotes.includes(sourceMidi)
            ) {
              const remainingNotes = normalizedEventNotes.filter((note) => note !== sourceMidi);
              const sourceSpelling = event.accidentalSpellings?.[sourceMidi];
              const remainingEvent: LiveComposerEvent = {
                ...freezeAdaptiveEventTiming(event),
                midiNotes: remainingNotes,
                accidentalSpellings: pickAccidentalSpellings(
                  event.accidentalSpellings,
                  remainingNotes,
                ),
                restBeforeDivisions: 0,
              };
              const movedEvent: LiveComposerEvent = {
                ...freezeAdaptiveEventTiming(event),
                id: splitEventId,
                source: event.source === 'audio' ? event.source : 'manual',
                midiNotes: [movedMidi],
                accidentalSpellings: normalizeAccidentalSpellings(
                  sourceSpelling ? { [movedMidi]: sourceSpelling } : undefined,
                ),
                capturedAtMs: performance.now(),
                restBeforeDivisions: 0,
                durationDivisions: movedDurationDivisions,
                startDivisions: movedStartDivisions,
              };

              return [remainingEvent, movedEvent];
            }

            const currentAverageMidi =
              event.midiNotes.reduce((sum, note) => sum + note, 0) /
              Math.max(event.midiNotes.length, 1);
            const pitchDelta = movedMidi - Math.round(currentAverageMidi);
            return {
              ...freezeAdaptiveEventTiming(event),
              source: event.source === 'audio' ? event.source : 'manual',
              midiNotes: normalizeMidiNotes(event.midiNotes.map((note) => note + pitchDelta)),
              accidentalSpellings: shiftAccidentalSpellings(event.accidentalSpellings, pitchDelta),
              restBeforeDivisions: 0,
              startDivisions: movedStartDivisions,
              durationDivisions: movedDurationDivisions,
            };
          }),
        ),
      );
      setSelectedEventIds([splitEventId ?? eventId]);
      setSelectedNoteTarget(
        sourceMidi != null || currentEventNotes.length === 1
          ? { eventId: splitEventId ?? eventId, midi: movedMidi }
          : null,
      );
    },
    [capturedEvents, flushPendingChord, isEditMode, stopPlayback],
  );

  const updateSelectedEventsDuration = useCallback(
    (durationDivisions: number) => {
      if (!isEditMode) return;

      setSelectedDurationDivisions(durationDivisions);
      if (selectedEventIds.length === 0) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();
      const selectedEventIdSet = new Set(selectedEventIds);
      setCapturedEvents((prev) =>
        sortDraftEvents(
          prev.map((event) =>
            selectedEventIdSet.has(event.id)
              ? {
                  ...freezeAdaptiveEventTiming(event),
                  durationDivisions: Math.max(MIN_EVENT_DURATION_DIVISIONS, durationDivisions),
                  endedAtMs: undefined,
                }
              : event,
          ),
        ),
      );
    },
    [flushPendingChord, isEditMode, selectedEventIds, stopPlayback],
  );

  const updateSelectedAccidentalSpelling = useCallback(
    (spelling: AccidentalSpelling) => {
      if (!isEditMode) return;
      if (selectedEventIds.length === 0) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const selectedEventIdSet = new Set(selectedEventIds);
      setCapturedEvents((prev) =>
        prev.map((event) => {
          if (!selectedEventIdSet.has(event.id)) return event;

          const targetNotes =
            selectedNoteTarget?.eventId === event.id
              ? normalizeMidiNotes(event.midiNotes).filter(
                  (midi) => midi === selectedNoteTarget.midi,
                )
              : normalizeMidiNotes(event.midiNotes);

          if (targetNotes.length === 0) return event;

          const nextSpellings: Record<number, AccidentalSpelling> = {
            ...(event.accidentalSpellings ?? {}),
          };
          targetNotes.forEach((midi) => {
            if (spelling === 'auto') {
              delete nextSpellings[midi];
              return;
            }
            nextSpellings[midi] = spelling;
          });

          return {
            ...event,
            accidentalSpellings: normalizeAccidentalSpellings(nextSpellings),
          };
        }),
      );
    },
    [flushPendingChord, isEditMode, selectedEventIds, selectedNoteTarget, stopPlayback],
  );

  const mergeDraftEvents = useCallback(
    (eventIds: string[]) => {
      if (!isEditMode) return;

      const uniqueEventIds = Array.from(new Set(eventIds));
      if (uniqueEventIds.length < 2) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const nextId = `live-composer-merged-${nextEventIdRef.current++}`;
      setCapturedEvents((prev) => {
        const selectedIdSet = new Set(uniqueEventIds);
        const timelineEvents = getDraftTimelineEvents(prev);
        const selectedEvents = timelineEvents.filter((event) => selectedIdSet.has(event.id));
        if (selectedEvents.length < 2) return prev;

        const startDivisions = Math.min(...selectedEvents.map((event) => event.startDivisions));
        const endDivisions = Math.max(
          ...selectedEvents.map((event) => event.startDivisions + event.durationDivisions),
        );
        const selectedStaffs = new Set(selectedEvents.map((event) => event.staff ?? 'split'));
        const mergedStaff =
          selectedStaffs.size === 1
            ? (selectedEvents[0].staff as StaffNumber | undefined)
            : undefined;
        const mergedAccidentalSpellings = normalizeAccidentalSpellings(
          selectedEvents.reduce<Record<number, AccidentalSpelling>>((spellings, event) => {
            normalizeMidiNotes(event.midiNotes).forEach((midi) => {
              const spelling = event.accidentalSpellings?.[midi];
              if (spelling && spelling !== 'auto') {
                spellings[midi] = spelling;
              }
            });
            return spellings;
          }, {}),
        );
        const mergedEvent: LiveComposerEvent = {
          id: nextId,
          midiNotes: normalizeMidiNotes(selectedEvents.flatMap((event) => event.midiNotes)),
          accidentalSpellings: mergedAccidentalSpellings,
          capturedAtMs: performance.now(),
          source: 'manual',
          restBeforeDivisions: 0,
          durationDivisions: Math.max(MIN_EVENT_DURATION_DIVISIONS, endDivisions - startDivisions),
          startDivisions,
          staff: mergedStaff,
        };

        return sortDraftEvents([
          ...timelineEvents.filter((event) => !selectedIdSet.has(event.id)),
          mergedEvent,
        ]);
      });
      setSelectedEventIds([nextId]);
      setSelectedNoteTarget(null);
    },
    [flushPendingChord, isEditMode, stopPlayback],
  );

  const handleEditorEventSelect = useCallback(
    (eventId: string, midi?: number | null, options?: EventSelectOptions) => {
      if (!isEditMode) return;

      if (options?.toggleSelection) {
        const selectedIdSet = new Set(selectedEventIds);
        if (selectedIdSet.has(eventId)) {
          selectedIdSet.delete(eventId);
        } else {
          selectedIdSet.add(eventId);
        }

        const nextSelectedEventIds = Array.from(selectedIdSet);
        setSelectedEventIds(nextSelectedEventIds);
        setSelectedNoteTarget(
          nextSelectedEventIds.length === 1 && nextSelectedEventIds[0] === eventId && midi != null
            ? { eventId, midi: Math.round(midi) }
            : null,
        );
        return;
      }

      const deselectEvent = selectedEventIds.length === 1 && selectedEventIds[0] === eventId;
      setSelectedEventIds(deselectEvent ? [] : [eventId]);
      setSelectedNoteTarget(
        !deselectEvent && midi != null ? { eventId, midi: Math.round(midi) } : null,
      );
    },
    [isEditMode, selectedEventIds],
  );

  const handleUndo = useCallback(() => {
    if (isPracticeMode) return;

    const previousSnapshot = undoStack[undoStack.length - 1];
    if (!previousSnapshot) return;

    flushPendingChord();
    setRedoStack((prev) =>
      [cloneDraftSnapshot(getCurrentDraftSnapshot()), ...prev].slice(0, DRAFT_HISTORY_LIMIT),
    );
    setUndoStack((prev) => prev.slice(0, -1));
    applyDraftSnapshot(previousSnapshot);
  }, [applyDraftSnapshot, flushPendingChord, getCurrentDraftSnapshot, isPracticeMode, undoStack]);

  const handleRedo = useCallback(() => {
    if (isPracticeMode) return;

    const nextSnapshot = redoStack[0];
    if (!nextSnapshot) return;

    flushPendingChord();
    setUndoStack((prev) =>
      [...prev, cloneDraftSnapshot(getCurrentDraftSnapshot())].slice(-DRAFT_HISTORY_LIMIT),
    );
    setRedoStack((prev) => prev.slice(1));
    applyDraftSnapshot(nextSnapshot);
  }, [applyDraftSnapshot, flushPendingChord, getCurrentDraftSnapshot, isPracticeMode, redoStack]);

  const handleDeleteSelectedEvents = useCallback(() => {
    if (!isEditMode) return;
    if (selectedEventIds.length === 0) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();
    const selectedIdSet = new Set(selectedEventIds);
    setCapturedEvents((prev) => prev.filter((event) => !selectedIdSet.has(event.id)));
    selectedEventIds.forEach((eventId) => {
      activeCapturedEventsRef.current.delete(eventId);
    });
    activeNoteOwnerRef.current.forEach((eventId, note) => {
      if (selectedIdSet.has(eventId)) {
        activeNoteOwnerRef.current.delete(note);
      }
    });
    setSelectedEventIds([]);
    setSelectedNoteTarget(null);
  }, [flushPendingChord, isEditMode, selectedEventIds, stopPlayback]);

  const handleDuplicateSelectedEvents = useCallback(() => {
    if (!isEditMode) return;
    if (selectedEventIds.length === 0) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();
    const selectedIdSet = new Set(selectedEventIds);
    const timelineEvents = getDraftTimelineEvents(capturedEvents);
    const selectedEvents = timelineEvents.filter((event) => selectedIdSet.has(event.id));
    if (selectedEvents.length === 0) return;

    const startDivisions = Math.min(...selectedEvents.map((event) => event.startDivisions));
    const endDivisions = Math.max(
      ...selectedEvents.map((event) => event.startDivisions + event.durationDivisions),
    );
    const duplicateOffset = Math.max(
      selectedDurationDivisions,
      endDivisions - startDivisions,
      MIN_EVENT_DURATION_DIVISIONS,
    );
    const duplicatedIds: string[] = [];
    const duplicatedEvents = selectedEvents.map((event, index) => {
      const nextId = `live-composer-copy-${nextEventIdRef.current++}`;
      duplicatedIds.push(nextId);

      return {
        ...freezeAdaptiveEventTiming(event),
        id: nextId,
        source: (event.source === 'audio' ? event.source : 'manual') as ComposerSource,
        midiNotes: [...event.midiNotes],
        accidentalSpellings: event.accidentalSpellings
          ? { ...event.accidentalSpellings }
          : undefined,
        capturedAtMs: performance.now() + index,
        restBeforeDivisions: 0,
        startDivisions: event.startDivisions + duplicateOffset,
        endedAtMs: undefined,
      };
    });

    setCapturedEvents((prev) => sortDraftEvents([...prev, ...duplicatedEvents]));
    setSelectedEventIds(duplicatedIds);
    setSelectedNoteTarget(null);
    setRecordingCursor(endDivisions + duplicateOffset);
  }, [
    capturedEvents,
    flushPendingChord,
    selectedDurationDivisions,
    selectedEventIds,
    setRecordingCursor,
    isEditMode,
    stopPlayback,
  ]);

  const insertNoteAtCursor = useCallback(() => {
    if (!isEditMode) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();

    const selectedNoteEvent =
      selectedNoteTarget == null
        ? null
        : selectedTimelineEvents.find((event) => event.id === selectedNoteTarget.eventId);
    const selectedEventWithNotes = selectedTimelineEvents.find((event) => !isDraftRestEvent(event));
    const insertMidi =
      selectedNoteTarget?.midi ??
      normalizeMidiNotes(
        selectedNoteEvent?.midiNotes ?? selectedEventWithNotes?.midiNotes ?? [],
      )[0] ??
      DEFAULT_MANUAL_NOTE_MIDI;
    const insertStaff =
      selectedNoteEvent?.staff ??
      selectedEventWithNotes?.staff ??
      (insertMidi >= STAFF_SPLIT_MIDI ? 1 : 2);
    const startDivisions = snapStartDivisionsToDurationBucket(
      recordingCursorDivisionsRef.current,
      selectedDurationDivisions,
      measureDurationDivisions,
    );
    const durationDivisions = Math.max(MIN_EVENT_DURATION_DIVISIONS, selectedDurationDivisions);
    const id = `live-composer-insert-${nextEventIdRef.current++}`;
    const event: LiveComposerEvent = {
      id,
      midiNotes: [insertMidi],
      capturedAtMs: performance.now(),
      source: 'manual',
      restBeforeDivisions: 0,
      durationDivisions,
      startDivisions,
      staff: insertStaff,
    };

    setCapturedEvents((prev) => sortDraftEvents([...prev, event]));
    setSelectedEventIds([id]);
    setSelectedNoteTarget({ eventId: id, midi: insertMidi });
    setRecordingCursor(startDivisions + durationDivisions);
  }, [
    flushPendingChord,
    isEditMode,
    measureDurationDivisions,
    selectedDurationDivisions,
    selectedNoteTarget,
    selectedTimelineEvents,
    setRecordingCursor,
    stopPlayback,
  ]);

  const insertRestAtCursor = useCallback(() => {
    if (!isEditMode) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();

    const startDivisions = snapStartDivisionsToDurationBucket(
      recordingCursorDivisionsRef.current,
      selectedDurationDivisions,
      measureDurationDivisions,
    );
    const durationDivisions = Math.max(MIN_EVENT_DURATION_DIVISIONS, selectedDurationDivisions);
    const id = `live-composer-rest-${nextEventIdRef.current++}`;
    const restEvent: LiveComposerEvent = {
      id,
      midiNotes: [],
      capturedAtMs: performance.now(),
      source: 'manual',
      restBeforeDivisions: 0,
      durationDivisions,
      startDivisions,
      staff: undefined,
    };

    setCapturedEvents((prev) =>
      sortDraftEvents([
        ...getDraftTimelineEvents(prev).map((event) =>
          event.startDivisions >= startDivisions
            ? {
                ...freezeAdaptiveEventTiming(event),
                restBeforeDivisions: 0,
                startDivisions: event.startDivisions + durationDivisions,
              }
            : event,
        ),
        restEvent,
      ]),
    );
    setCapturedPedalEvents((prev) =>
      prev.map((event) =>
        event.positionDivisions >= startDivisions
          ? {
              ...requantizeAdaptivePedalForTempo(event, captureTempoRef.current),
              adaptiveTiming: false,
              rawCaptureSessionStartedAtMs: undefined,
              rawCaptureSessionStartDivisions: undefined,
              positionDivisions: event.positionDivisions + durationDivisions,
            }
          : event,
      ),
    );
    setSelectedEventIds([id]);
    setSelectedNoteTarget(null);
    setRecordingCursor(startDivisions + durationDivisions);
  }, [
    flushPendingChord,
    isEditMode,
    measureDurationDivisions,
    selectedDurationDivisions,
    setRecordingCursor,
    stopPlayback,
  ]);

  const nudgeSelectedEvents = useCallback(
    (direction: -1 | 1) => {
      if (!isEditMode) return;
      if (selectedEventIds.length === 0) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const selectedIdSet = new Set(selectedEventIds);
      const delta = direction * MIN_EVENT_DURATION_DIVISIONS;
      const nextCursorDivisions =
        selectedTimelineEvents.length > 0
          ? Math.min(
              ...selectedTimelineEvents.map((event) => Math.max(0, event.startDivisions + delta)),
            )
          : null;
      setCapturedEvents((prev) =>
        sortDraftEvents(
          getDraftTimelineEvents(prev).map((event) => {
            if (!selectedIdSet.has(event.id)) return event;
            const startDivisions = Math.max(0, event.startDivisions + delta);
            return {
              ...freezeAdaptiveEventTiming(event),
              restBeforeDivisions: 0,
              startDivisions,
            };
          }),
        ),
      );
      if (nextCursorDivisions != null) {
        setRecordingCursor(nextCursorDivisions);
      }
    },
    [
      flushPendingChord,
      isEditMode,
      selectedEventIds,
      selectedTimelineEvents,
      setRecordingCursor,
      stopPlayback,
    ],
  );

  const transposeSelectedEvents = useCallback(
    (semitones: -1 | 1) => {
      if (!isEditMode) return;
      if (selectedEventIds.length === 0) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const selectedIdSet = new Set(selectedEventIds);
      const selectedTarget = selectedNoteTarget;
      const nextSelectedNoteTarget =
        selectedTarget && selectedIdSet.has(selectedTarget.eventId)
          ? {
              eventId: selectedTarget.eventId,
              midi: Math.max(0, Math.min(127, selectedTarget.midi + semitones)),
            }
          : null;

      setCapturedEvents((prev) =>
        sortDraftEvents(
          prev.map((event) => {
            if (!selectedIdSet.has(event.id) || isDraftRestEvent(event)) return event;

            const normalizedNotes = normalizeMidiNotes(event.midiNotes);
            const targetOnly =
              selectedTarget?.eventId === event.id && normalizedNotes.includes(selectedTarget.midi);
            const targetNotes = targetOnly ? [selectedTarget.midi] : normalizedNotes;
            const nextSpellings: Record<number, AccidentalSpelling> = {
              ...(event.accidentalSpellings ?? {}),
            };
            const nextNotes = normalizeMidiNotes(
              normalizedNotes.map((midi) => {
                if (!targetNotes.includes(midi)) return midi;
                const nextMidi = Math.max(0, Math.min(127, midi + semitones));
                const spelling = nextSpellings[midi];
                delete nextSpellings[midi];
                if (spelling && spelling !== 'auto') {
                  nextSpellings[nextMidi] = spelling;
                }
                return nextMidi;
              }),
            );

            return {
              ...freezeAdaptiveEventTiming(event),
              source: event.source === 'audio' ? event.source : 'manual',
              midiNotes: nextNotes,
              accidentalSpellings: normalizeAccidentalSpellings(nextSpellings),
            };
          }),
        ),
      );

      if (selectedTarget) {
        setSelectedNoteTarget(nextSelectedNoteTarget);
      }
    },
    [flushPendingChord, isEditMode, selectedEventIds, selectedNoteTarget, stopPlayback],
  );

  const moveSelectedEventsToStaff = useCallback(
    (staff?: StaffNumber) => {
      if (!isEditMode) return;
      if (selectedEventIds.length === 0) return;

      stopPlayback({ resetProgress: true, hideCursor: true });
      flushPendingChord();

      const selectedIdSet = new Set(selectedEventIds);
      setCapturedEvents((prev) =>
        sortDraftEvents(
          prev.map((event) =>
            selectedIdSet.has(event.id)
              ? {
                  ...freezeAdaptiveEventTiming(event),
                  source: event.source === 'audio' ? event.source : 'manual',
                  staff,
                }
              : event,
          ),
        ),
      );
    },
    [flushPendingChord, isEditMode, selectedEventIds, stopPlayback],
  );

  const splitSelectedEvents = useCallback(() => {
    if (!isEditMode) return;
    if (selectedEventIds.length === 0) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();

    const selectedIdSet = new Set(selectedEventIds);
    const splitIdByEventId = new Map(
      selectedTimelineEvents
        .filter((event) => event.durationDivisions > MIN_EVENT_DURATION_DIVISIONS)
        .map((event) => [event.id, `live-composer-split-${nextEventIdRef.current++}`]),
    );
    const nextSelectedEventIds = selectedTimelineEvents.flatMap((event) => {
      const splitId = splitIdByEventId.get(event.id);
      return splitId ? [event.id, splitId] : [event.id];
    });

    setCapturedEvents((prev) =>
      sortDraftEvents(
        getDraftTimelineEvents(prev).flatMap((event) => {
          const secondId = splitIdByEventId.get(event.id);
          if (!selectedIdSet.has(event.id) || !secondId) {
            return event;
          }

          const firstDuration = Math.max(
            MIN_EVENT_DURATION_DIVISIONS,
            Math.floor(event.durationDivisions / 2),
          );
          const secondDuration = Math.max(
            MIN_EVENT_DURATION_DIVISIONS,
            event.durationDivisions - firstDuration,
          );
          return [
            {
              ...freezeAdaptiveEventTiming(event),
              source: event.source === 'audio' ? event.source : 'manual',
              restBeforeDivisions: 0,
              durationDivisions: firstDuration,
              endedAtMs: undefined,
            },
            {
              ...freezeAdaptiveEventTiming(event),
              id: secondId,
              source: event.source === 'audio' ? event.source : 'manual',
              capturedAtMs: performance.now(),
              restBeforeDivisions: 0,
              startDivisions: event.startDivisions + firstDuration,
              durationDivisions: secondDuration,
              endedAtMs: undefined,
              accidentalSpellings: event.accidentalSpellings
                ? { ...event.accidentalSpellings }
                : undefined,
              midiNotes: [...event.midiNotes],
            },
          ];
        }),
      ),
    );
    setSelectedEventIds(nextSelectedEventIds);
    setSelectedNoteTarget(null);
  }, [flushPendingChord, isEditMode, selectedEventIds, selectedTimelineEvents, stopPlayback]);

  const getDraftEventsSnapshot = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (!pendingChord) return capturedEvents;

    const midiNotes = getPendingChordNotes(pendingChord);
    if (midiNotes.length === 0) return capturedEvents;

    return [
      ...capturedEvents,
      {
        id: 'live-composer-pending',
        midiNotes,
        capturedAtMs: pendingChord.startedAtMs,
        source: pendingChord.source,
        restBeforeDivisions: 0,
        durationDivisions: MIN_EVENT_DURATION_DIVISIONS,
        startDivisions: pendingChord.startDivisions,
        staff: pendingChord.staff,
        adaptiveTiming: true,
        rawStartedAtMs: pendingChord.startedAtMs,
        rawCaptureSessionStartedAtMs: pendingChord.rawCaptureSessionStartedAtMs,
        rawCaptureSessionStartDivisions: pendingChord.rawCaptureSessionStartDivisions,
        endedAtMs: undefined,
      },
    ];
  }, [capturedEvents]);

  const getDraftPedalEventsSnapshot = useCallback(() => capturedPedalEvents, [capturedPedalEvents]);

  const handleExportMusicXml = useCallback(() => {
    const noteEventsToExport = getDraftEventsSnapshot();
    const pedalEventsToExport = getDraftPedalEventsSnapshot();
    flushPendingChord();
    const exportTitle =
      draftTitle === DEFAULT_DRAFT_TITLE ? 'MuseFlow Repertoire Draft' : draftTitle;
    const musicXml = buildLiveComposerMusicXml(
      noteEventsToExport,
      pedalEventsToExport,
      selectedTimeSignature,
      {
        title: exportTitle,
        composer: CREATED_REPERTOIRE_AUTHOR,
      },
    ).xml;
    const blob = new Blob([musicXml], { type: 'application/vnd.recordare.musicxml+xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getDraftFileName(exportTitle);
    link.click();
    window.URL.revokeObjectURL(url);
  }, [
    draftTitle,
    flushPendingChord,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    selectedTimeSignature,
  ]);

  const getDraftEventsSignature = useCallback(
    (events: LiveComposerEvent[], pedalEvents: LiveComposerPedalEvent[]) =>
      `${events.length}:${events
        .map(
          (event) =>
            `${event.id}:${event.startDivisions ?? ''}:${event.restBeforeDivisions}:${
              event.durationDivisions
            }:${event.staff ?? 'split'}:${event.midiNotes.join(',')}:${Object.entries(
              event.accidentalSpellings ?? {},
            )
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([midi, spelling]) => `${midi}=${spelling}`)
              .join(',')}`,
        )
        .join('|')}::${pedalEvents
        .map((event) => `${event.id}:${event.positionDivisions}:${event.pedalDown ? 1 : 0}`)
        .join('|')}`,
    [],
  );

  const handleRequestClear = useCallback(() => {
    if (isPracticeMode) return;
    setClearConfirmOpen(true);
  }, [isPracticeMode]);

  const handleCloseClearConfirm = useCallback(() => {
    setClearConfirmOpen(false);
  }, []);

  const handleClear = useCallback(() => {
    if (isPracticeMode) return;

    stopPlayback({ resetProgress: true, hideCursor: true });
    clearPendingChord();
    clearActiveCaptureState();
    skipNextHistoryCaptureRef.current = true;
    setCapturedEvents([]);
    setCapturedPedalEvents([]);
    setUndoStack([]);
    setRedoStack([]);
    setSelectedEventIds([]);
    setSelectedNoteTarget(null);
    setRecordingCursor(0);
    setDraftTitle(DEFAULT_DRAFT_TITLE);
    setSaveSuccessRepertoire(null);
    setClearConfirmOpen(false);
    clearStoredDraftSnapshot();
    recentAudioOnsetsRef.current.clear();
    pendingAudioOnsetBurstRef.current = null;
    pendingAudioSparseOnsetUntilMsRef.current = 0;
    lastAudioCaptureCandidatesRef.current = [];
    latestAudioFrameCaptureCandidatesRef.current = [];
    resetAdaptiveTempoLearning();
  }, [
    clearActiveCaptureState,
    clearPendingChord,
    resetAdaptiveTempoLearning,
    setRecordingCursor,
    isPracticeMode,
    stopPlayback,
  ]);

  const handleOpenSaveModal = useCallback(() => {
    setSaveTitleInput(draftTitle === DEFAULT_DRAFT_TITLE ? '' : draftTitle);
    setSaveError('');
    setSaveModalOpen(true);
  }, [draftTitle]);

  const handleCloseSaveModal = useCallback(() => {
    if (isSavingDraft) return;
    setSaveModalOpen(false);
    setSaveError('');
  }, [isSavingDraft]);

  const handleSaveDraft = useCallback(async () => {
    const title = saveTitleInput.trim();
    if (!title) {
      setSaveError('Enter a title to save this repertoire.');
      return;
    }

    const noteEventsToSave = getDraftEventsSnapshot();
    const pedalEventsToSave = getDraftPedalEventsSnapshot();
    flushPendingChord();

    setIsSavingDraft(true);
    setSaveError('');

    try {
      const musicXml = buildLiveComposerMusicXml(
        noteEventsToSave,
        pedalEventsToSave,
        selectedTimeSignature,
        {
          title,
          composer: CREATED_REPERTOIRE_AUTHOR,
        },
      ).xml;
      const token = await currentAuthToken();
      const formData = new FormData();
      const file = new File([musicXml], getDraftFileName(title), {
        type: 'application/vnd.recordare.musicxml+xml',
      });

      formData.append('name', title);
      formData.append('source', CREATED_REPERTOIRE_SOURCE);
      formData.append('composers', JSON.stringify([{ name: CREATED_REPERTOIRE_AUTHOR }]));
      formData.append('musicXml', file);

      const response = await axios.post<UserRepertoireData>(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      const savedRepertoire = {
        ...response.data,
        name: response.data.name || title,
        source: CREATED_REPERTOIRE_SOURCE,
        composers:
          response.data.composers && response.data.composers.length > 0
            ? response.data.composers
            : [{ id: 0, name: CREATED_REPERTOIRE_AUTHOR }],
        artists: response.data.artists ?? [],
        arrangers: response.data.arrangers ?? [],
        genres: response.data.genres ?? [],
        status: response.data.status ?? 'processing',
      } as UserRepertoireData;

      setDraftTitle(savedRepertoire.name);
      dispatch(
        repertoireActions.fetchUserRepertoireSuccess(
          upsertById(userRepertoireData, savedRepertoire),
        ),
      );
      dispatch(
        repertoireActions.fetchRepertoireDataSuccess(
          upsertById(repertoireData, savedRepertoire as unknown as RepertoireData),
        ),
      );
      setSaveModalOpen(false);
      setSaveSuccessRepertoire(savedRepertoire);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setSaveError(message || 'Failed to save repertoire.');
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    currentAuthToken,
    dispatch,
    flushPendingChord,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    repertoireData,
    saveTitleInput,
    selectedTimeSignature,
    userRepertoireData,
  ]);

  const handlePracticeSavedRepertoire = useCallback(() => {
    if (!saveSuccessRepertoire) return;

    const savedRepertoire = saveSuccessRepertoire as unknown as RepertoireData;
    dispatch(
      repertoireActions.fetchRepertoireDataSuccess(upsertById(repertoireData, savedRepertoire)),
    );
    dispatch(
      repertoireActions.setRepertoireSelect({ repertoireIdSelect: saveSuccessRepertoire.id }),
    );
    dispatch(appActions.setRepPageSelected('Home'));
    setSaveSuccessRepertoire(null);
    navigate('/repertoire-play');
  }, [dispatch, navigate, repertoireData, saveSuccessRepertoire]);

  const triggerPlaybackAttack = useCallback(
    (midiNotes: number[], velocity: number) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const synth = sampler ? null : ensurePlaybackSynth();
      const noteNames = midiNotes.map((note) => midiToName(note));

      if (sampler) {
        noteNames.forEach((note) => {
          sampler.triggerAttack(note, Tone.immediate(), velocity);
        });
        return;
      }

      const frequencies = midiNotes.map((note) => Tone.Frequency(note, 'midi').toFrequency());
      frequencies.forEach((frequency) => {
        synth?.triggerAttack(frequency, Tone.immediate(), velocity);
      });
    },
    [ensurePlaybackSynth],
  );

  const triggerPlaybackRelease = useCallback(
    (midiNotes: number[]) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const synth = sampler ? null : ensurePlaybackSynth();
      const noteNames = midiNotes.map((note) => midiToName(note));

      if (sampler) {
        noteNames.forEach((note) => {
          sampler.triggerRelease(note, Tone.immediate());
        });
        return;
      }

      const frequencies = midiNotes.map((note) => Tone.Frequency(note, 'midi').toFrequency());
      frequencies.forEach((frequency) => {
        synth?.triggerRelease(frequency, Tone.immediate());
      });
    },
    [ensurePlaybackSynth],
  );

  const releasePlaybackSustainedNotes = useCallback(() => {
    const sustainedNotes = Array.from(playbackSustainedNotesRef.current);
    if (sustainedNotes.length === 0) return;
    triggerPlaybackRelease(sustainedNotes);
    playbackSustainedNotesRef.current.clear();
  }, [triggerPlaybackRelease]);

  const buildPlaybackInstructions = useCallback(
    (noteEvents: LiveComposerEvent[], pedalEvents: LiveComposerPedalEvent[]) => {
      const instructions: PlaybackInstruction[] = [];

      getDraftTimelineEvents(noteEvents).forEach((event) => {
        instructions.push({
          id: `${event.id}:on`,
          atDivisions: event.startDivisions,
          type: 'note-on',
          midiNotes: event.midiNotes,
          source: event.source,
        });
        instructions.push({
          id: `${event.id}:off`,
          atDivisions:
            event.startDivisions + Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions),
          type: 'note-off',
          midiNotes: event.midiNotes,
          source: event.source,
        });
      });

      pedalEvents.forEach((event) => {
        instructions.push({
          id: event.id,
          atDivisions: Math.max(0, event.positionDivisions),
          type: 'pedal',
          pedalDown: event.pedalDown,
        });
      });

      const priority = (instruction: PlaybackInstruction) => {
        if (instruction.type === 'pedal' && instruction.pedalDown) return 0;
        if (instruction.type === 'pedal' && !instruction.pedalDown) return 1;
        if (instruction.type === 'note-on') return 2;
        return 3;
      };

      return instructions.sort(
        (a, b) => a.atDivisions - b.atDivisions || priority(a) - priority(b),
      );
    },
    [],
  );

  const playInstructionAtIndex = useCallback(
    async (instructionIndex: number) => {
      const instructions = playbackInstructionsRef.current;
      if (instructionIndex >= instructions.length) {
        playbackNextInstructionIndexRef.current = instructions.length;
        playbackResumeDelayMsRef.current = 0;
        playbackNextInstructionDueAtMsRef.current = null;
        playbackNextInstructionTimeoutRef.current = null;
        playbackPedalDownRef.current = false;
        releasePlaybackSustainedNotes();
        setIsPlayingBack(false);
        return;
      }

      const instruction = instructions[instructionIndex];

      if (instruction.type === 'pedal') {
        playbackPedalDownRef.current = instruction.pedalDown;
        if (!instruction.pedalDown) {
          releasePlaybackSustainedNotes();
        }
      } else if (instruction.type === 'note-on') {
        instruction.midiNotes.forEach((note) => {
          if (playbackSustainedNotesRef.current.has(note)) {
            triggerPlaybackRelease([note]);
            playbackSustainedNotesRef.current.delete(note);
          }
        });
        const velocity = instruction.source === 'audio' ? 0.82 : 0.92;
        triggerPlaybackAttack(instruction.midiNotes, velocity);
      } else {
        if (playbackPedalDownRef.current) {
          instruction.midiNotes.forEach((note) => playbackSustainedNotesRef.current.add(note));
        } else {
          triggerPlaybackRelease(instruction.midiNotes);
        }
      }

      playbackNextInstructionIndexRef.current = instructionIndex + 1;

      if (instructionIndex + 1 >= instructions.length) {
        playbackResumeDelayMsRef.current = 0;
        playbackNextInstructionDueAtMsRef.current = null;
        playbackNextInstructionTimeoutRef.current = null;
        playbackPedalDownRef.current = false;
        releasePlaybackSustainedNotes();
        setIsPlayingBack(false);
        return;
      }

      const nextInstruction = instructions[instructionIndex + 1];
      const eventDurationMs = Math.round(
        Math.max(0, nextInstruction.atDivisions - instruction.atDivisions) *
          getDivisionDurationSeconds(playbackTempoRef.current) *
          1000,
      );
      playbackResumeDelayMsRef.current = eventDurationMs;
      playbackNextInstructionDueAtMsRef.current = performance.now() + eventDurationMs;
      playbackNextInstructionTimeoutRef.current = window.setTimeout(() => {
        void playInstructionAtIndex(playbackNextInstructionIndexRef.current);
      }, eventDurationMs);
    },
    [
      getDivisionDurationSeconds,
      releasePlaybackSustainedNotes,
      triggerPlaybackAttack,
      triggerPlaybackRelease,
    ],
  );

  const startPlayback = useCallback(
    async ({
      noteEventsToPlay,
      pedalEventsToPlay,
      startDivisions,
      fromStart,
    }: {
      noteEventsToPlay: LiveComposerEvent[];
      pedalEventsToPlay: LiveComposerPedalEvent[];
      startDivisions: number;
      fromStart: boolean;
    }) => {
      const instructions = buildPlaybackInstructions(noteEventsToPlay, pedalEventsToPlay);
      if (instructions.length === 0) return;
      const playbackStartDivisions = Math.max(0, Math.round(startDivisions));
      const firstInstructionIndexFromCursor = instructions.findIndex(
        (instruction) => instruction.atDivisions >= playbackStartDivisions,
      );
      if (fromStart && firstInstructionIndexFromCursor < 0) {
        setPlaybackCursorStartDivisions(playbackStartDivisions);
        setPlaybackSessionKey((key) => key + 1);
        setShowPlaybackCursor(true);
        setIsPlayingBack(false);
        return;
      }

      await Tone.start();
      const playbackWindow = window as PlaybackSamplerWindow;
      await playbackWindow.__ensureSamplerReady?.();

      playbackInstructionsRef.current = instructions;
      playbackSnapshotSignatureRef.current = getDraftEventsSignature(
        noteEventsToPlay,
        pedalEventsToPlay,
      );
      playbackPedalDownRef.current = false;
      playbackSustainedNotesRef.current.clear();

      if (fromStart || playbackNextInstructionIndexRef.current >= instructions.length) {
        playbackNextInstructionIndexRef.current = Math.max(0, firstInstructionIndexFromCursor);
        playbackResumeDelayMsRef.current = 0;
        setPlaybackCursorStartDivisions(playbackStartDivisions);
        setPlaybackSessionKey((key) => key + 1);
        setShowPlaybackCursor(true);
      }

      setIsPlayingBack(true);

      const nextInstructionIndex = playbackNextInstructionIndexRef.current;
      const initialDelayMs = fromStart
        ? Math.round(
            Math.max(0, instructions[nextInstructionIndex].atDivisions - playbackStartDivisions) *
              getDivisionDurationSeconds(playbackTempoRef.current) *
              1000,
          )
        : 0;
      const resumeDelayMs = fromStart
        ? initialDelayMs
        : Math.max(0, playbackResumeDelayMsRef.current);

      if (resumeDelayMs === 0) {
        await playInstructionAtIndex(nextInstructionIndex);
        return;
      }

      playbackNextInstructionDueAtMsRef.current = performance.now() + resumeDelayMs;
      playbackNextInstructionTimeoutRef.current = window.setTimeout(() => {
        void playInstructionAtIndex(playbackNextInstructionIndexRef.current);
      }, resumeDelayMs);
    },
    [
      buildPlaybackInstructions,
      getDivisionDurationSeconds,
      getDraftEventsSignature,
      playInstructionAtIndex,
    ],
  );

  const handlePlaybackToggle = useCallback(async () => {
    const noteEventsToPlay = getDraftEventsSnapshot();
    const pedalEventsToPlay = getDraftPedalEventsSnapshot();
    flushPendingChord();

    if (isPlayingBack) {
      stopPlayback();
      return;
    }

    await startPlayback({
      noteEventsToPlay,
      pedalEventsToPlay,
      startDivisions: recordingCursorDivisionsRef.current,
      fromStart: true,
    });
  }, [
    flushPendingChord,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    isPlayingBack,
    startPlayback,
    stopPlayback,
  ]);

  const handlePlaybackRestart = useCallback(async () => {
    const noteEventsToPlay = getDraftEventsSnapshot();
    const pedalEventsToPlay = getDraftPedalEventsSnapshot();
    flushPendingChord();
    stopPlayback({ resetProgress: true });
    await startPlayback({
      noteEventsToPlay,
      pedalEventsToPlay,
      startDivisions: recordingCursorDivisionsRef.current,
      fromStart: true,
    });
  }, [
    flushPendingChord,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    startPlayback,
    stopPlayback,
  ]);

  const handleMetronomeToggle = useCallback(() => {
    void Tone.start();
    setMetronomeEnabled((enabled) => !enabled);
  }, []);

  useEffect(() => {
    setRecognitionHints(inputMode === 'audio' ? { mode: 'free-capture' } : null);
    return () => {
      setRecognitionHints(null);
    };
  }, [inputMode, setRecognitionHints]);

  useEffect(() => {
    if (!hasInitializedInputModeRef.current) {
      hasInitializedInputModeRef.current = true;
      void applyInputMode(preferredInputMode);
      return;
    }
    if (preferredInputMode === inputMode) return;
    void applyInputMode(preferredInputMode);
  }, [applyInputMode, inputMode, preferredInputMode]);

  useEffect(() => {
    const setMergeKeyState = (event: KeyboardEvent, active: boolean) => {
      if (event.key === 'Shift' || event.key.toLowerCase() === 'm') {
        mergeKeyActiveRef.current = active;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => setMergeKeyState(event, true);
    const handleKeyUp = (event: KeyboardEvent) => setMergeKeyState(event, false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      mergeKeyActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      const element = target instanceof HTMLElement ? target : null;
      if (!element) return false;
      const tagName = element.tagName.toLowerCase();
      return (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        element.isContentEditable
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;

      const shortcutKey = event.key.toLowerCase();
      const commandPressed = event.metaKey || event.ctrlKey;

      if (commandPressed && shortcutKey === 'z') {
        if (isPracticeMode) return;
        event.preventDefault();
        if (event.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }

      if (commandPressed && shortcutKey === 'y') {
        if (isPracticeMode) return;
        event.preventDefault();
        handleRedo();
        return;
      }

      if (commandPressed && shortcutKey === 'd') {
        if (!isEditMode) return;
        event.preventDefault();
        handleDuplicateSelectedEvents();
        return;
      }

      if (commandPressed && shortcutKey === 's') {
        event.preventDefault();
        handleOpenSaveModal();
        return;
      }

      if (commandPressed && shortcutKey === 'e') {
        event.preventDefault();
        handleExportMusicXml();
        return;
      }

      if (!commandPressed && isEditMode && selectedEventIds.length > 0) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          nudgeSelectedEvents(-1);
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          nudgeSelectedEvents(1);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          transposeSelectedEvents(1);
          return;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          transposeSelectedEvents(-1);
          return;
        }
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (!isEditMode) return;
        if (selectedEventIds.length === 0) return;
        event.preventDefault();
        handleDeleteSelectedEvents();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleDeleteSelectedEvents,
    handleDuplicateSelectedEvents,
    handleExportMusicXml,
    handleOpenSaveModal,
    handleRedo,
    handleUndo,
    isEditMode,
    isPracticeMode,
    nudgeSelectedEvents,
    selectedEventIds.length,
    transposeSelectedEvents,
  ]);

  useEffect(() => {
    captureSourceReadyRef.current = {
      midi: canCaptureMidi,
      audio: canCaptureAudio,
    };

    if (!canCaptureAudio) {
      recentAudioOnsetsRef.current.clear();
      audioStartRequestRef.current += 1;
      void stop();
    }

    if (!canCaptureMidi && !canCaptureAudio) {
      clearPendingChord();
      clearActiveCaptureState();
      resetAdaptiveTempoLearning();
    }
  }, [
    canCaptureAudio,
    canCaptureMidi,
    clearActiveCaptureState,
    clearPendingChord,
    resetAdaptiveTempoLearning,
    stop,
  ]);

  useEffect(() => {
    if (!canCaptureMidi) return;

    const listener: Listener = {
      name: MIDI_LISTENER_NAME,
      noteOn: (event) => {
        captureInputNotes([event.note], performance.now(), 'midi');
      },
      noteOff: (event) => {
        releaseCapturedNote(event.note, performance.now(), 'midi');
      },
      pedalChange: (event: PedalMessage) => {
        registerPedalEvent(event.down, performance.now());
      },
    };

    addMidiListener(listener);

    return () => {
      removeMidiListener(listener);
    };
  }, [
    addMidiListener,
    canCaptureMidi,
    captureInputNotes,
    registerPedalEvent,
    releaseCapturedNote,
    removeMidiListener,
  ]);

  useEffect(() => {
    if (!canCaptureAudio) return;

    const requestId = ++audioStartRequestRef.current;
    void start().catch(() => {
      if (audioStartRequestRef.current !== requestId) return;
    });

    const listener = {
      name: AUDIO_LISTENER_NAME,
      onFrame: (frame: RecognitionFrame) => {
        const frameTimeMs = frame.timeSec * 1000;
        const hasOnsetEvidence = hasAudioCaptureOnsetEvidence(frame);
        let pendingAudioOnsetBurst = pendingAudioOnsetBurstRef.current;
        if (hasOnsetEvidence && !pendingAudioOnsetBurst) {
          pendingAudioOnsetBurst = {
            startedAtMs: frameTimeMs,
            deadlineMs: frameTimeMs + AUDIO_SPARSE_ONSET_WAIT_MS,
            observations: [],
          };
          pendingAudioOnsetBurstRef.current = pendingAudioOnsetBurst;
        }
        if (pendingAudioOnsetBurst) {
          pendingAudioSparseOnsetUntilMsRef.current = pendingAudioOnsetBurst.deadlineMs;
        } else if (hasOnsetEvidence) {
          pendingAudioSparseOnsetUntilMsRef.current = frameTimeMs + AUDIO_SPARSE_ONSET_WAIT_MS;
        }
        const frameCaptureCandidates = selectAudioCaptureCandidates(frame, {
          allowFreeCaptureSustainSelection:
            frame.recognitionMode === 'free-capture' &&
            frameTimeMs <= pendingAudioSparseOnsetUntilMsRef.current,
        });
        if (
          pendingAudioOnsetBurst &&
          frameTimeMs <= pendingAudioOnsetBurst.deadlineMs &&
          frameCaptureCandidates.length > 0
        ) {
          pendingAudioOnsetBurst.observations.push(
            ...frameCaptureCandidates.map((candidate) => ({
              candidate,
              observedAtMs: frameTimeMs,
            })),
          );
        }
        let captureCandidates =
          frame.recognitionMode === 'free-capture' ? [] : frameCaptureCandidates;
        let captureTimeMs = frameTimeMs;
        if (pendingAudioOnsetBurst && frameTimeMs >= pendingAudioOnsetBurst.deadlineMs) {
          captureCandidates = selectAudioCaptureConsensusCandidates(
            pendingAudioOnsetBurst.observations,
          );
          captureTimeMs = pendingAudioOnsetBurst.startedAtMs;
          pendingAudioOnsetBurstRef.current = null;
          pendingAudioSparseOnsetUntilMsRef.current = 0;
        }
        const onsetMidiNotes = captureCandidates.map((candidate) => candidate.midi);
        const onsetScores = new Map(
          captureCandidates.map((candidate) => [candidate.midi, getAudioCaptureScore(candidate)]),
        );
        if (captureCandidates.length > 0) {
          pendingAudioSparseOnsetUntilMsRef.current = 0;
          lastAudioCaptureCandidatesRef.current = captureCandidates;
        }
        latestAudioFrameCaptureCandidatesRef.current = captureCandidates;
        const stableMidiNotes = new Set(getAudioFrameStableMidiNotes(frame));
        const stableNotesAreAudible = canAudioFrameSustainActiveNotes(frame);

        if (onsetMidiNotes.length > 0) {
          captureInputNotes(onsetMidiNotes, captureTimeMs, 'audio', onsetScores);
        }

        activeCapturedEventsRef.current.forEach((activeEvent) => {
          if (activeEvent.source !== 'audio') return;

          activeEvent.allNotes.forEach((note) => {
            if (stableNotesAreAudible && stableMidiNotes.has(note)) {
              audioMissingSinceRef.current.delete(note);
              return;
            }

            const missingSince = audioMissingSinceRef.current.get(note);
            if (missingSince == null) {
              audioMissingSinceRef.current.set(note, frameTimeMs);
              return;
            }

            if (frameTimeMs - missingSince >= AUDIO_RELEASE_GRACE_MS) {
              releaseCapturedNote(note, frameTimeMs, 'audio');
            }
          });
        });
      },
    };

    addAudioListener(listener);

    return () => {
      audioStartRequestRef.current += 1;
      pendingAudioOnsetBurstRef.current = null;
      pendingAudioSparseOnsetUntilMsRef.current = 0;
      latestAudioFrameCaptureCandidatesRef.current = [];
      removeAudioListener(listener);
    };
  }, [
    addAudioListener,
    canCaptureAudio,
    captureInputNotes,
    releaseCapturedNote,
    removeAudioListener,
    start,
  ]);

  useEffect(() => {
    if (!canCaptureAudio || !audioDiagnosticsVisible) return;

    const listener = {
      name: `${AUDIO_LISTENER_NAME}-diagnostics`,
      wantsWaveform: true,
      onFrame: (frame: RecognitionFrame) => {
        recordAudioDiagnosticsFrame(frame, latestAudioFrameCaptureCandidatesRef.current);
      },
    };

    addAudioListener(listener);

    return () => {
      removeAudioListener(listener);
    };
  }, [
    addAudioListener,
    audioDiagnosticsVisible,
    canCaptureAudio,
    recordAudioDiagnosticsFrame,
    removeAudioListener,
  ]);

  useEffect(() => {
    return () => {
      audioStartRequestRef.current += 1;
      stopPlayback({ resetProgress: true, hideCursor: true });
      stopMetronome();
      clearPendingChord();
      clearActiveCaptureState();
      void stop();
      playbackSynthRef.current?.dispose();
      playbackSynthRef.current = null;
      const disposeMetronomeSamples = (samples: MetronomeSamples) => {
        samples.downBeatSample.dispose();
        samples.upBeatSample.dispose();
        if (metronomeSamplesRef.current === samples) {
          metronomeSamplesRef.current = null;
        }
      };
      if (metronomeSamplesRef.current) {
        disposeMetronomeSamples(metronomeSamplesRef.current);
      } else {
        void metronomeSamplesPromiseRef.current
          ?.then(disposeMetronomeSamples)
          .catch(() => undefined);
      }
      metronomeSamplesPromiseRef.current = null;
    };
  }, [clearActiveCaptureState, clearPendingChord, stop, stopMetronome, stopPlayback]);

  const recentCaptures = useMemo<RecentCaptureItem[]>(
    () =>
      [
        ...capturedEvents.map((event) => ({
          id: event.id,
          capturedAtMs: event.capturedAtMs,
          source: event.source,
          label: formatChordLabel(event.midiNotes),
        })),
        ...capturedPedalEvents.map((event) => ({
          id: event.id,
          capturedAtMs: event.capturedAtMs,
          source: event.source,
          label: event.pedalDown ? PEDAL_LABEL_DOWN : PEDAL_LABEL_UP,
        })),
      ]
        .sort((a, b) => b.capturedAtMs - a.capturedAtMs)
        .slice(0, 8),
    [capturedEvents, capturedPedalEvents],
  );
  const scoreBuild = useMemo(
    () => buildLiveComposerMusicXml(capturedEvents, capturedPedalEvents, selectedTimeSignature),
    [capturedEvents, capturedPedalEvents, selectedTimeSignature],
  );
  const recordingCursorLabel = formatRecordingCursorLabel(
    recordingCursorDivisions,
    measureDurationDivisions,
  );
  const captureTempoConfidencePercent = Math.round(captureTempoConfidence * 100);
  const captureTempoStatusLabel = captureTempoAutoEnabled
    ? `Auto ${captureTempoBpm} BPM`
    : `Manual ${captureTempoBpm} BPM`;
  const draftEventsSnapshot = getDraftEventsSnapshot();
  const draftPedalEventsSnapshot = getDraftPedalEventsSnapshot();
  const hasDraftMaterial = draftEventsSnapshot.length > 0 || draftPedalEventsSnapshot.length > 0;
  const hasEditSelection = selectedEventIds.length > 0;
  const selectedEventsHaveNotes = selectedTimelineEvents.some((event) => !isDraftRestEvent(event));
  const selectedEventsCanSplit = selectedTimelineEvents.some(
    (event) => event.durationDivisions > MIN_EVENT_DURATION_DIVISIONS,
  );
  const selectedStaffMode = useMemo(() => {
    if (selectedTimelineEvents.length === 0) return null;
    const staffKeys = new Set(selectedTimelineEvents.map((event) => event.staff ?? 'split'));
    return staffKeys.size === 1 ? Array.from(staffKeys)[0] : 'mixed';
  }, [selectedTimelineEvents]);

  const handleCaptureTempoAutoToggle = useCallback(() => {
    setCaptureTempoAutoEnabled((enabled) => {
      const nextEnabled = !enabled;
      if (nextEnabled) {
        adaptiveTempoOnsetsRef.current = getAdaptiveTempoOnsetsFromEvents(capturedEvents);
        const { min, max } = captureTempoRangeRef.current;
        const estimate = estimateAdaptiveCaptureTempo(
          adaptiveTempoOnsetsRef.current,
          captureTempoRef.current,
          selectedDurationDivisions,
          min,
          max,
        );
        if (estimate && estimate.confidence >= AUTO_TEMPO_MIN_CONFIDENCE) {
          const nextTempo = clampTempoBpm(estimate.tempoBpm, min, max);
          captureTempoRef.current = nextTempo;
          setCaptureTempoBpm(nextTempo);
          setCaptureTempoConfidence(estimate.confidence);
          requantizeAdaptiveCaptureForTempo(nextTempo);
        }
      } else {
        adaptiveTempoOnsetsRef.current = [];
        setCaptureTempoConfidence(0);
        setCaptureTempoBpm(clampTempoBpm(playbackTempo, captureTempoMinBpm, captureTempoMaxBpm));
      }
      return nextEnabled;
    });
  }, [
    captureTempoMaxBpm,
    captureTempoMinBpm,
    capturedEvents,
    playbackTempo,
    requantizeAdaptiveCaptureForTempo,
    selectedDurationDivisions,
  ]);

  const handleCaptureTempoMinChange = useCallback(
    (value: number) => {
      if (!Number.isFinite(value)) return;
      const nextMin = clampTempoBpm(value, MIN_PLAYBACK_TEMPO_BPM, captureTempoMaxBpm);
      setCaptureTempoMinBpm(nextMin);
      setCaptureTempoBpm((tempo) => clampTempoBpm(tempo, nextMin, captureTempoMaxBpm));
    },
    [captureTempoMaxBpm],
  );

  const handleCaptureTempoMaxChange = useCallback(
    (value: number) => {
      if (!Number.isFinite(value)) return;
      const nextMax = clampTempoBpm(value, captureTempoMinBpm, MAX_PLAYBACK_TEMPO_BPM);
      setCaptureTempoMaxBpm(nextMax);
      setCaptureTempoBpm((tempo) => clampTempoBpm(tempo, captureTempoMinBpm, nextMax));
    },
    [captureTempoMinBpm],
  );

  const finishActiveCaptureSession = useCallback(() => {
    flushPendingChord();

    const activeEventIds = Array.from(activeCapturedEventsRef.current.keys());
    if (activeEventIds.length > 0) {
      const endedAtMs = performance.now();
      activeEventIds.forEach((eventId) => finalizeCapturedEvent(eventId, endedAtMs));
    }

    clearActiveCaptureState();
    recentAudioOnsetsRef.current.clear();
    resetAdaptiveTempoLearning();
  }, [
    clearActiveCaptureState,
    finalizeCapturedEvent,
    flushPendingChord,
    resetAdaptiveTempoLearning,
  ]);

  const handleBuilderModeChange = useCallback(
    (nextMode: BuilderMode) => {
      if (nextMode === builderMode) return;

      if (builderMode === 'capture' && nextMode !== 'capture') {
        finishActiveCaptureSession();
      }

      if (nextMode !== 'edit') {
        setSelectedEventIds([]);
        setSelectedNoteTarget(null);
        mergeKeyActiveRef.current = false;
      }

      setBuilderMode(nextMode);
    },
    [builderMode, finishActiveCaptureSession],
  );

  useEffect(() => {
    if (isEditMode) return;
    if (selectedEventIds.length === 0 && selectedNoteTarget == null) return;

    setSelectedEventIds([]);
    setSelectedNoteTarget(null);
    mergeKeyActiveRef.current = false;
  }, [isEditMode, selectedEventIds.length, selectedNoteTarget]);

  const handleManualNotePointerDragHandlerChange = useCallback(
    (handler: ManualNotePointerDragHandler | null) => {
      manualNotePointerDragHandlerRef.current = handler;
    },
    [],
  );

  const notationToolbar =
    builderMode === 'edit' ? (
      <Box sx={scoreNotationToolbarStyles(scoreTheme)}>
        <Box sx={toolbarGroupStyles}>
          <Box sx={toolbarGroupLabelStyles(scoreTheme)}>Note value</Box>
          <Box sx={toolbarNoteGridStyles}>
            {DRAFT_DURATION_OPTIONS.map((option) => {
              const selected = selectedDurationDivisions === option.divisions;
              return (
                <Box
                  key={option.label}
                  component="button"
                  type="button"
                  draggable
                  title={option.label}
                  aria-label={`${option.label} note`}
                  onClick={() => updateSelectedEventsDuration(option.divisions)}
                  onPointerDown={(event: ReactPointerEvent<HTMLElement>) => {
                    setSelectedDurationDivisions(option.divisions);
                    manualNotePointerDragHandlerRef.current?.(option.divisions, event);
                  }}
                  onDragStart={(event: DragEvent<HTMLButtonElement>) => {
                    setSelectedDurationDivisions(option.divisions);
                    event.dataTransfer.setData(MANUAL_NOTE_DRAG_TYPE, String(option.divisions));
                    event.dataTransfer.effectAllowed = 'copy';
                    event.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0);
                  }}
                  sx={notePaletteButtonStyles(selected, scoreTheme)}
                >
                  <NoteLengthIcon
                    durationDivisions={option.divisions}
                    fillColor={scorePalette.notationColor}
                    stemColor={scorePalette.notationColor}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={toolbarGroupStyles}>
          <Box sx={toolbarGroupLabelStyles(scoreTheme)}>Accidental</Box>
          <Box sx={toolbarAccidentalGridStyles}>
            {ACCIDENTAL_SPELLING_OPTIONS.map((option) => {
              const selected = selectedAccidentalSpelling === option.id;
              const disabled = !selectedEventsHaveNotes;
              return (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  aria-label={option.ariaLabel}
                  title={option.ariaLabel}
                  disabled={disabled}
                  onClick={() => updateSelectedAccidentalSpelling(option.id)}
                  sx={accidentalSegmentButtonStyles(selected, scoreTheme)}
                >
                  {option.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={toolbarSelectionStyles}>
          <Box sx={toolbarStatusPillStyles(scoreTheme)}>{selectedAccidentalTargetLabel}</Box>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={selectedEventIds.length === 0}
            onClick={handleDuplicateSelectedEvents}
            sx={toolbarActionButtonStyles(scoreTheme)}
          >
            Duplicate
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            disabled={selectedEventIds.length === 0}
            onClick={handleDeleteSelectedEvents}
            sx={toolbarActionButtonStyles(scoreTheme, 'danger')}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            disabled={selectedEventIds.length < 2}
            onClick={() => mergeDraftEvents(selectedEventIds)}
            sx={toolbarActionButtonStyles(scoreTheme)}
          >
            Merge
          </Button>
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Menu
        selected={'Repertoire'}
        collapseToDrawer
        mobileOpen={menuOpen}
        setMobileOpen={setMenuOpen}
        hideMobileTrigger
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isDrawer ? '100%' : 'calc(100% - var(--app-sidebar-width, 100px))',
          minWidth: 0,
        }}
      >
        <PageNav selected={'Create'} onMenuOpen={isDrawer ? () => setMenuOpen(true) : undefined} />
        <Box
          sx={{
            height: 'calc(100vh - var(--rep-nav-height, 100px))',
            overflowY: 'auto',
            background:
              'radial-gradient(circle at top left, rgba(111, 135, 241, 0.16) 0%, rgba(111, 135, 241, 0) 32%), linear-gradient(180deg, #08111E 0%, #060C15 100%)',
            padding: { xs: '18px 16px 28px', md: '22px 22px 32px' },
          }}
        >
          <Box sx={createWorkbenchGridStyles}>
            <Box sx={modeSwitcherShellStyles}>
              {BUILDER_MODE_OPTIONS.map((option) => {
                const selected = builderMode === option.id;
                return (
                  <Button
                    key={option.id}
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => handleBuilderModeChange(option.id)}
                    sx={builderModeButtonStyles(selected)}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={createControlsColumnStyles}>
              <Box sx={panelStyles}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <Box sx={actionRowStyles}>
                    <Button
                      variant="contained"
                      onClick={handleOpenSaveModal}
                      disabled={isSavingDraft}
                      sx={saveDraftButtonStyles}
                    >
                      {isSavingDraft ? 'Saving...' : 'Save to Library'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportMusicXml}
                      disabled={!hasDraftMaterial}
                      sx={secondaryActionButtonStyles}
                    >
                      Export MusicXML
                    </Button>
                  </Box>
                  <Box sx={actionRowStyles}>
                    <Button
                      variant="contained"
                      startIcon={<UndoIcon />}
                      onClick={handleUndo}
                      disabled={isPracticeMode || undoStack.length === 0}
                      sx={actionButtonStyles}
                    >
                      Undo
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RedoIcon />}
                      onClick={handleRedo}
                      disabled={isPracticeMode || redoStack.length === 0}
                      sx={secondaryActionButtonStyles}
                    >
                      Redo
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleRequestClear}
                      disabled={isPracticeMode || !hasDraftMaterial}
                      sx={dangerActionButtonStyles}
                    >
                      Clear draft
                    </Button>
                  </Box>
                </Box>
              </Box>

              {builderMode === 'capture' && (
                <>
                  <Box sx={panelStyles}>
                    <Box sx={panelTitleStyles}>Capture</Box>
                    <Box sx={{ marginTop: '14px', display: 'grid', gap: '16px' }}>
                      <Box
                        role={canOpenAudioCalibrationSetup ? 'button' : undefined}
                        tabIndex={canOpenAudioCalibrationSetup ? 0 : undefined}
                        onClick={
                          canOpenAudioCalibrationSetup ? openAudioCalibrationSetup : undefined
                        }
                        onKeyDown={(event) => {
                          if (!canOpenAudioCalibrationSetup) return;
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          openAudioCalibrationSetup();
                        }}
                        sx={{
                          ...inputStatusCardStyles(inputStatus.tone),
                          cursor: canOpenAudioCalibrationSetup ? 'pointer' : 'default',
                          '&:hover': canOpenAudioCalibrationSetup
                            ? {
                                borderColor: 'rgba(255, 210, 125, 0.54)',
                                backgroundColor: 'rgba(255, 210, 125, 0.14)',
                              }
                            : undefined,
                        }}
                      >
                        <Box sx={inputStatusDotStyles(inputStatus.tone)} />
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={inputStatusTitleStyles}>{inputStatus.label}</Box>
                          <Box sx={inputStatusDetailStyles}>{inputStatus.detail}</Box>
                        </Box>
                        {canRunAudioCalibration && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReplayIcon />}
                            onClick={(event) => {
                              event.stopPropagation();
                              openAudioCalibrationSetup();
                            }}
                            sx={inputStatusActionButtonStyles}
                          >
                            {calibrationProfile ? 'Recalibrate' : 'Calibrate'}
                          </Button>
                        )}
                      </Box>

                      <Box>
                        <Box sx={controlLabelStyles}>Time signature</Box>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={timeSignatureId}
                          onChange={(event) => {
                            stopPlayback({ resetProgress: true, hideCursor: true });
                            resetAdaptiveTempoLearning();
                            setTimeSignatureId(String(event.target.value));
                          }}
                          SelectProps={{ MenuProps: selectControlMenuProps }}
                          sx={selectControlStyles}
                        >
                          {COMMON_TIME_SIGNATURES.map((timeSignature) => (
                            <MenuItem key={timeSignature.id} value={timeSignature.id}>
                              {timeSignature.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      <Box>
                        <Box sx={controlLabelStyles}>Capture tempo</Box>
                        <Box sx={captureTempoControlStyles}>
                          <Box sx={captureTempoTopRowStyles}>
                            <Box sx={captureTempoStatusStyles}>{captureTempoStatusLabel}</Box>
                            <Box sx={metronomeSwitchControlRowStyles}>
                              <CustomSwitch
                                checked={captureTempoAutoEnabled}
                                onChange={handleCaptureTempoAutoToggle}
                                inputProps={{ 'aria-label': 'Auto capture tempo' }}
                                sx={metronomeSwitchControlStyles}
                              />
                              <Box sx={captureTempoAutoLabelStyles}>
                                {captureTempoAutoEnabled ? 'Auto' : 'Off'}
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={captureTempoRangeGridStyles}>
                            <Box>
                              <Box sx={miniControlLabelStyles}>Min</Box>
                              <Box
                                component="input"
                                type="number"
                                min={MIN_PLAYBACK_TEMPO_BPM}
                                max={captureTempoMaxBpm}
                                value={captureTempoMinBpm}
                                disabled={!captureTempoAutoEnabled}
                                onChange={(event) =>
                                  handleCaptureTempoMinChange(Number(event.target.value))
                                }
                                sx={captureTempoNumberInputStyles}
                              />
                            </Box>
                            <Box>
                              <Box sx={miniControlLabelStyles}>Max</Box>
                              <Box
                                component="input"
                                type="number"
                                min={captureTempoMinBpm}
                                max={MAX_PLAYBACK_TEMPO_BPM}
                                value={captureTempoMaxBpm}
                                disabled={!captureTempoAutoEnabled}
                                onChange={(event) =>
                                  handleCaptureTempoMaxChange(Number(event.target.value))
                                }
                                sx={captureTempoNumberInputStyles}
                              />
                            </Box>
                            <Box>
                              <Box sx={miniControlLabelStyles}>Confidence</Box>
                              <Box sx={captureTempoConfidenceStyles}>
                                {captureTempoAutoEnabled
                                  ? `${captureTempoConfidencePercent}%`
                                  : '--'}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box>
                        <Box sx={controlLabelStyles}>Start at</Box>
                        <Box sx={recordingCursorReadoutStyles}>{recordingCursorLabel}</Box>
                        <Box
                          component="input"
                          type="range"
                          min={0}
                          max={recordingCursorMaxDivisions}
                          step={MIN_EVENT_DURATION_DIVISIONS}
                          value={recordingCursorDivisions}
                          onChange={(event) =>
                            handleRecordingCursorChange(Number(event.target.value))
                          }
                          sx={recordingCursorRangeStyles}
                        />
                      </Box>

                      <Box>
                        <Box sx={controlLabelStyles}>Hand</Box>
                        <Box sx={recordingHandGridStyles}>
                          {RECORDING_HAND_OPTIONS.map((option) => {
                            const selected = recordingHandMode === option.id;
                            return (
                              <Button
                                key={option.id}
                                variant={selected ? 'contained' : 'outlined'}
                                onClick={() => setRecordingHandMode(option.id)}
                                sx={
                                  selected ? compactActionButtonStyles : compactOutlineButtonStyles
                                }
                              >
                                {option.label}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={panelStyles}>
                    <Box sx={panelTitleStyles}>Recent Capture</Box>
                    <Box sx={panelBodyStyles}>
                      Sixteenth-note grid in {selectedTimeSignature.label}.
                    </Box>

                    <Box sx={recentCaptureListStyles}>
                      {recentCaptures.length > 0 ? (
                        recentCaptures.map((event) => (
                          <Box key={event.id} sx={recentCapturePillStyles(event)}>
                            {event.label}
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ color: 'rgba(226, 234, 255, 0.62)', fontSize: '12px' }}>
                          No notes or pedal events captured yet.
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {builderMode === 'edit' && (
                <Box sx={panelStyles}>
                  <Box sx={panelTitleStyles}>Score</Box>
                  <Box sx={{ marginTop: '14px', display: 'grid', gap: '16px' }}>
                    <Box>
                      <Box sx={controlLabelStyles}>Time signature</Box>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={timeSignatureId}
                        onChange={(event) => {
                          stopPlayback({ resetProgress: true, hideCursor: true });
                          resetAdaptiveTempoLearning();
                          setTimeSignatureId(String(event.target.value));
                        }}
                        SelectProps={{ MenuProps: selectControlMenuProps }}
                        sx={selectControlStyles}
                      >
                        {COMMON_TIME_SIGNATURES.map((timeSignature) => (
                          <MenuItem key={timeSignature.id} value={timeSignature.id}>
                            {timeSignature.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={editSelectionSummaryStyles}>
                      <Box sx={controlLabelStyles}>Selection</Box>
                      <Box sx={editorStatusPillStyles}>{selectedAccidentalTargetLabel}</Box>
                    </Box>
                    <Box>
                      <Box sx={controlLabelStyles}>Cursor</Box>
                      <Box sx={recordingCursorReadoutStyles}>{recordingCursorLabel}</Box>
                      <Box
                        component="input"
                        type="range"
                        min={0}
                        max={recordingCursorMaxDivisions}
                        step={MIN_EVENT_DURATION_DIVISIONS}
                        value={recordingCursorDivisions}
                        onChange={(event) =>
                          handleRecordingCursorChange(Number(event.target.value))
                        }
                        sx={recordingCursorRangeStyles}
                      />
                    </Box>
                    <Box>
                      <Box sx={controlLabelStyles}>Insert</Box>
                      <Box sx={phraseToolGridStyles}>
                        <Button
                          variant="contained"
                          onClick={insertNoteAtCursor}
                          sx={compactActionButtonStyles}
                        >
                          Insert note
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={insertRestAtCursor}
                          sx={compactOutlineButtonStyles}
                        >
                          Insert rest
                        </Button>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={controlLabelStyles}>Timing</Box>
                      <Box sx={phraseToolGridStyles}>
                        <Button
                          variant="outlined"
                          disabled={!hasEditSelection}
                          onClick={() => nudgeSelectedEvents(-1)}
                          sx={compactOutlineButtonStyles}
                        >
                          Earlier
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={!hasEditSelection}
                          onClick={() => nudgeSelectedEvents(1)}
                          sx={compactOutlineButtonStyles}
                        >
                          Later
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={!selectedEventsCanSplit}
                          onClick={splitSelectedEvents}
                          sx={compactOutlineButtonStyles}
                        >
                          Split
                        </Button>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={controlLabelStyles}>Pitch</Box>
                      <Box sx={phraseToolGridStyles}>
                        <Button
                          variant="outlined"
                          disabled={!selectedEventsHaveNotes}
                          onClick={() => transposeSelectedEvents(-1)}
                          sx={compactOutlineButtonStyles}
                        >
                          Down
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={!selectedEventsHaveNotes}
                          onClick={() => transposeSelectedEvents(1)}
                          sx={compactOutlineButtonStyles}
                        >
                          Up
                        </Button>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={controlLabelStyles}>Hand</Box>
                      <Box sx={phraseToolGridStyles}>
                        <Button
                          variant={selectedStaffMode === 'split' ? 'contained' : 'outlined'}
                          disabled={!hasEditSelection}
                          onClick={() => moveSelectedEventsToStaff(undefined)}
                          sx={
                            selectedStaffMode === 'split'
                              ? compactActionButtonStyles
                              : compactOutlineButtonStyles
                          }
                        >
                          Auto
                        </Button>
                        <Button
                          variant={selectedStaffMode === 1 ? 'contained' : 'outlined'}
                          disabled={!hasEditSelection}
                          onClick={() => moveSelectedEventsToStaff(1)}
                          sx={
                            selectedStaffMode === 1
                              ? compactActionButtonStyles
                              : compactOutlineButtonStyles
                          }
                        >
                          Right
                        </Button>
                        <Button
                          variant={selectedStaffMode === 2 ? 'contained' : 'outlined'}
                          disabled={!hasEditSelection}
                          onClick={() => moveSelectedEventsToStaff(2)}
                          sx={
                            selectedStaffMode === 2
                              ? compactActionButtonStyles
                              : compactOutlineButtonStyles
                          }
                        >
                          Left
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {builderMode === 'practice' && (
                <Box sx={panelStyles}>
                  <Box sx={panelTitleStyles}>Practice</Box>
                  <Box sx={{ marginTop: '14px', display: 'grid', gap: '12px' }}>
                    <Box sx={practiceMetricStyles}>
                      <Box sx={miniControlLabelStyles}>Draft</Box>
                      <Box sx={practiceMetricValueStyles}>
                        {hasDraftMaterial ? 'Ready' : 'Empty'}
                      </Box>
                    </Box>
                    <Box sx={practiceMetricStyles}>
                      <Box sx={miniControlLabelStyles}>Tempo</Box>
                      <Box sx={practiceMetricValueStyles}>{playbackTempo} BPM</Box>
                    </Box>
                    <Box sx={practiceMetricStyles}>
                      <Box sx={miniControlLabelStyles}>Metronome</Box>
                      <Box sx={practiceMetricValueStyles}>{metronomeEnabled ? 'On' : 'Off'}</Box>
                    </Box>
                    <Box sx={practiceMetricStyles}>
                      <Box sx={miniControlLabelStyles}>Capture</Box>
                      <Box sx={practiceMetricValueStyles}>Off</Box>
                    </Box>
                    <Box sx={practiceMetricStyles}>
                      <Box sx={miniControlLabelStyles}>Editing</Box>
                      <Box sx={practiceMetricValueStyles}>Locked</Box>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={panelStyles}>
                <Box sx={advancedHeaderStyles}>
                  <Box sx={panelTitleStyles}>Advanced</Box>
                  <Button
                    variant="outlined"
                    onClick={() => setAdvancedToolsOpen((open) => !open)}
                    sx={compactOutlineButtonStyles}
                  >
                    {advancedToolsOpen ? 'Hide' : 'Show'}
                  </Button>
                </Box>
                {advancedToolsOpen && (
                  <Box sx={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (audioDiagnosticsVisible) {
                          setAudioDiagnosticsVisible(false);
                        } else {
                          handleRunAudioDiagnostics();
                        }
                      }}
                      sx={secondaryActionButtonStyles}
                    >
                      {audioDiagnosticsVisible ? 'Hide audio diagnostics' : 'Audio diagnostics'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={createSheetColumnStyles}>
              <LiveComposerSheet
                draftTitle={draftTitle}
                musicXml={scoreBuild.xml}
                measureCount={scoreBuild.measureCount}
                draftEvents={capturedEvents}
                selectedEventIds={selectedEventIds}
                selectedNoteTarget={selectedNoteTarget}
                timeSignatureLabel={selectedTimeSignature.label}
                measureDurationDivisions={measureDurationDivisions}
                selectedDurationDivisions={selectedDurationDivisions}
                recordingCursorDivisions={recordingCursorDivisions}
                playbackCursorStartDivisions={playbackCursorStartDivisions}
                playbackSessionKey={playbackSessionKey}
                playbackTempo={playbackTempo}
                metronomeEnabled={metronomeEnabled}
                canPlay={hasDraftMaterial}
                isPlayingBack={isPlayingBack}
                showPlaybackCursor={showPlaybackCursor}
                scoreEditingEnabled={isEditMode}
                onTempoChange={setPlaybackTempo}
                onMetronomeToggle={handleMetronomeToggle}
                onPlaybackToggle={() => {
                  void handlePlaybackToggle();
                }}
                onPlaybackRestart={() => {
                  void handlePlaybackRestart();
                }}
                onManualNoteDrop={addManualNote}
                onExistingNoteDrop={moveExistingNote}
                onEventSelect={handleEditorEventSelect}
                onRecordingCursorChange={handleRecordingCursorChange}
                onManualNotePointerDragHandlerChange={handleManualNotePointerDragHandlerChange}
                audioDiagnosticsVisible={audioDiagnosticsVisible}
                audioDiagnosticsSnapshot={audioDiagnosticsSnapshot}
                audioDiagnosticsFrames={audioDiagnosticsFramesRef.current}
                scoreTheme={scoreTheme}
                notationToolbar={notationToolbar}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <SaveRepertoireDialog
        open={saveModalOpen}
        title={saveTitleInput}
        error={saveError}
        isSaving={isSavingDraft}
        onTitleChange={setSaveTitleInput}
        onClose={handleCloseSaveModal}
        onSave={() => {
          void handleSaveDraft();
        }}
      />
      <ClearDraftDialog
        open={clearConfirmOpen}
        onClose={handleCloseClearConfirm}
        onConfirm={handleClear}
      />
      <SaveSuccessDialog
        repertoire={saveSuccessRepertoire}
        onClose={() => setSaveSuccessRepertoire(null)}
        onPractice={handlePracticeSavedRepertoire}
      />
    </Box>
  );
};

const createWorkbenchGridStyles = {
  width: '100%',
  maxWidth: '1680px',
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"mode" "controls" "sheet"',
  gap: '20px',
  alignItems: 'start',
  minWidth: 0,
  [TABLET_WORKBENCH_MEDIA]: {
    gridTemplateColumns: 'minmax(0, 1fr) clamp(286px, 23vw, 330px)',
    gridTemplateAreas: '"mode mode" "sheet controls"',
    gap: '20px',
  },
  [WIDE_WORKBENCH_MEDIA]: {
    gridTemplateColumns: 'minmax(0, 1fr) 340px',
    gap: '24px',
  },
};

const modeSwitcherShellStyles = {
  gridArea: 'mode',
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '100%',
  gap: '6px',
  padding: '6px',
  borderRadius: '999px',
  backgroundColor: 'rgba(5, 11, 19, 0.74)',
  border: '1px solid rgba(121, 216, 255, 0.14)',
  boxShadow: '0 16px 30px rgba(0, 0, 0, 0.18)',
  overflowX: 'auto',
};

const builderModeButtonStyles = (selected: boolean) => ({
  borderRadius: '999px',
  minHeight: '36px',
  paddingX: '20px',
  textTransform: 'none',
  fontWeight: 900,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  color: selected ? '#07111E' : '#EAF2FF',
  borderColor: selected ? 'transparent' : 'rgba(226, 234, 255, 0.14)',
  background: selected ? '#79D8FF' : 'transparent',
  boxShadow: selected ? '0 10px 18px rgba(121, 216, 255, 0.22)' : 'none',
  '&:hover': {
    borderColor: selected ? 'transparent' : 'rgba(121, 216, 255, 0.32)',
    background: selected ? '#9DE4FF' : 'rgba(121, 216, 255, 0.08)',
  },
});

const createControlsColumnStyles = {
  gridArea: 'controls',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  minWidth: 0,
  [TABLET_WORKBENCH_MEDIA]: {
    position: 'sticky',
    top: 0,
    maxHeight: 'calc(100vh - 136px)',
    overflowY: 'auto',
    gap: '14px',
    paddingRight: '2px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
};

const createSheetColumnStyles = {
  gridArea: 'sheet',
  minWidth: 0,
};

const panelStyles = {
  borderRadius: '22px',
  border: '1px solid rgba(121, 216, 255, 0.14)',
  background: 'linear-gradient(180deg, rgba(10, 18, 31, 0.96) 0%, rgba(7, 12, 22, 0.99) 100%)',
  boxShadow: '0 18px 34px rgba(0, 0, 0, 0.2)',
  padding: '20px',
  [TABLET_WORKBENCH_MEDIA]: {
    borderRadius: '18px',
    padding: '16px',
  },
  [WIDE_WORKBENCH_MEDIA]: {
    borderRadius: '22px',
    padding: '20px',
  },
};

const panelTitleStyles = {
  color: '#F2F6FF',
  fontSize: '22px',
  fontWeight: 700,
  lineHeight: 1.2,
  [TABLET_WORKBENCH_MEDIA]: {
    fontSize: '20px',
  },
  [WIDE_WORKBENCH_MEDIA]: {
    fontSize: '22px',
  },
};

const panelBodyStyles = {
  color: 'rgba(226, 234, 255, 0.74)',
  fontSize: '13px',
  lineHeight: 1.65,
  marginTop: '8px',
};

const actionRowStyles = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
};

const actionButtonStyles = {
  background: 'linear-gradient(90deg, #FFD27D 0%, #FFB95E 100%)',
  color: '#1D1303',
  fontWeight: 800,
  borderRadius: '999px',
  paddingX: '16px',
  '&:hover': {
    background: 'linear-gradient(90deg, #FFD88F 0%, #FFC46F 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(255, 210, 125, 0.16)',
    color: 'rgba(255, 231, 188, 0.56)',
  },
};

const secondaryActionButtonStyles = {
  color: '#EAF2FF',
  borderColor: 'rgba(255,255,255,0.16)',
  borderRadius: '999px',
  paddingX: '16px',
  '&:hover': {
    borderColor: 'rgba(121, 216, 255, 0.28)',
    backgroundColor: 'rgba(121, 216, 255, 0.08)',
  },
  '&.Mui-disabled': {
    color: 'rgba(226, 234, 255, 0.5)',
    borderColor: 'rgba(226, 234, 255, 0.14)',
    backgroundColor: 'rgba(226, 234, 255, 0.05)',
  },
};

const dangerActionButtonStyles = {
  ...secondaryActionButtonStyles,
  color: '#FFD1D1',
  borderColor: 'rgba(255, 157, 157, 0.22)',
  '&:hover': {
    borderColor: 'rgba(255, 157, 157, 0.42)',
    backgroundColor: 'rgba(255, 97, 97, 0.1)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 209, 209, 0.48)',
    borderColor: 'rgba(255, 157, 157, 0.14)',
    backgroundColor: 'rgba(255, 157, 157, 0.05)',
  },
};

const saveDraftButtonStyles = {
  background: 'linear-gradient(90deg, #469F6A 0%, #5EBB82 100%)',
  color: '#FFFFFF',
  fontWeight: 800,
  borderRadius: '999px',
  minHeight: '36px',
  paddingX: '18px',
  textTransform: 'none',
  boxShadow: '0 8px 18px rgba(70, 159, 106, 0.24)',
  '&:hover': {
    background: 'linear-gradient(90deg, #56AD78 0%, #6ACB91 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(118, 124, 140, 0.42)',
    color: 'rgba(255, 255, 255, 0.58)',
    boxShadow: 'none',
  },
};

const inputStatusCardStyles = (tone: 'ready' | 'warning' | 'idle') => ({
  display: 'grid',
  gridTemplateColumns: '10px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '14px',
  border:
    tone === 'ready'
      ? '1px solid rgba(94, 187, 130, 0.28)'
      : tone === 'warning'
        ? '1px solid rgba(255, 210, 125, 0.3)'
        : '1px solid rgba(226, 234, 255, 0.14)',
  backgroundColor:
    tone === 'ready'
      ? 'rgba(94, 187, 130, 0.12)'
      : tone === 'warning'
        ? 'rgba(255, 210, 125, 0.1)'
        : 'rgba(226, 234, 255, 0.06)',
  padding: '12px',
});

const inputStatusActionButtonStyles = {
  borderRadius: '999px',
  borderColor: 'rgba(127, 215, 255, 0.34)',
  color: '#C7F0FF',
  fontSize: '11px',
  fontWeight: 900,
  minHeight: '28px',
  paddingX: '10px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    '& svg': {
      fontSize: '15px',
    },
  },
  '&:hover': {
    borderColor: 'rgba(127, 215, 255, 0.56)',
    backgroundColor: 'rgba(127, 215, 255, 0.1)',
  },
};

const inputStatusDotStyles = (tone: 'ready' | 'warning' | 'idle') => ({
  width: '10px',
  height: '10px',
  borderRadius: '999px',
  backgroundColor:
    tone === 'ready' ? '#5EBB82' : tone === 'warning' ? '#FFD27D' : 'rgba(226,234,255,0.42)',
  boxShadow:
    tone === 'ready'
      ? '0 0 0 4px rgba(94, 187, 130, 0.12)'
      : tone === 'warning'
        ? '0 0 0 4px rgba(255, 210, 125, 0.1)'
        : 'none',
});

const inputStatusTitleStyles = {
  color: '#F2F6FF',
  fontSize: '13px',
  fontWeight: 900,
  lineHeight: 1.25,
};

const inputStatusDetailStyles = {
  color: 'rgba(226, 234, 255, 0.64)',
  fontSize: '12px',
  fontWeight: 700,
  marginTop: '2px',
};

const recordingHandGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '8px',
};

const recentCaptureListStyles = {
  marginTop: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
};

const recentCapturePillStyles = (event: Pick<RecentCaptureItem, 'label' | 'source'>) => ({
  borderRadius: '999px',
  padding: '9px 12px',
  backgroundColor:
    event.label === PEDAL_LABEL_DOWN || event.label === PEDAL_LABEL_UP
      ? 'rgba(183, 167, 255, 0.14)'
      : event.source === 'audio'
        ? 'rgba(121, 216, 255, 0.14)'
        : event.source === 'manual'
          ? 'rgba(183, 167, 255, 0.14)'
          : 'rgba(255, 210, 125, 0.14)',
  border:
    event.label === PEDAL_LABEL_DOWN || event.label === PEDAL_LABEL_UP
      ? '1px solid rgba(183, 167, 255, 0.24)'
      : event.source === 'audio'
        ? '1px solid rgba(121, 216, 255, 0.24)'
        : event.source === 'manual'
          ? '1px solid rgba(183, 167, 255, 0.24)'
          : '1px solid rgba(255, 210, 125, 0.22)',
  color: '#EAF2FF',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.02em',
});

const editSelectionSummaryStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  flexWrap: 'wrap',
};

const phraseToolGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '8px',
  '& .MuiButton-root': {
    minWidth: 0,
  },
};

const practiceMetricStyles = {
  borderRadius: '14px',
  border: '1px solid rgba(226, 234, 255, 0.12)',
  backgroundColor: 'rgba(226, 234, 255, 0.05)',
  padding: '12px',
};

const practiceMetricValueStyles = {
  color: '#F2F6FF',
  fontSize: '15px',
  fontWeight: 900,
};

const advancedHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const controlLabelStyles = {
  color: 'rgba(226, 234, 255, 0.68)',
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: '8px',
};

const selectControlMenuProps = {
  sx: {
    zIndex: 5000,
  },
  PaperProps: {
    sx: {
      zIndex: 5001,
      mt: '6px',
      color: '#F2F6FF',
      backgroundColor: 'rgba(13, 22, 38, 0.98)',
      border: '1px solid rgba(255,255,255,0.16)',
      borderRadius: '10px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.34)',
      maxHeight: '260px',
      '& .MuiMenuItem-root': {
        minHeight: '36px',
        fontSize: '14px',
        fontWeight: 700,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(121, 216, 255, 0.22)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(121, 216, 255, 0.16)',
      },
    },
  },
} as const;

const selectControlStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '42px',
    color: '#F2F6FF',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 700,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 36px 8px 12px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.16)',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(121, 216, 255, 0.32)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(121, 216, 255, 0.52)',
  },
  '& .MuiSvgIcon-root': {
    color: 'rgba(226, 234, 255, 0.72)',
  },
} as const;

const captureTempoControlStyles = {
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.12)',
  backgroundColor: 'rgba(255,255,255,0.05)',
  padding: '12px',
};

const captureTempoTopRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const captureTempoStatusStyles = {
  color: '#F2F6FF',
  fontSize: '13px',
  fontWeight: 900,
  letterSpacing: '0.01em',
};

const captureTempoAutoLabelStyles = {
  color: 'rgba(226, 234, 255, 0.76)',
  fontSize: '12px',
  fontWeight: 800,
  minWidth: '28px',
};

const captureTempoRangeGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '8px',
  marginTop: '12px',
};

const miniControlLabelStyles = {
  color: 'rgba(226, 234, 255, 0.56)',
  fontSize: '10px',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: '5px',
};

const captureTempoNumberInputStyles = {
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  height: '34px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.14)',
  backgroundColor: 'rgba(3, 8, 16, 0.58)',
  color: '#F2F6FF',
  padding: '0 8px',
  fontSize: '13px',
  fontWeight: 800,
  outline: 'none',
  '&:disabled': {
    color: 'rgba(226,234,255,0.38)',
    borderColor: 'rgba(255,255,255,0.08)',
  },
};

const captureTempoConfidenceStyles = {
  minHeight: '34px',
  borderRadius: '10px',
  border: '1px solid rgba(121, 216, 255, 0.16)',
  backgroundColor: 'rgba(121, 216, 255, 0.08)',
  color: '#EAF2FF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13px',
  fontWeight: 900,
};

const recordingCursorReadoutStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',
  minWidth: '78px',
  minHeight: '24px',
  borderRadius: '999px',
  border: '1px solid rgba(121, 216, 255, 0.18)',
  backgroundColor: 'rgba(121, 216, 255, 0.08)',
  color: '#EAF2FF',
  padding: '0 10px',
  fontSize: '12px',
  fontWeight: 800,
  lineHeight: 1,
  margin: '0 auto 8px',
};

const recordingCursorRangeStyles = {
  width: '100%',
  accentColor: '#6F87F1',
  cursor: 'ew-resize',
};

const compactActionButtonStyles = {
  background: '#79D8FF',
  color: '#07111E',
  fontWeight: 800,
  borderRadius: '999px',
  minHeight: '34px',
  paddingX: '12px',
  textTransform: 'none',
  '&:hover': {
    background: '#9DE4FF',
  },
};

const compactOutlineButtonStyles = {
  color: '#EAF2FF',
  borderColor: 'rgba(255,255,255,0.16)',
  borderRadius: '999px',
  minHeight: '34px',
  paddingX: '12px',
  textTransform: 'none',
  '&:hover': {
    borderColor: 'rgba(121, 216, 255, 0.32)',
    backgroundColor: 'rgba(121, 216, 255, 0.08)',
  },
  '&.Mui-disabled': {
    color: 'rgba(226,234,255,0.34)',
    borderColor: 'rgba(255,255,255,0.08)',
  },
};

const scoreNotationToolbarStyles = (scoreTheme: ScoreTheme) => {
  const scorePalette = getScoreThemePalette(scoreTheme);
  const practiceDarkMode = scoreThemeToPracticeDarkMode(scoreTheme);

  return {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    rowGap: '14px',
    flexWrap: 'wrap',
    padding: { xs: '14px', md: '14px 24px' },
    borderBottom: practiceDarkMode
      ? '1px solid rgba(243, 247, 255, 0.12)'
      : '1px solid rgba(9, 19, 36, 0.08)',
    backgroundColor:
      scoreTheme === 'light'
        ? 'rgba(247, 249, 253, 0.92)'
        : scoreTheme === 'dimPaper'
          ? 'rgba(238, 221, 195, 0.86)'
          : scorePalette.scoreBackground,
  };
};

const toolbarGroupStyles = {
  display: 'grid',
  gap: '8px',
  minWidth: 0,
};

const toolbarGroupLabelStyles = (scoreTheme: ScoreTheme) => ({
  color: scoreThemeToPracticeDarkMode(scoreTheme) ? 'rgba(243, 247, 255, 0.58)' : '#475569',
  fontSize: '10px',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
});

const toolbarNoteGridStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  maxWidth: '100%',
};

const getNotePaletteButtonBorderColor = (scoreTheme: ScoreTheme, selected: boolean) => {
  const scorePalette = getScoreThemePalette(scoreTheme);
  if (selected) return scorePalette.playbackCursor;
  if (scoreTheme === 'dimPaper') return 'rgba(56, 45, 37, 0.34)';
  if (scoreThemeToPracticeDarkMode(scoreTheme)) return 'rgba(243, 247, 255, 0.22)';
  return 'rgba(15, 23, 42, 0.2)';
};

const notePaletteButtonStyles = (selected: boolean, scoreTheme: ScoreTheme) => {
  const scorePalette = getScoreThemePalette(scoreTheme);
  const practiceDarkMode = scoreThemeToPracticeDarkMode(scoreTheme);
  const controlBackground =
    scoreTheme === 'light'
      ? '#FFFFFF'
      : scoreTheme === 'dimPaper'
        ? 'rgba(255, 249, 237, 0.74)'
        : 'rgba(243, 247, 255, 0.08)';
  const themedBackground = selected
    ? `linear-gradient(${scorePalette.selectedNoteHighlight}, ${scorePalette.selectedNoteHighlight}), ${controlBackground}`
    : controlBackground;

  return {
    appearance: 'none',
    border: `1px solid ${getNotePaletteButtonBorderColor(scoreTheme, selected)}`,
    borderRadius: '10px',
    width: '64px',
    flex: '0 0 64px',
    minHeight: '64px',
    padding: '4px 6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: themedBackground,
    color: scorePalette.notationColor,
    boxShadow: selected
      ? `0 0 0 3px ${scorePalette.selectedNoteHighlight}, inset 0 1px 0 ${
          practiceDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.42)'
        }, 0 10px 20px rgba(0,0,0,0.16)`
      : `inset 0 1px 0 ${
          practiceDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.28)'
        }, 0 8px 16px rgba(0,0,0,0.12)`,
    cursor: 'grab',
    userSelect: 'none',
    touchAction: 'none',
    WebkitUserSelect: 'none',
    WebkitUserDrag: 'none',
    transition:
      'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
    '&:hover': {
      borderColor: scorePalette.staffLineColor,
      background: `linear-gradient(${scorePalette.selectedNoteHighlight}, ${scorePalette.selectedNoteHighlight}), ${controlBackground}`,
      boxShadow: `0 0 0 3px ${scorePalette.selectedNoteHighlight}, 0 12px 24px rgba(0,0,0,0.18)`,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      cursor: 'grabbing',
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: `2px solid ${scorePalette.playbackCursor}`,
      outlineOffset: '2px',
    },
  };
};

const toolbarAccidentalGridStyles = {
  display: 'grid',
  gridTemplateColumns: '1.35fr repeat(5, minmax(0, 1fr))',
  gap: '7px',
  minWidth: '300px',
};

const toolbarSelectionStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};

const toolbarControlColors = (scoreTheme: ScoreTheme) => {
  const scorePalette = getScoreThemePalette(scoreTheme);

  if (scoreTheme === 'light') {
    return {
      text: '#10203A',
      mutedText: 'rgba(16, 32, 58, 0.48)',
      border: 'rgba(16, 32, 58, 0.18)',
      dangerText: '#B42318',
      dangerBorder: 'rgba(180, 35, 24, 0.28)',
      surface: '#FFFFFF',
      selectedText: '#0F5271',
      selectedBorder: 'rgba(79, 150, 189, 0.74)',
      selectedSurface: scorePalette.selectedNoteHighlight,
      hoverSurface: 'rgba(79, 150, 189, 0.08)',
    };
  }

  if (scoreTheme === 'dimPaper') {
    return {
      text: '#211915',
      mutedText: 'rgba(42, 32, 26, 0.52)',
      border: 'rgba(56, 45, 37, 0.24)',
      dangerText: '#8F2E1F',
      dangerBorder: 'rgba(143, 46, 31, 0.3)',
      surface: 'rgba(255, 249, 237, 0.62)',
      selectedText: '#473018',
      selectedBorder: 'rgba(92, 69, 42, 0.68)',
      selectedSurface: scorePalette.selectedNoteHighlight,
      hoverSurface: 'rgba(92, 69, 42, 0.08)',
    };
  }

  return {
    text: '#F3F7FF',
    mutedText: 'rgba(243, 247, 255, 0.48)',
    border: 'rgba(243, 247, 255, 0.18)',
    dangerText: '#FFD1D1',
    dangerBorder: 'rgba(255, 157, 157, 0.28)',
    surface: 'rgba(243, 247, 255, 0.08)',
    selectedText: '#EAF2FF',
    selectedBorder: scorePalette.playbackCursor,
    selectedSurface: scorePalette.selectedNoteHighlight,
    hoverSurface: 'rgba(121, 216, 255, 0.1)',
  };
};

const accidentalSegmentButtonStyles = (selected: boolean, scoreTheme: ScoreTheme) => {
  const colors = toolbarControlColors(scoreTheme);

  return {
    appearance: 'none',
    minWidth: 0,
    minHeight: '40px',
    borderRadius: '10px',
    padding: '0 7px',
    border: selected ? `1px solid ${colors.selectedBorder}` : `1px solid ${colors.border}`,
    backgroundColor: selected ? colors.selectedSurface : colors.surface,
    color: selected ? colors.selectedText : colors.text,
    fontSize: '18px',
    fontWeight: 900,
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    boxShadow: selected ? `0 0 0 3px ${colors.selectedSurface}` : 'none',
    '&:hover': {
      borderColor: colors.selectedBorder,
      backgroundColor: colors.hoverSurface,
    },
    '&:focus-visible': {
      outline: `2px solid ${colors.selectedBorder}`,
      outlineOffset: '2px',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: colors.mutedText,
      borderColor: colors.border,
      backgroundColor: 'transparent',
      opacity: 1,
    },
  };
};

const toolbarStatusPillStyles = (scoreTheme: ScoreTheme) => {
  const colors = toolbarControlColors(scoreTheme);

  return {
    borderRadius: '999px',
    padding: '8px 11px',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: '12px',
    fontWeight: 800,
  };
};

const toolbarActionButtonStyles = (
  scoreTheme: ScoreTheme,
  intent: 'neutral' | 'danger' = 'neutral',
) => {
  const colors = toolbarControlColors(scoreTheme);
  const color = intent === 'danger' ? colors.dangerText : colors.text;
  const borderColor = intent === 'danger' ? colors.dangerBorder : colors.border;

  return {
    color,
    borderColor,
    borderRadius: '999px',
    minHeight: '34px',
    paddingX: '12px',
    textTransform: 'none',
    backgroundColor: colors.surface,
    '&:hover': {
      borderColor: intent === 'danger' ? colors.dangerText : colors.selectedBorder,
      backgroundColor: intent === 'danger' ? 'rgba(255, 97, 97, 0.1)' : colors.hoverSurface,
    },
    '&.Mui-disabled': {
      color: colors.mutedText,
      borderColor: colors.border,
      backgroundColor: 'transparent',
    },
  };
};

const editorStatusPillStyles = {
  borderRadius: '999px',
  padding: '8px 11px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'rgba(226, 234, 255, 0.78)',
  fontSize: '12px',
  fontWeight: 800,
};

export default Create;
